// src/utils/planSwitcher.js — formula-based plan engine (Smart Coach–style)
import { computeWeeks } from "./dateMath";
import { MI_TO_KM } from "./distanceUnits";
import { placeRaceDayOnCorrectWeekday } from "./raceDay";

const WEEK_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// JS getDay(): 0=Sun … 6=Sat
const JS_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** Days per week by intensity and distance (approved matrix). */
export const DAYS_PER_WEEK = {
  /** 5K Easy: 4 days → 3 easy slots so peak volume (~12 mi) can distribute under 3 mi/run cap. */
  Easy: { "5K": 4, "10K": 4, "10 Miler": 4, "Half Marathon": 4, Marathon: 5 },
  Moderate: {
    "5K": 4,
    "10K": 4,
    /** Fifth day spreads easy volume (was 4: single easy slot stacked 7–8 mi). */
    "10 Miler": 5,
    "Half Marathon": 5,
    Marathon: 5,
  },
  Challenging: {
    "5K": 5,
    "10K": 5,
    "10 Miler": 5,
    "Half Marathon": 5,
    Marathon: 6,
  },
  /** 5K Intense: 5 active days — Thu & Sun rest; Mon tempo, Wed intervals, Tue/Fri easy, Sat LR. */
  Intense: { "5K": 5, "10K": 6, "10 Miler": 6, "Half Marathon": 6, Marathon: 6 },
};

const LONG_RUN_PCT = {
  "5K": 0.25,
  "10K": 0.28,
  "10 Miler": 0.3,
  "Half Marathon": 0.32,
  Marathon: 0.35,
};

/**
 * Marathon + Intense only (same peak LR mi as Challenging from the table). Tune here — not in
 * PEAK_LONG_RUN_MI: higher weekly targets + larger tempo/interval shares vs easy.
 */
const MARATHON_INTENSE_PLAN_BIAS = Object.freeze({
  peakWeeklyVolMult: 1.15,
  qualityFrac: 0.52,
  tempoShareOfQuality: 0.58,
  intervalShareOfQuality: 0.42,
  qualityTempoShare2Slot: 0.62,
});

/** Taper week 1 volume as a fraction of peak training miles (~70%). */
const TAPER_WEEK_RETAIN = 0.7;


/** Max single-session tempo (mi) on quality days — avoids oversized tempo blocks on peak weeks. */
const MAX_TEMPO_RUN_MI = 10;
/** 5K plans: tempo session cap (shorter race). */
const MAX_TEMPO_RUN_MI_5K = 4;
/** Max single-session interval block (mi) — matches tempo-style session cap (Marathon Intense quality weeks). */
const MAX_INTERVAL_RUN_MI = 8;
/** 5K plans: interval session cap (shorter race). */
const MAX_INTERVAL_RUN_MI_5K = 3;
/** Shared Moderate plans (Marathon / Half / 10 Miler / 5K): min tempo/interval mi; spare quality split ~55/45 (see buildTrainingWeek). */
const MARATHON_MODERATE_MIN_TEMPO_MI = 4;
const MARATHON_MODERATE_MIN_INTERVAL_MI = 2;
/** Marathon Moderate only: keep easy/tempo/interval sessions below Challenging. */
const MARATHON_MODERATE_MAX_EASY_RUN_MI = 6;
/** Marathon Intense only: cap recovery-day easy runs (Thu/Fri) vs default Intense 12 mi table. */
const MARATHON_INTENSE_MAX_EASY_RUN_MI = 9;
const MARATHON_MODERATE_MAX_TEMPO_RUN_MI = 8;
const MARATHON_MODERATE_MAX_INTERVAL_RUN_MI = 5;
/** Moderate quality plans: at least this many easy miles on each non–quality run day (avoids Rest on Thu, etc.). */
const MODERATE_QUALITY_MIN_EASY_MI_PER_SLOT = 2;
/** Any plan with an interval day: session total is at least this many miles. */
const MIN_INTERVAL_SESSION_MI = 2;

/**
 * Minimum non–long-run mi (`remaining`) so single-tempo quality weeks can assign
 * `tempo = min(maxTempoCap, max(minTempoMi, round(remaining × qualityFrac)))` and still have at
 * least `nEasySlots × 2` mi left for easy days (matches buildTrainingWeek quality slice).
 */
function minRemainingMiForQualitySplitSingleTempoFloor(
  nEasySlots,
  minTempoMi,
  qualityFrac,
  maxTempoCap,
  extraEasyMi = 0
) {
  const extra = Math.max(0, roundWhole(Number(extraEasyMi) || 0));
  const needEasy =
    nEasySlots * MODERATE_QUALITY_MIN_EASY_MI_PER_SLOT + extra;
  const minT = Math.max(1, roundWhole(Number(minTempoMi) || 0));
  const frac = Number(qualityFrac);
  const f = Number.isFinite(frac) && frac > 0 && frac < 1 ? frac : 0.45;
  const maxT = Math.max(minT, roundWhole(Number(maxTempoCap) || minT));
  let R = needEasy + minT;
  for (let g = 0; g < 200; g += 1) {
    const q = roundWhole(R * f);
    const tempo = Math.min(maxT, Math.max(minT, q));
    const easyAvail = R - tempo;
    if (easyAvail >= needEasy) return R;
    R += 1;
  }
  return R;
}

/**
 * Cutback long run ≈ this fraction of the prior week’s long run (midpoint of 65–70%).
 * Marathon build-week parsed WOW (≤15%) uses the last non-cutback week as reference; cutback weeks
 * use ~20% total drop vs prior week (vols) and cutback-below-prior display rules.
 */
const CUTBACK_LONG_RUN_FRAC = 0.675;

/** Cutback parsed weekly total ≈ this × previous week’s parsed total (~20% drop). */
const CUTBACK_TOTAL_MILEAGE_FRAC = 0.8;
/** Single-tempo cutback weeks: tempo mi capped at this × last non-cutback week’s tempo (whole mi). */
const CUTBACK_SINGLE_TEMPO_FRAC_OF_PRIOR_BUILD = 0.75;
/** Non-Easy cutback weeks: each easy run ≤ this × prior week’s average easy run (whole mi), min 2. */
const CUTBACK_EASY_RUN_FRAC_OF_PRIOR_BUILD_AVG = 0.8;
const CUTBACK_EASY_RUN_MIN_MI = 2;

/**
 * 5K non-Easy: w1→w2 WOW cap vs week 1 **parsed** total only when training weeks ≤ this (e.g. 7 calendar
 * weeks → 5 training weeks). Otherwise max(parsed, budget) supports vol reconciliation. Aligns with short
 * 10K cutback window (≤6 training weeks). Applies to Moderate, Challenging, and Intense.
 */
const FIVE_K_NON_EASY_ONBOARDING_WOW_PARSED_ONLY_MAX_TRAINING_WEEKS = 6;

/**
 * Easy: week after a cutback — LR cannot jump straight back to the calendar ramp target; cap vs
 * prior week’s actual LR (ramp and weeklyMi remain upper bounds).
 */
const MAX_LONG_RUN_INCREASE_AFTER_CUTBACK_EASY = 0.25;
/** Marathon Easy: slightly faster LR rebound after a cutback so weekly floor fits under easy caps (LR−1). */
const MARATHON_EASY_LR_INCREASE_AFTER_PRIOR_CUTBACK = 0.48;

function localWeekdayIndexFromString(dateStr) {
  if (typeof dateStr !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    return new Date(y, mo, d).getDay();
  }
  const dt = new Date(dateStr);
  return Number.isNaN(dt.getTime()) ? null : dt.getDay();
}

function getDaysPerWeek(intensity, distance) {
  const key = normalizeIntensityLabel(intensity);
  const row = DAYS_PER_WEEK[key] ?? DAYS_PER_WEEK.Moderate;
  const d = normalizeRaceDistance(distance);
  return row[d] ?? 4;
}

function getLongRunPct(distance) {
  return LONG_RUN_PCT[normalizeRaceDistance(distance)] ?? 0.3;
}

/**
 * Peak-week long run (mi) by race distance and training intensity.
 * Weekly peak volume = lrTarget / longRunPct (same rule as before), so totals scale with these targets.
 */
const PEAK_LONG_RUN_MI_BY_DISTANCE_AND_INTENSITY = {
  "5K": { Easy: 3, Moderate: 4, Challenging: 5, Intense: 6 },
  "10K": { Easy: 5, Moderate: 6, Challenging: 8, Intense: 8 },
  "10 Miler": { Easy: 8, Moderate: 8, Challenging: 10, Intense: 12 },
  "Half Marathon": { Easy: 11, Moderate: 12, Challenging: 14, Intense: 15 },
  Marathon: { Easy: 20, Moderate: 20, Challenging: 22, Intense: 22 },
};

const _normalizeIntensityCache = new Map();
function normalizeIntensityLabel(intensity) {
  const raw = String(intensity ?? "").trim().toLowerCase();
  const hit = _normalizeIntensityCache.get(raw);
  if (hit !== undefined) return hit;
  let out = "Moderate";
  if (raw === "easy") out = "Easy";
  else if (raw === "moderate") out = "Moderate";
  else if (raw === "challenging") out = "Challenging";
  else if (raw === "intense") out = "Intense";
  if (_normalizeIntensityCache.size < 32) {
    _normalizeIntensityCache.set(raw, out);
  }
  return out;
}

/** Map common distance strings to canonical keys used in tables (LONG_RUN_PCT, peak LR, etc.). */
const _normalizeRaceDistanceCache = new Map();
function normalizeRaceDistance(distance) {
  const s = String(distance ?? "").replace(/\u00A0/g, " ").trim();
  const cached = _normalizeRaceDistanceCache.get(s);
  if (cached !== undefined) return cached;
  const lower = s.toLowerCase();
  const map = {
    "5k": "5K",
    "10k": "10K",
    "10 miler": "10 Miler",
    "half marathon": "Half Marathon",
    marathon: "Marathon",
  };
  const out = map[lower] ?? s;
  if (_normalizeRaceDistanceCache.size < 64) {
    _normalizeRaceDistanceCache.set(s, out);
  }
  return out;
}

/** Fixed starting weekly volume (mi) by race distance + intensity — replaces user mileage input. */
const FIXED_START_WEEKLY_MI_BY_DISTANCE_AND_INTENSITY = {
  /** Moderate: higher than Easy so tempo/interval/LR weeks are not volume-starved vs Easy. */
  "5K": { Easy: 8, Moderate: 12, Challenging: 12, Intense: 16 },
  "10K": { Easy: 8, Moderate: 10, Challenging: 14, Intense: 20 },
  "10 Miler": { Easy: 10, Moderate: 12, Challenging: 18, Intense: 24 },
  "Half Marathon": { Easy: 11, Moderate: 14, Challenging: 20, Intense: 28 },
};

/** Calendar-week knots; Marathon fixed start (mi) scales with plan length (linear between knots). */
const MARATHON_FIXED_START_PLAN_WEEKS = Object.freeze([16, 20, 24, 28, 32]);
const MARATHON_FIXED_START_MI_BY_INTENSITY = Object.freeze({
  Easy: Object.freeze([20, 18, 15, 12, 10]),
  Moderate: Object.freeze([24, 21, 18, 15, 12]),
  Challenging: Object.freeze([28, 25, 22, 19, 16]),
  Intense: Object.freeze([32, 29, 26, 23, 20]),
});

/**
 * Set only while `getPlan` is building a Marathon plan — used by getFixedStartWeeklyMiles / marathon WOW floor.
 * Cleared in `getPlan` finally (single-threaded; avoids threading calendar weeks through every rebuild call).
 */
let _marathonCalendarWeeksForFixedStart = null;
/**
 * Set while `getPlan` builds a short non-Easy 10K plan (≤6 training weeks): gentler cutback vs
 * `CUTBACK_TOTAL_MILEAGE_FRAC` (0.92) plus a minimum cutback vol bump; paired with blended WOW
 * in `enforceParsedWeekOverWeekCap` for the week after cutback.
 */
let _tenKShortNonEasyCutbackFrac = null;

function marathonFixedStartWeeklyMiForPlanLength(intensity, calendarWeeks) {
  const inten = normalizeIntensityLabel(intensity);
  const vals =
    MARATHON_FIXED_START_MI_BY_INTENSITY[inten] ??
    MARATHON_FIXED_START_MI_BY_INTENSITY.Moderate;
  const knots = MARATHON_FIXED_START_PLAN_WEEKS;
  const W = Math.max(1, roundWhole(Number(calendarWeeks) || 16));
  const wLo = knots[0];
  const wHi = knots[knots.length - 1];
  const w = W < wLo ? wLo : W > wHi ? wHi : W;
  if (w <= knots[0]) return vals[0];
  for (let i = 0; i < knots.length - 1; i++) {
    if (w <= knots[i + 1]) {
      const a = knots[i];
      const b = knots[i + 1];
      const t = b > a ? (w - a) / (b - a) : 0;
      return roundWhole(vals[i] + t * (vals[i + 1] - vals[i]));
    }
  }
  return vals[vals.length - 1];
}

function getFixedStartWeeklyMiles(distance, intensity) {
  const d = normalizeRaceDistance(distance);
  const inten = normalizeIntensityLabel(intensity);
  if (d === "Marathon") {
    const cal =
      _marathonCalendarWeeksForFixedStart != null
        ? _marathonCalendarWeeksForFixedStart
        : 32;
    return marathonFixedStartWeeklyMiForPlanLength(inten, cal);
  }
  const row = FIXED_START_WEEKLY_MI_BY_DISTANCE_AND_INTENSITY[d];
  if (row && row[inten] != null) return row[inten];
  if (row) return row.Moderate;
  return (
    FIXED_START_WEEKLY_MI_BY_DISTANCE_AND_INTENSITY["10K"]?.Moderate ?? 10
  );
}

function isEasyIntensity(intensity) {
  return normalizeIntensityLabel(intensity) === "Easy";
}

/** Easy plans: no single workout (easy, tempo, or Saturday) longer than goal race distance (miles). */
const EASY_INTENSITY_MAX_SINGLE_RUN_MI_BY_RACE_DISTANCE = Object.freeze({
  "5K": 3.1,
  "10K": 6.2,
});

function getEasyIntensityRaceDistanceRunCapMi(distance) {
  const d = normalizeRaceDistance(distance);
  return EASY_INTENSITY_MAX_SINGLE_RUN_MI_BY_RACE_DISTANCE[d] ?? null;
}

/** Clamps session miles for Easy 5K/10K only; returns `miles` unchanged for other plans. */
function clampEasyIntensityRunMilesToRaceDistance(miles, distance, intensity) {
  if (!isEasyIntensity(intensity)) return miles;
  const cap = getEasyIntensityRaceDistanceRunCapMi(distance);
  if (cap == null) return miles;
  const x = Number(miles);
  if (!Number.isFinite(x)) return miles;
  return Math.min(x, cap);
}

/** Saturday primary endurance line: 5K and 10K Easy use “Easy Run”; all other plans use “Long Run”. */
function primarySaturdayRunLabel(distance, intensity = "") {
  const d = normalizeRaceDistance(distance);
  const inten = normalizeIntensityLabel(intensity);
  if (d === "5K") return "Easy Run";
  if (d === "10K" && inten === "Easy") return "Easy Run";
  return "Long Run";
}

function formatPrimarySaturdayRunLine(
  distance,
  lrMiles,
  unitPreference,
  intensity = ""
) {
  return `${primarySaturdayRunLabel(distance, intensity)} (${formatDist(
    lrMiles,
    unitPreference
  )})`;
}

function isMarathonIntensePlan(distance, intensity) {
  return (
    normalizeRaceDistance(distance) === "Marathon" &&
    normalizeIntensityLabel(intensity) === "Intense"
  );
}

function isMarathonModeratePlan(distance, intensity) {
  return (
    normalizeRaceDistance(distance) === "Marathon" &&
    normalizeIntensityLabel(intensity) === "Moderate"
  );
}

/** Marathon / Half / 10 Miler / 10K / 5K Moderate: shared min tempo + interval + min easy per slot (see buildTrainingWeek). */
function usesSharedModerateQualityFloorPlan(distance, intensity) {
  const d = normalizeRaceDistance(distance);
  return (
    normalizeIntensityLabel(intensity) === "Moderate" &&
    (d === "Marathon" ||
      d === "Half Marathon" ||
      d === "10 Miler" ||
      d === "10K" ||
      d === "5K")
  );
}

/** 5K Challenging/Intense: minimum tempo session (mi); cutback caps may not pull below this when weekly allows. */
const FIVE_K_NON_MODERATE_MIN_TEMPO_MI = 3;

function usesFiveKChallengingOrIntenseQualityTempoFloor(distance, intensity) {
  const d = normalizeRaceDistance(distance);
  if (d !== "5K") return false;
  const inten = normalizeIntensityLabel(intensity);
  return inten === "Challenging" || inten === "Intense";
}

/** Challenging/Intense distances other than 5K: min whole tempo mi (5K uses {@link FIVE_K_NON_MODERATE_MIN_TEMPO_MI}). */
const QUALITY_CHALLENGING_INTENSE_MIN_TEMPO_MI = 4;

function usesChallengingIntenseNonFiveKQualityTempoFloor(distance, intensity) {
  if (isEasyIntensity(intensity)) return false;
  const d = normalizeRaceDistance(distance);
  if (d === "5K") return false;
  const inten = normalizeIntensityLabel(intensity);
  return inten === "Challenging" || inten === "Intense";
}

/**
 * Pull from easy budget so tempo is at least {@link QUALITY_CHALLENGING_INTENSE_MIN_TEMPO_MI} on thin quality weeks.
 */
function bumpTempoWithEasyBudgetToChallengingIntenseFloor(
  distance,
  intensity,
  runSlots,
  tempoMiForSched,
  easyBudget
) {
  if (!usesChallengingIntenseNonFiveKQualityTempoFloor(distance, intensity)) {
    return { tempoMiForSched, easyBudget };
  }
  if (runSlots < 2) return { tempoMiForSched, easyBudget };
  const minT = QUALITY_CHALLENGING_INTENSE_MIN_TEMPO_MI;
  if (tempoMiForSched <= 0 || tempoMiForSched >= minT) {
    return { tempoMiForSched, easyBudget };
  }
  const needT = minT - tempoMiForSched;
  const takeT = Math.min(needT, easyBudget);
  let t = tempoMiForSched + takeT;
  let e = easyBudget - takeT;
  const ct = capTempoRunMilesMi(t, distance, intensity);
  e += ct.returnedToEasy;
  t = ct.assigned;
  return { tempoMiForSched: t, easyBudget: e };
}

/** Last pre-taper training week: min whole weekly mi (LR still capped at peakLrMi in build). */
function marathonMinPeakTrainingWeekVolMi(peakLrMi, longRunPct, distance, intensity) {
  const p = Math.max(4, roundWhole(Number(peakLrMi) || 20));
  const base = weeklyMilesForLongRunTarget(p, longRunPct);
  if (!isMarathonIntensePlan(distance, intensity)) return base;
  return Math.max(base, roundWhole(base * MARATHON_INTENSE_PLAN_BIAS.peakWeeklyVolMult));
}

function getPeakLongRunTargetMiForIntensity(distance, intensity) {
  const d = normalizeRaceDistance(distance);
  const row = PEAK_LONG_RUN_MI_BY_DISTANCE_AND_INTENSITY[d];
  const inten = normalizeIntensityLabel(intensity);
  if (row && row[inten] != null) return row[inten];
  if (row) return row.Moderate;
  const fallback =
    PEAK_LONG_RUN_MI_BY_DISTANCE_AND_INTENSITY["10K"]?.Moderate ?? 6;
  return fallback;
}

/** Marathon peak-week long run (mi) — same table row as other distances. */
function getMarathonPeakLongRunTargetMi(intensity) {
  return getPeakLongRunTargetMiForIntensity("Marathon", intensity);
}

/**
 * Weekly mileage at peak: goal LR ÷ longRunPct, floored at current base, capped at same LR ceiling.
 */
function computePeakWeeklyMiles(startMiles, distance, longRunPct, intensity) {
  const lrTarget = getPeakLongRunTargetMiForIntensity(distance, intensity);
  const lrMax = lrTarget;
  const peakFromGoal = lrTarget / longRunPct;
  const peakCap = lrMax / longRunPct;
  let peak = Math.min(peakCap, Math.max(peakFromGoal, startMiles));
  if (
    normalizeRaceDistance(distance) === "Marathon" &&
    isEasyIntensity(intensity)
  ) {
    peak = Math.min(peak, 40);
  }
  if (isMarathonIntensePlan(distance, intensity)) {
    const baseline = lrTarget / longRunPct;
    peak = Math.max(
      peak,
      roundWhole(baseline * MARATHON_INTENSE_PLAN_BIAS.peakWeeklyVolMult)
    );
  }
  return peak;
}

/**
 * Geometric progression from start → peakTarget over T weeks (last week = peakTarget).
 * When start > peakTarget, volume decreases toward the plan peak (e.g. 15 mpw runner, 5K Easy cap 12).
 * Cutback every 4th week (not on peak week): volume × cutbackMult (default ~0.8).
 */
function buildGoalDrivenVolumes(
  trainingWeekCount,
  startMiles,
  peakMiles,
  cutbackMult = CUTBACK_TOTAL_MILEAGE_FRAC
) {
  const T = trainingWeekCount;
  if (T <= 0) return [];

  const start = Math.max(Number(startMiles) || 0.1, 0.1);
  const peakTarget = Math.max(Number(peakMiles) || start, 0.1);
  const ascending = start <= peakTarget;
  const cb = Number(cutbackMult);
  const cutbackFactor =
    Number.isFinite(cb) && cb > 0 && cb < 1 ? cb : CUTBACK_TOTAL_MILEAGE_FRAC;

  if (T === 1) {
    return [roundWhole(peakTarget)];
  }

  const ratio = peakTarget / start;
  const r = Math.pow(ratio, 1 / (T - 1));
  const raw = [];
  for (let i = 0; i < T; i++) {
    if (i === T - 1) raw.push(peakTarget);
    else raw.push(start * Math.pow(r, i));
  }
  raw[T - 1] = peakTarget;

  for (let i = 0; i < T; i++) {
    const weekNum = i + 1;
    if (isCutbackWeek(weekNum, T)) {
      raw[i] *= cutbackFactor;
    }
  }
  raw[T - 1] = peakTarget;

  const rounded = raw.map((v) => roundWhole(v));
  for (let i = 1; i < T; i++) {
    const weekNum = i + 1;
    if (isCutbackWeek(weekNum, T)) continue;
    if (ascending) {
      if (rounded[i] < rounded[i - 1]) rounded[i] = rounded[i - 1];
    } else if (rounded[i] > rounded[i - 1]) {
      rounded[i] = rounded[i - 1];
    }
  }
  rounded[T - 1] = roundWhole(peakTarget);
  return rounded;
}

/** Integer weekly volume (mi) so round(weekly × pct) hits target long-run miles. */
function weeklyMilesForLongRunTarget(targetLR, longRunPct) {
  const pct = Number(longRunPct) || 0.3;
  const t = roundWhole(targetLR);
  for (let w = 1; w <= 250; w++) {
    if (roundWhole(w * pct) === t) return w;
  }
  return Math.max(1, roundWhole(targetLR / pct));
}

/**
 * Week 1 (onboarding) always strictly lower planned volume than week 2 when T ≥ 2.
 * Uses min(week2−1, 92% of week2), with a 2-week floor when peak LR is known (canonical LR distances).
 */
function enforceOnboardingLowerThanWeek2(
  vols,
  distance,
  longRunPct,
  peakLrMiForCanonicalPlan,
  week1FloorMi = null
) {
  const T = vols.length;
  if (T < 2) return vols;
  const out = [...vols];
  if (out[0] > out[1]) {
    return out;
  }
  const d = String(distance || "").trim();
  const peakLr = peakLrMiForCanonicalPlan ?? 20;
  const marathonTwoWeekFloor =
    d === "Marathon" && T === 2 && peakLrMiForCanonicalPlan != null
      ? weeklyMilesForLongRunTarget(Math.max(4, peakLr - 1), longRunPct)
      : null;
  const halfTwoWeekFloor =
    d === "Half Marathon" && T === 2 && peakLrMiForCanonicalPlan != null
      ? weeklyMilesForLongRunTarget(Math.max(3, peakLr - 1), longRunPct)
      : null;
  const tenMilerTwoWeekFloor =
    d === "10 Miler" && T === 2 && peakLrMiForCanonicalPlan != null
      ? weeklyMilesForLongRunTarget(Math.max(2, peakLr - 1), longRunPct)
      : null;
  const tenKTwoWeekFloor =
    d === "10K" && T === 2 && peakLrMiForCanonicalPlan != null
      ? weeklyMilesForLongRunTarget(Math.max(2, peakLr - 1), longRunPct)
      : null;
  const fiveKTwoWeekFloor =
    d === "5K" && T === 2 && peakLrMiForCanonicalPlan != null
      ? weeklyMilesForLongRunTarget(Math.max(2, peakLr - 1), longRunPct)
      : null;
  const twoWeekLrFloor =
    marathonTwoWeekFloor ??
    halfTwoWeekFloor ??
    tenMilerTwoWeekFloor ??
    tenKTwoWeekFloor ??
    fiveKTwoWeekFloor;

  const capGap = Math.max(1, out[1] - 1);
  const capPct = Math.max(1, roundWhole(out[1] * 0.92));

  let w1 = Math.min(out[0], capGap, capPct);
  if (twoWeekLrFloor != null) {
    w1 = Math.max(w1, twoWeekLrFloor);
  }

  if (w1 >= out[1]) {
    out[1] = Math.max(out[1], w1 + 1);
  }

  out[0] = Math.min(w1, out[1] - 1);
  if (twoWeekLrFloor != null) {
    out[0] = Math.max(out[0], twoWeekLrFloor);
    if (out[0] >= out[1]) {
      out[1] = Math.max(out[1], out[0] + 1);
    }
  }

  const floor = Number(week1FloorMi);
  if (Number.isFinite(floor) && floor > 0 && out[0] < floor) {
    out[0] = roundWhole(floor);
    if (out[1] <= out[0]) {
      out[1] = out[0] + 1;
    }
  }

  return out;
}

/** Max allowed increase: parsed week[i+1] ≤ floor(week[i] × (1 + this)) (stricter than round). */
const MAX_WEEK_OVER_WEEK_INCREASE = 0.15;

function maxParsedTotalForWowIncrease(tmPrev) {
  const p = Number(tmPrev);
  if (Number.isNaN(p) || p <= 0) return 0;
  return Math.floor(p * (1 + MAX_WEEK_OVER_WEEK_INCREASE) + 1e-9);
}

/**
 * Reference load (max parsed, planned mi) for skip-cutback WOW: most recent non-cutback week
 * strictly before plan index `nextPlanIdx`. Used by Marathon and 10K.
 */
function skipCutbackWowRefLoad(plan, nextPlanIdx, trainingWeekCount, vols = null) {
  const T = trainingWeekCount;
  let refIdx = -1;
  for (let j = nextPlanIdx - 1; j >= 0; j--) {
    if (!isCutbackWeek(j + 1, T)) {
      refIdx = j;
      break;
    }
  }
  if (refIdx < 0) return null;
  const parsed = plan[refIdx].totalMileage;
  const budgetMi =
    vols != null && vols[refIdx] != null
      ? roundWhole(vols[refIdx])
      : parsed;
  return Math.max(parsed, budgetMi);
}

/**
 * Skip-cutback reference using **parsed** totals only (no max with internal `vols`).
 * Used for short 5K non-Easy WOW so every build edge caps vs displayed week totals, not inflated budgets.
 */
function skipCutbackWowRefLoadParsedOnly(plan, nextPlanIdx, trainingWeekCount) {
  const T = trainingWeekCount;
  let refIdx = -1;
  for (let j = nextPlanIdx - 1; j >= 0; j--) {
    if (!isCutbackWeek(j + 1, T)) {
      refIdx = j;
      break;
    }
  }
  if (refIdx < 0) return null;
  const p = Number(plan[refIdx].totalMileage);
  if (!Number.isFinite(p) || p <= 0) return null;
  return roundWhole(p);
}

/**
 * Marathon / 10K / 5K Easy: for a build week at plan index `nextPlanIdx`, parsed WOW cap is vs the most
 * recent non-cutback week before it (cutback weeks in between are skipped). Cutback target weeks are not
 * capped by this rule (~20% vs prior build is handled elsewhere). Week 2 is uncapped (onboarding),
 * except 5K Easy uses skip-cutback like other marathon-style distances. 5K non-Easy uses this helper
 * in `enforceParsedWeekOverWeekCap` so each non-cutback week caps vs the prior non-cutback week (not vs
 * a cutback week in between).
 * When `vols` is passed, reference load is max(parsed, planned mi) so caps track volume intent.
 * Exception: 5K non-Easy with training weeks ≤
 * {@link FIVE_K_NON_EASY_ONBOARDING_WOW_PARSED_ONLY_MAX_TRAINING_WEEKS} — every **build** week caps vs the
 * prior skip-cutback week’s **parsed** total only (not max with internal `vols`), so inflated budgets do not
 * widen WOW on w1→w2, w2→w3, etc.
 */
function marathonParsedWowCapForWeekAfterEdge(
  plan,
  nextPlanIdx,
  trainingWeekCount,
  vols = null,
  normalizedDistance = null,
  intensity = null
) {
  const T = trainingWeekCount;
  const weekNum = nextPlanIdx + 1;
  if (nextPlanIdx < 1 || weekNum > T) return Infinity;

  const fiveKShortNonEasy =
    normalizeRaceDistance(normalizedDistance) === "5K" &&
    !isEasyIntensity(intensity) &&
    T <= FIVE_K_NON_EASY_ONBOARDING_WOW_PARSED_ONLY_MAX_TRAINING_WEEKS;

  if (nextPlanIdx === 1) {
    if (fiveKShortNonEasy) {
      const refParsed = skipCutbackWowRefLoadParsedOnly(plan, 1, T);
      if (refParsed == null) return Infinity;
      return maxParsedTotalForWowIncrease(refParsed);
    }
    return Infinity;
  }
  if (isCutbackWeek(weekNum, T)) return Infinity;

  if (fiveKShortNonEasy) {
    const refParsed = skipCutbackWowRefLoadParsedOnly(plan, nextPlanIdx, T);
    if (refParsed == null) return Infinity;
    return maxParsedTotalForWowIncrease(refParsed);
  }

  const refLoad = skipCutbackWowRefLoad(plan, nextPlanIdx, T, vols);
  if (refLoad == null) return Infinity;
  return maxParsedTotalForWowIncrease(refLoad);
}

/** Outer sweeps for enforceParsedWeekOverWeekCap; inner work is mostly O(log Δvol) rebuilds now. */
const WOW_PARSED_CAP_OUTER_MAX = 48;

/**
 * One WOW violation on edge plan[i]→plan[i+1]: find the largest vols[i+1] in [floorNext, cur] with
 * parsed total ≤ cap, using binary search (O(log range) rebuilds vs O(range) −1 steps).
 * Returns true if vols/plan were updated.
 */
function binarySearchReduceVolsForParsedWowCap(
  plan,
  vols,
  nextIdx,
  trainingWeekCount,
  cap,
  floorNext,
  daysPerWeek,
  normalizedDistance,
  unitPref,
  longRunPct,
  scheduleIntensity
) {
  const T = trainingWeekCount;
  /**
   * Short 5K non-Easy: quality-floor `effectiveWeeklyMi` bumps must be suppressed while binary-searching
   * WOW shrink for **every** week index — not only week 2 — or parsed totals stay high and caps never bite
   * (e.g. w2→w3 stuck at ~19 mi vs floor(1.15×w2)).
   */
  const suppressEff =
    normalizeRaceDistance(normalizedDistance) === "5K" &&
    !isEasyIntensity(scheduleIntensity) &&
    T <= FIVE_K_NON_EASY_ONBOARDING_WOW_PARSED_ONLY_MAX_TRAINING_WEEKS &&
    nextIdx >= 1;
  const curV = roundWhole(vols[nextIdx]);
  const fl = roundWhole(floorNext);
  if (curV <= fl) return false;

  vols[nextIdx] = fl;
  rebuildTrainingWeekPlanEntry(
    plan,
    vols,
    nextIdx,
    T,
    daysPerWeek,
    normalizedDistance,
    unitPref,
    longRunPct,
    scheduleIntensity,
    false,
    suppressEff
  );
  if (plan[nextIdx].totalMileage > cap) {
    vols[nextIdx] = curV;
    rebuildTrainingWeekPlanEntry(
      plan,
      vols,
      nextIdx,
      T,
      daysPerWeek,
      normalizedDistance,
      unitPref,
      longRunPct,
      scheduleIntensity,
      false,
      suppressEff
    );
    return false;
  }

  let lo = fl;
  let hi = curV;
  let best = fl;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    vols[nextIdx] = mid;
    rebuildTrainingWeekPlanEntry(
      plan,
      vols,
      nextIdx,
      T,
      daysPerWeek,
      normalizedDistance,
      unitPref,
      longRunPct,
      scheduleIntensity,
      false,
      suppressEff
    );
    const tmMid = plan[nextIdx].totalMileage;
    if (tmMid <= cap) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  vols[nextIdx] = best;
  rebuildTrainingWeekPlanEntry(
    plan,
    vols,
    nextIdx,
    T,
    daysPerWeek,
    normalizedDistance,
    unitPref,
    longRunPct,
    scheduleIntensity,
    false,
    suppressEff
  );
  if (suppressEff) {
    rebuildTrainingWeekPlanEntry(
      plan,
      vols,
      nextIdx,
      T,
      daysPerWeek,
      normalizedDistance,
      unitPref,
      longRunPct,
      scheduleIntensity,
      false,
      false
    );
    if (plan[nextIdx].totalMileage > cap) {
      vols[nextIdx] = best;
      rebuildTrainingWeekPlanEntry(
        plan,
        vols,
        nextIdx,
        T,
        daysPerWeek,
        normalizedDistance,
        unitPref,
        longRunPct,
        scheduleIntensity,
        false,
        true
      );
    }
  }
  return true;
}

function rebuildTrainingWeekPlanEntry(
  plan,
  vols,
  idx,
  trainingWeekCount,
  daysPerWeek,
  normalizedDistance,
  unitPref,
  longRunPct,
  intensity = "",
  skipMarathonWowClamp = false,
  suppressEffectiveWeeklyBump = false
) {
  const weekNum = idx + 1;
  const wmi = resolveWeeklyMiForScheduleBuild(
    plan,
    vols,
    idx,
    trainingWeekCount,
    normalizedDistance
  );
  vols[idx] = wmi;
  if (isFiveKEasyPlan(normalizedDistance, intensity)) {
    const schedEz = buildFiveKEasyTrainingWeekSchedule(
      roundWhole(wmi),
      weekNum,
      trainingWeekCount,
      unitPref,
      weekNum === 1
    );
    const tmEz = computeWeekMileage(schedEz, unitPref);
    plan[idx] = {
      ...plan[idx],
      schedule: schedEz,
      totalMileage: tmEz,
      totalMileageLabel: `${tmEz} ${unitPref === "Kilometers" ? "km" : "miles"}`,
    };
    return;
  }
  if (isTenKEasyPlan(normalizedDistance, intensity)) {
    const schedTk = buildTenKEasyTrainingWeekSchedule(
      roundWhole(wmi),
      weekNum,
      trainingWeekCount,
      unitPref,
      weekNum === 1
    );
    const tmTk = computeWeekMileage(schedTk, unitPref);
    plan[idx] = {
      ...plan[idx],
      schedule: schedTk,
      totalMileage: tmTk,
      totalMileageLabel: `${tmTk} ${unitPref === "Kilometers" ? "km" : "miles"}`,
    };
    return;
  }
  if (isHalfMarathonEasyPlan(normalizedDistance, intensity)) {
    const schedHe = buildHalfMarathonEasyTrainingWeekSchedule(
      roundWhole(wmi),
      weekNum,
      trainingWeekCount,
      unitPref,
      weekNum === 1
    );
    const tmHe = computeWeekMileage(schedHe, unitPref);
    plan[idx] = {
      ...plan[idx],
      schedule: schedHe,
      totalMileage: tmHe,
      totalMileageLabel: `${tmHe} ${unitPref === "Kilometers" ? "km" : "miles"}`,
    };
    return;
  }
  if (isTenMilerEasyPlan(normalizedDistance, intensity)) {
    const schedTm = buildTenMilerEasyTrainingWeekSchedule(
      roundWhole(wmi),
      weekNum,
      trainingWeekCount,
      unitPref,
      weekNum === 1
    );
    const tmTm = computeWeekMileage(schedTm, unitPref);
    plan[idx] = {
      ...plan[idx],
      schedule: schedTm,
      totalMileage: tmTm,
      totalMileageLabel: `${tmTm} ${unitPref === "Kilometers" ? "km" : "miles"}`,
    };
    return;
  }
  const distR = normalizeRaceDistance(normalizedDistance);
  const cutbackPrevLr = cutbackPrevLongRunMilesFromPlan(
    plan,
    vols,
    idx,
    trainingWeekCount,
    unitPref,
    longRunPct
  );
  const prevWeekLr =
    idx > 0
      ? parseLongRunMilesFromAnyDay(plan[idx - 1]?.schedule)
      : null;
  const priorNcLr =
    idx > 0
      ? priorNonCutbackLongRunMiFromPlan(plan, idx, trainingWeekCount)
      : null;
  const priorBuildTempoMi =
    idx > 0
      ? priorNonCutbackBuildWeekTempoMiFromPlan(plan, idx, trainingWeekCount)
      : null;
  const cutbackEasySessionCapMi =
    idx > 0 &&
    isCutbackWeek(weekNum, trainingWeekCount) &&
    !isEasyIntensity(intensity)
      ? cutbackEasySessionCapFromPriorWeekSchedule(
          plan[idx - 1]?.schedule,
          normalizedDistance
        )
      : null;
  /** Inner WOW-shrink passes: null avoids effectiveWeeklyMi re-bumps that defeat skip-cutback caps. */
  const volOut =
    (distR === "Marathon" && skipMarathonWowClamp) ||
    suppressEffectiveWeeklyBump
      ? null
      : !isFiveKEasyPlan(normalizedDistance, intensity) &&
          !isTenKEasyPlan(normalizedDistance, intensity) &&
          !isHalfMarathonEasyPlan(normalizedDistance, intensity) &&
          !isTenMilerEasyPlan(normalizedDistance, intensity) &&
          (usesCanonicalLongRunSchedule(normalizedDistance) ||
            isEasyIntensity(intensity))
        ? {}
        : null;
  const sched = buildTrainingWeek(
    wmi,
    daysPerWeek,
    normalizedDistance,
    unitPref,
    longRunPct,
    false,
    weekNum === 1,
    cutbackPrevLr,
    intensity,
    weekNum,
    trainingWeekCount,
    prevWeekLr,
    volOut,
    priorNcLr,
    priorBuildTempoMi,
    cutbackEasySessionCapMi
  );
  if (
    volOut &&
    volOut.effectiveWeeklyMi != null &&
    volOut.effectiveWeeklyMi > roundWhole(wmi) &&
    (!isCutbackWeek(weekNum, trainingWeekCount) ||
      (normalizeRaceDistance(normalizedDistance) === "5K" &&
        !isEasyIntensity(intensity)))
  ) {
    vols[idx] = volOut.effectiveWeeklyMi;
  }
  const tm = computeWeekMileage(sched, unitPref);
  plan[idx] = {
    ...plan[idx],
    schedule: sched,
    totalMileage: tm,
    totalMileageLabel: `${tm} ${unitPref === "Kilometers" ? "km" : "miles"}`,
  };
  if (
    isCutbackWeek(weekNum, trainingWeekCount) &&
    !isEasyIntensity(intensity) &&
    daysPerWeek >= 4
  ) {
    vols[idx] = Math.min(roundWhole(vols[idx]), Math.max(1, tm));
  }

  if (
    !skipMarathonWowClamp &&
    distR === "Marathon" &&
    idx >= 2 &&
    !isCutbackWeek(weekNum, trainingWeekCount)
  ) {
    const T = trainingWeekCount;
    const peakLr = Math.max(
      4,
      roundWhole(getMarathonPeakLongRunTargetMi(intensity))
    );
    const minPeakVolFloor = marathonMinPeakTrainingWeekVolMi(
      peakLr,
      longRunPct,
      normalizedDistance,
      intensity
    );
    const es = roundWhole(getFixedStartWeeklyMiles(normalizedDistance, intensity));
    let guard = 0;
    while (
      guard < 280 &&
      plan[idx].totalMileage >
        marathonParsedWowCapForWeekAfterEdge(
          plan,
          idx,
          T,
          vols,
          normalizedDistance,
          intensity
        )
    ) {
      guard += 1;
      const floorNext = marathonLoweringFloorVols(
        normalizedDistance,
        idx,
        T,
        es,
        minPeakVolFloor
      );
      if (roundWhole(vols[idx]) <= floorNext) break;
      vols[idx] = roundWhole(vols[idx]) - 1;
      rebuildTrainingWeekPlanEntry(
        plan,
        vols,
        idx,
        T,
        daysPerWeek,
        normalizedDistance,
        unitPref,
        longRunPct,
        intensity,
        true
      );
    }
  }
}

/**
 * Enforces parsed WOW on adjacent weeks. Non-Marathon: week[i+1] ≤ floor(week[i] × 1.15).
 * Marathon: build weeks use skip-cutback reference (see marathonParsedWowCapForWeekAfterEdge).
 * Only lowers vols[i+1] (no “raise prior” here), except Marathon may raise vols[i] when floored.
 * @param opts.marathonPreamble — if true (default), Marathon: once bump last week to peak LR volume before sweep.
 * @param opts.lastWeekFloor — min vols[T-1] when lowering (use marathon peak vol on second pass).
 * @param opts.marathonPeakLrMi — marathon peak long-run miles (18–20 by intensity); default 20.
 * @param opts.marathonNonCutbackFloorMi — Marathon: min vols for non-cutback weeks when lowering (fixed start).
 * @param opts.intensity — passed through to schedule rebuilds (Easy = easy-only weeks).
 */
function enforceParsedWeekOverWeekCap(
  plan,
  vols,
  trainingWeekCount,
  normalizedDistance,
  daysPerWeek,
  unitPref,
  longRunPct,
  opts = {}
) {
  const T = trainingWeekCount;
  const scheduleIntensity = opts.intensity ?? "";
  const marathonPreamble = opts.marathonPreamble !== false;
  const lastWeekFloor =
    opts.lastWeekFloor !== undefined ? opts.lastWeekFloor : 1;

  const peakLrMi =
    normalizedDistance === "Marathon"
      ? Math.max(4, roundWhole(Number(opts.marathonPeakLrMi ?? 20)))
      : 20;
  const minMarathonPeakVol =
    normalizedDistance === "Marathon"
      ? marathonMinPeakTrainingWeekVolMi(
          peakLrMi,
          longRunPct,
          normalizedDistance,
          scheduleIntensity
        )
      : 1;

  if (T < 2) {
    if (T === 1 && normalizedDistance === "Marathon") {
      vols[0] = Math.max(vols[0], minMarathonPeakVol);
      rebuildTrainingWeekPlanEntry(
        plan,
        vols,
        0,
        T,
        daysPerWeek,
        normalizedDistance,
        unitPref,
        longRunPct,
        scheduleIntensity
      );
    }
    return;
  }

  if (marathonPreamble && normalizedDistance === "Marathon") {
    vols[T - 1] = Math.max(vols[T - 1], minMarathonPeakVol);
    rebuildTrainingWeekPlanEntry(
      plan,
      vols,
      T - 1,
      T,
      daysPerWeek,
      normalizedDistance,
      unitPref,
      longRunPct,
      scheduleIntensity
    );
  }

  const shortRacePeakFloor =
    (normalizedDistance === "10 Miler" ||
      normalizedDistance === "10K" ||
      normalizedDistance === "5K") &&
    lastWeekFloor > 1
      ? roundWhole(Number(lastWeekFloor))
      : 0;
  if (shortRacePeakFloor > 0) {
    const skipShortRacePeakFloorOnLastWeek =
      normalizedDistance === "5K" &&
      !isEasyIntensity(scheduleIntensity) &&
      T === 2;
    if (!skipShortRacePeakFloorOnLastWeek) {
      vols[T - 1] = Math.max(vols[T - 1], shortRacePeakFloor);
      rebuildTrainingWeekPlanEntry(
        plan,
        vols,
        T - 1,
        T,
        daysPerWeek,
        normalizedDistance,
        unitPref,
        longRunPct,
        scheduleIntensity
      );
    }
  }

  for (let outer = 0; outer < WOW_PARSED_CAP_OUTER_MAX; outer++) {
    let changed = false;
    for (let i = 0; i < T - 1; i++) {
      let floorNext = marathonLoweringFloorVols(
        normalizedDistance,
        i + 1,
        T,
        opts.marathonNonCutbackFloorMi,
        lastWeekFloor
      );
      if (
        normalizedDistance === "5K" &&
        !isEasyIntensity(scheduleIntensity) &&
        i === 0 &&
        T === 2
      ) {
        /** Week 2 is also peak week: peak vol floor must not block w1→w2 WOW lowering. */
        floorNext = 1;
      }
      if (
        normalizedDistance === "5K" &&
        !isEasyIntensity(scheduleIntensity) &&
        T <= FIVE_K_NON_EASY_ONBOARDING_WOW_PARSED_ONLY_MAX_TRAINING_WEEKS &&
        i + 1 === T - 1
      ) {
        /**
         * Short 5K non-Easy peak week: `lastWeekFloor` (min peak mi) must not be the WOW shrink floor.
         * Otherwise skip-cutback caps vs the last **build** week (e.g. w3 before cutback) cannot pull peak
         * below that floor — w4→w5 jumps like 11→21 despite floor(1.15×w3) ≈ 16.
         */
        floorNext = 1;
      }

      let inner = 0;
      while (inner < 80) {
        inner += 1;
        const tmPrev = plan[i].totalMileage;
        const marathonStyleParsedWowCap =
          normalizedDistance === "Marathon" ||
          normalizedDistance === "10K" ||
          normalizedDistance === "5K";
        let cap = marathonStyleParsedWowCap
          ? marathonParsedWowCapForWeekAfterEdge(
              plan,
              i + 1,
              T,
              vols,
              normalizedDistance,
              scheduleIntensity
            )
          : maxParsedTotalForWowIncrease(tmPrev);
        /**
         * Short non-Easy 10K (≤6 training weeks): after a cutback, tighten WOW using
         * mean(pre-cutback ref, cutback parsed); penultimate→peak edge uncapped so peak can land in one step.
         * 5K non-Easy: skip-cutback WOW (same as 5K Easy) so build weeks cap vs the last non-cutback week.
         */
        const useShortRaceBlendedWow =
          normalizedDistance === "10K" && T <= 6;
        if (useShortRaceBlendedWow && i === T - 2) {
          cap = Infinity;
        } else if (
          useShortRaceBlendedWow &&
          isCutbackWeek(i + 1, T)
        ) {
          const refL = skipCutbackWowRefLoad(plan, i + 1, T, vols);
          if (refL != null) {
            const cutTm = plan[i].totalMileage;
            const blended = (refL + cutTm) / 2;
            cap = Math.min(cap, maxParsedTotalForWowIncrease(blended));
          }
        }
        const tmNext = plan[i + 1].totalMileage;
        if (tmNext <= cap) break;

        if (vols[i + 1] > floorNext) {
          const did = binarySearchReduceVolsForParsedWowCap(
            plan,
            vols,
            i + 1,
            T,
            cap,
            floorNext,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            scheduleIntensity
          );
          if (did) {
            changed = true;
            continue;
          }
        }
        if (
          normalizedDistance === "Marathon" &&
          opts.marathonNonCutbackFloorMi != null &&
          vols[i] < 450
        ) {
          vols[i] += 1;
          rebuildTrainingWeekPlanEntry(
            plan,
            vols,
            i,
            T,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            scheduleIntensity
          );
          changed = true;
          continue;
        }
        break;
      }
    }
    if (!changed) break;
  }
}

/**
 * Prior week index `i` for edge plan[i]→plan[i+1]: if that week is in a cutback min(vols,0.8×prev)
 * chain, return the vol index to bump so parsed WOW can rise (Moderate-only reconcile / adjacent WOW).
 */
function marathonWowBumpVolsIndex(i, vols, trainingWeekCount) {
  let j = i;
  while (j >= 0) {
    const weekNum = j + 1;
    if (!isCutbackWeek(weekNum, trainingWeekCount) || j < 1) return j;
    const prevVol = roundWhole(vols[j - 1]);
    const cutCap = Math.max(
      1,
      roundWhole(prevVol * CUTBACK_TOTAL_MILEAGE_FRAC)
    );
    const vj = roundWhole(vols[j]);
    if (vj < cutCap) return j;
    j -= 1;
  }
  return 0;
}

/** Marathon + Moderate only: parsed adjacent WOW ≤15% with iteration caps (does not run for Easy). */
const MARATHON_ADJ_WOW_INNER_MAX = 72;
const MARATHON_ADJ_WOW_OUTER_MAX = 40;

function enforceMarathonModerateAdjacentParsedWowCaps(
  plan,
  vols,
  trainingWeekCount,
  normalizedDistance,
  daysPerWeek,
  unitPref,
  longRunPct,
  marathonNonCutbackFloorMi,
  lastWeekFloor,
  marathonPeakLrMi,
  intensity = ""
) {
  const T = trainingWeekCount;
  if (!isMarathonModeratePlan(normalizedDistance, intensity) || T < 2) return;

  const peakLrMi = Math.max(4, roundWhole(Number(marathonPeakLrMi) || 20));
  const minPeakVol = marathonMinPeakTrainingWeekVolMi(
    peakLrMi,
    longRunPct,
    normalizedDistance,
    intensity
  );

  for (let outer = 0; outer < MARATHON_ADJ_WOW_OUTER_MAX; outer++) {
    let changed = false;
    for (let i = 0; i < T - 1; i++) {
      let inner = 0;
      while (inner < MARATHON_ADJ_WOW_INNER_MAX) {
        inner += 1;
        const cap = marathonParsedWowCapForWeekAfterEdge(
          plan,
          i + 1,
          T,
          vols,
          normalizedDistance,
          intensity
        );
        const tmNext = plan[i + 1].totalMileage;
        if (tmNext <= cap) break;

        const floorNext = marathonLoweringFloorVols(
          normalizedDistance,
          i + 1,
          T,
          marathonNonCutbackFloorMi,
          lastWeekFloor
        );

        if (vols[i + 1] > floorNext) {
          vols[i + 1] -= 1;
          rebuildTrainingWeekPlanEntry(
            plan,
            vols,
            i + 1,
            T,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            intensity
          );
          changed = true;
          continue;
        }

        if (marathonNonCutbackFloorMi != null) {
          const bumpIdx = marathonWowBumpVolsIndex(i, vols, T);
          if (bumpIdx >= 0 && vols[bumpIdx] < 450) {
            vols[bumpIdx] += 1;
            rebuildTrainingWeekPlanEntry(
              plan,
              vols,
              bumpIdx,
              T,
              daysPerWeek,
              normalizedDistance,
              unitPref,
              longRunPct,
              intensity
            );
            changed = true;
            continue;
          }
        }

        if (i + 1 === T - 1 && vols[i + 1] > minPeakVol) {
          vols[i + 1] -= 1;
          rebuildTrainingWeekPlanEntry(
            plan,
            vols,
            i + 1,
            T,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            intensity
          );
          changed = true;
          continue;
        }

        break;
      }
    }
    if (!changed) break;
  }
}

/**
 * Marathon: jointly satisfy peak ≥ target LR, parsed WOW ≤ 15% (skip-cutback reference for build weeks),
 * and cutback strictly below prior. May raise prior build / adjust cutback vols when reconciling.
 */
function reconcileMarathonWowCutbackPeak(
  plan,
  vols,
  trainingWeekCount,
  normalizedDistance,
  daysPerWeek,
  unitPref,
  longRunPct,
  marathonPeakLrMi = 20,
  intensity = "",
  marathonNonCutbackFloorMi = null
) {
  const T = trainingWeekCount;
  if (normalizedDistance !== "Marathon" || T < 2) return;

  const peakLr = Math.max(4, roundWhole(Number(marathonPeakLrMi) || 20));
  const minPeakVol = marathonMinPeakTrainingWeekVolMi(
    peakLr,
    longRunPct,
    normalizedDistance,
    intensity
  );

  for (let outer = 0; outer < 320; outer++) {
    let changed = false;

    if (vols[T - 1] < minPeakVol) {
      vols[T - 1] = minPeakVol;
      rebuildTrainingWeekPlanEntry(
        plan,
        vols,
        T - 1,
        T,
        daysPerWeek,
        normalizedDistance,
        unitPref,
        longRunPct,
        intensity
      );
      changed = true;
    }

    for (let i = 0; i < T - 1; i++) {
      const floorNext = marathonLoweringFloorVols(
        normalizedDistance,
        i + 1,
        T,
        marathonNonCutbackFloorMi,
        minPeakVol
      );
      let inner = 0;
      while (inner < 500 && vols[i + 1] > floorNext) {
        inner += 1;
        const capFwd = marathonParsedWowCapForWeekAfterEdge(
          plan,
          i + 1,
          T,
          vols,
          normalizedDistance,
          intensity
        );
        if (plan[i + 1].totalMileage <= capFwd) break;
        vols[i + 1] -= 1;
        rebuildTrainingWeekPlanEntry(
          plan,
          vols,
          i + 1,
          T,
          daysPerWeek,
          normalizedDistance,
          unitPref,
          longRunPct,
          intensity
        );
        changed = true;
      }
    }

    for (let i = 1; i < T; i++) {
      if (!isCutbackWeek(i + 1, T)) continue;
      let inner = 0;
      while (inner < 500 && plan[i].totalMileage >= plan[i - 1].totalMileage) {
        inner += 1;
        if (vols[i] > 1) {
          vols[i] -= 1;
          rebuildTrainingWeekPlanEntry(
            plan,
            vols,
            i,
            T,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            intensity
          );
          changed = true;
        } else if (vols[i - 1] < 450) {
          vols[i - 1] += 1;
          rebuildTrainingWeekPlanEntry(
            plan,
            vols,
            i - 1,
            T,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            intensity
          );
          changed = true;
        } else break;
      }
    }

    for (let i = T - 2; i >= 0; i--) {
      const weekNumI = i + 1;
      let inner = 0;
      while (inner < 500) {
        inner += 1;
        const capRev = marathonParsedWowCapForWeekAfterEdge(
          plan,
          i + 1,
          T,
          vols,
          normalizedDistance,
          intensity
        );
        if (plan[i + 1].totalMileage <= capRev) break;
        if (!isCutbackWeek(weekNumI, T)) {
          if (vols[i] >= 450) break;
          const esN = Number(marathonNonCutbackFloorMi);
          const esR =
            Number.isFinite(esN) && esN > 0 ? roundWhole(esN) : 0;
          if (normalizedDistance === "Marathon") {
            const floorNext = marathonLoweringFloorVols(
              normalizedDistance,
              i + 1,
              T,
              marathonNonCutbackFloorMi,
              minPeakVol
            );
            if (vols[i + 1] > floorNext) {
              vols[i + 1] -= 1;
              rebuildTrainingWeekPlanEntry(
                plan,
                vols,
                i + 1,
                T,
                daysPerWeek,
                normalizedDistance,
                unitPref,
                longRunPct,
                intensity
              );
              changed = true;
              continue;
            }
            if (i === 0 && esR > 0 && roundWhole(vols[0]) >= esR) {
              break;
            }
            break;
          }
          vols[i] += 1;
          rebuildTrainingWeekPlanEntry(
            plan,
            vols,
            i,
            T,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            intensity
          );
          changed = true;
        } else {
          if (i < 1 || vols[i - 1] >= 450) break;
          vols[i - 1] += 1;
          rebuildTrainingWeekPlanEntry(
            plan,
            vols,
            i - 1,
            T,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            intensity
          );
          changed = true;
          let strictInner = 0;
          while (
            strictInner < 500 &&
            plan[i].totalMileage >= plan[i - 1].totalMileage &&
            vols[i] > 1
          ) {
            strictInner += 1;
            vols[i] -= 1;
            rebuildTrainingWeekPlanEntry(
              plan,
              vols,
              i,
              T,
              daysPerWeek,
              normalizedDistance,
              unitPref,
              longRunPct,
              intensity
            );
            changed = true;
          }
          let bumpedCutback = false;
          let bumpInner = 0;
          while (bumpInner < 500 && vols[i] < 450) {
            bumpInner += 1;
            const capBump = marathonParsedWowCapForWeekAfterEdge(
              plan,
              i + 1,
              T,
              vols,
              normalizedDistance,
              intensity
            );
            if (plan[i + 1].totalMileage <= capBump) break;
            const saved = vols[i];
            vols[i] += 1;
            rebuildTrainingWeekPlanEntry(
              plan,
              vols,
              i,
              T,
              daysPerWeek,
              normalizedDistance,
              unitPref,
              longRunPct,
              intensity
            );
            if (plan[i].totalMileage >= plan[i - 1].totalMileage) {
              vols[i] = saved;
              rebuildTrainingWeekPlanEntry(
                plan,
                vols,
                i,
                T,
                daysPerWeek,
                normalizedDistance,
                unitPref,
                longRunPct,
                intensity
              );
              break;
            }
            bumpedCutback = true;
            changed = true;
          }
          if (
            !bumpedCutback &&
            plan[i + 1].totalMileage >
              marathonParsedWowCapForWeekAfterEdge(
                plan,
                i + 1,
                T,
                vols,
                normalizedDistance,
                intensity
              )
          ) {
            continue;
          }
        }
      }
    }

    if (!changed) break;
  }
}

/**
 * Cutback weeks must show strictly lower parsed total mileage than the week before.
 * Reconcile/cap passes can inflate a cutback week's vols; prefer lowering the cutback,
 * else raise the prior build week so tm_cut < tm_prev remains satisfiable.
 */
function enforceCutbackStrictlyBelowPriorWeek(
  plan,
  vols,
  trainingWeekCount,
  normalizedDistance,
  daysPerWeek,
  unitPref,
  longRunPct,
  intensity = ""
) {
  const T = trainingWeekCount;
  if (T < 2) return;

  for (let outer = 0; outer < 120; outer++) {
    let changed = false;
    for (let i = 1; i < T; i++) {
      const weekNum = i + 1;
      if (!isCutbackWeek(weekNum, T)) continue;

      let cutInner = 0;
      while (
        cutInner < 500 &&
        plan[i].totalMileage >= plan[i - 1].totalMileage
      ) {
        cutInner += 1;
        if (vols[i] > 1) {
          vols[i] -= 1;
          rebuildTrainingWeekPlanEntry(
            plan,
            vols,
            i,
            T,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            intensity,
            false,
            normalizeRaceDistance(normalizedDistance) === "5K"
          );
          changed = true;
          continue;
        }
        if (vols[i - 1] < 450) {
          vols[i - 1] += 1;
          rebuildTrainingWeekPlanEntry(
            plan,
            vols,
            i - 1,
            T,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            intensity
          );
          changed = true;
          continue;
        }
        break;
      }
    }
    if (!changed) break;
  }
}

/**
 * Half Marathon: peak week weekly mi fits peak LR; penultimate week one LR step down when T ≥ 2.
 */
function applyHalfMarathonPreTaperLongRunTargets(
  vols,
  longRunPct,
  peakLrMi = 15
) {
  const T = vols.length;
  if (T < 1) return vols;
  const out = [...vols];
  const p = Math.max(2, roundWhole(Number(peakLrMi) || 15));
  out[T - 1] = roundWhole(weeklyMilesForLongRunTarget(p, longRunPct));
  if (T >= 2) {
    out[T - 2] = roundWhole(
      weeklyMilesForLongRunTarget(Math.max(2, p - 1), longRunPct)
    );
    if (out[T - 2] >= out[T - 1]) {
      out[T - 2] = roundWhole(
        weeklyMilesForLongRunTarget(Math.max(2, p - 2), longRunPct)
      );
    }
    if (out[T - 2] >= out[T - 1]) {
      out[T - 2] = Math.max(1, out[T - 1] - 1);
    }
  }
  return out;
}

/**
 * 10 Miler: peak week weekly mi fits peak LR; penultimate week one LR step down when T ≥ 2.
 */
function applyTenMilerPreTaperLongRunTargets(
  vols,
  longRunPct,
  peakLrMi = 12
) {
  const T = vols.length;
  if (T < 1) return vols;
  const out = [...vols];
  const p = Math.max(2, roundWhole(Number(peakLrMi) || 12));
  out[T - 1] = roundWhole(weeklyMilesForLongRunTarget(p, longRunPct));
  if (T >= 2) {
    out[T - 2] = roundWhole(
      weeklyMilesForLongRunTarget(Math.max(2, p - 1), longRunPct)
    );
    if (out[T - 2] >= out[T - 1]) {
      out[T - 2] = roundWhole(
        weeklyMilesForLongRunTarget(Math.max(2, p - 2), longRunPct)
      );
    }
    if (out[T - 2] >= out[T - 1]) {
      out[T - 2] = Math.max(1, out[T - 1] - 1);
    }
  }
  return out;
}

/**
 * 10K: peak week weekly mi fits peak LR; penultimate week one LR step down when T ≥ 2.
 */
function applyTenKPreTaperLongRunTargets(
  vols,
  longRunPct,
  peakLrMi = 8
) {
  const T = vols.length;
  if (T < 1) return vols;
  const out = [...vols];
  const p = Math.max(2, roundWhole(Number(peakLrMi) || 8));
  out[T - 1] = roundWhole(weeklyMilesForLongRunTarget(p, longRunPct));
  if (T >= 2) {
    out[T - 2] = roundWhole(
      weeklyMilesForLongRunTarget(Math.max(2, p - 1), longRunPct)
    );
    if (out[T - 2] >= out[T - 1]) {
      out[T - 2] = roundWhole(
        weeklyMilesForLongRunTarget(Math.max(2, p - 2), longRunPct)
      );
    }
    if (out[T - 2] >= out[T - 1]) {
      out[T - 2] = Math.max(1, out[T - 1] - 1);
    }
  }
  return out;
}

/**
 * 5K: peak week weekly mi fits peak LR; penultimate week one LR step down when T ≥ 2.
 */
function applyFiveKPreTaperLongRunTargets(
  vols,
  longRunPct,
  peakLrMi = 6
) {
  const T = vols.length;
  if (T < 1) return vols;
  const out = [...vols];
  const p = Math.max(2, roundWhole(Number(peakLrMi) || 6));
  out[T - 1] = roundWhole(weeklyMilesForLongRunTarget(p, longRunPct));
  if (T >= 2) {
    out[T - 2] = roundWhole(
      weeklyMilesForLongRunTarget(Math.max(2, p - 1), longRunPct)
    );
    if (out[T - 2] >= out[T - 1]) {
      out[T - 2] = roundWhole(
        weeklyMilesForLongRunTarget(Math.max(2, p - 2), longRunPct)
      );
    }
    if (out[T - 2] >= out[T - 1]) {
      out[T - 2] = Math.max(1, out[T - 1] - 1);
    }
  }
  return out;
}

/**
 * Marathon: peak week LR = peakLrMi; prior week one step down when T ≥ 2.
 * Intense: same peak LR mi as Challenging but higher weekly mi (extra miles are non–long-run).
 */
function applyMarathonPreTaperLongRunTargets(
  vols,
  longRunPct,
  peakLrMi = 20,
  distance = "",
  intensity = ""
) {
  const T = vols.length;
  if (T < 1) return vols;
  const out = [...vols];
  const p = Math.max(4, roundWhole(Number(peakLrMi) || 20));
  const mult = isMarathonIntensePlan(distance, intensity)
    ? MARATHON_INTENSE_PLAN_BIAS.peakWeeklyVolMult
    : 1;
  out[T - 1] = roundWhole(weeklyMilesForLongRunTarget(p, longRunPct) * mult);
  if (T >= 2) {
    out[T - 2] = roundWhole(
      weeklyMilesForLongRunTarget(Math.max(4, p - 1), longRunPct) * mult
    );
    if (out[T - 2] >= out[T - 1]) {
      out[T - 2] = roundWhole(
        weeklyMilesForLongRunTarget(Math.max(4, p - 2), longRunPct) * mult
      );
    }
    if (out[T - 2] >= out[T - 1]) {
      out[T - 2] = Math.max(1, out[T - 1] - 1);
    }
  }
  return out;
}

function isCutbackWeek(weekNum, trainingWeekCount) {
  return weekNum < trainingWeekCount && weekNum >= 4 && weekNum % 4 === 0;
}

/**
 * 5K Easy unified volume (training weeks): start 8 mi, peak 12 mi; adjacent non-cutback WOW ≤15%;
 * cutback weeks strictly below the prior week; schedules built separately (Saturday 2→3 mi with load,
 * weekday easies in {1,1.5,2,2.5}, Wednesday Walk, easy lines with 15 min/mi hints, runs ≤ race distance).
 */
const FIVE_K_EASY_WEEKLY_START_MI = 8;
const FIVE_K_EASY_WEEKLY_PEAK_MI = 12;

/**
 * 10K Easy unified volume: start 8 mi, peak 13 mi; WOW ≤15% on adjacent non-cutback weeks; cutbacks
 * down vs prior; schedule Mon/Thu easy, Tue Walk, Wed/Fri Rest, Sat 4→5 mi with load; runs ≤ 6.2 mi;
 * taper vol floored at 65% of peak (~35% max drop from peak).
 */
const TEN_K_EASY_WEEKLY_START_MI = 8;
const TEN_K_EASY_WEEKLY_PEAK_MI = 13;
const TEN_K_EASY_TAPER_MIN_FRAC_OF_PEAK = 0.65;

/**
 * Half Marathon Easy unified: start 11 mi, peak 21 mi; WOW ≤15% vs last non-cutback week on each build week;
 * Mon/Wed/Thu easy ≤4 mi; Sat Long Run 5→11 mi with weekly volume; taper floor 65% of peak; race week ~17 mi total.
 */
const HALF_MARATHON_EASY_WEEKLY_START_MI = 11;
const HALF_MARATHON_EASY_WEEKLY_PEAK_MI = 21;
const HALF_MARATHON_EASY_TAPER_MIN_FRAC_OF_PEAK = 0.65;
/** Challenging/Intense (non-Easy unified): taper long run and weekly floor so short plans keep a meaningful LR. */
const HALF_MARATHON_NON_EASY_TAPER_MIN_LONG_RUN_MI = 6;
/** Fits min LR + 4 mi tempo + 2 mi × three taper easy days (Challenging/Intense). */
const HALF_MARATHON_NON_EASY_TAPER_MIN_WEEKLY_MI = 16;
const HALF_MARATHON_NON_EASY_TAPER_RESERVE_FOR_TEMPO_EASY_MI = 3;
/** 10 Miler non-Easy: taper LR floor + weekly floor (mirror Half; shorter race, same min LR). */
const TEN_MILER_NON_EASY_TAPER_MIN_LONG_RUN_MI = 6;
const TEN_MILER_NON_EASY_TAPER_MIN_WEEKLY_MI = 16;
const TEN_MILER_NON_EASY_TAPER_RESERVE_FOR_TEMPO_EASY_MI = 3;
/** 10K non-Easy: taper Saturday Run floor + weekly floor (shorter race than 10 Miler). */
const TEN_K_NON_EASY_TAPER_MIN_LONG_RUN_MI = 4;
const TEN_K_NON_EASY_TAPER_MIN_WEEKLY_MI = 14;
const TEN_K_NON_EASY_TAPER_RESERVE_FOR_TEMPO_EASY_MI = 3;
const HALF_MARATHON_EASY_MAX_EASY_RUN_MI = 4;

/**
 * 10 Miler Easy unified: start 10 mi, peak 20 mi; WOW ≤15% vs last non-cutback week on each build week;
 * Mon/Wed/Thu easy ≤4 mi; Sat Long Run 4→8 mi with weekly volume; taper floor 65% of peak; race week ~14 mi total.
 */
const TEN_MILER_EASY_WEEKLY_START_MI = 10;
const TEN_MILER_EASY_WEEKLY_PEAK_MI = 20;
const TEN_MILER_EASY_TAPER_MIN_FRAC_OF_PEAK = 0.65;
const TEN_MILER_EASY_MAX_EASY_RUN_MI = 4;

function buildFiveKEasyWeeklyVolumes(trainingWeekCount) {
  const T = Math.max(0, roundWhole(Number(trainingWeekCount) || 0));
  if (T < 1) return [];
  const nonCutWeekNums = [];
  for (let w = 1; w <= T; w++) {
    if (!isCutbackWeek(w, T)) nonCutWeekNums.push(w);
  }
  const B = nonCutWeekNums.length;
  const chain = [];
  for (let k = 0; k < B; k++) {
    if (k === 0) {
      chain.push(FIVE_K_EASY_WEEKLY_START_MI);
    } else {
      const prev = chain[k - 1];
      chain.push(
        Math.min(FIVE_K_EASY_WEEKLY_PEAK_MI, maxParsedTotalForWowIncrease(prev))
      );
    }
  }
  const v = [];
  let ci = 0;
  for (let w = 1; w <= T; w++) {
    if (isCutbackWeek(w, T)) {
      const prevVol = v[w - 2];
      v.push(
        Math.max(1, Math.min(prevVol - 1, roundWhole(prevVol * 0.82)))
      );
    } else {
      v.push(chain[ci++]);
    }
  }
  if (T >= 2) {
    v[0] = Math.max(
      1,
      Math.min(v[0], Math.min(roundWhole(v[1] * 0.92), v[1] - 1))
    );
  }
  for (let pass = 0; pass < 32; pass++) {
    let changed = false;
    for (let i = 1; i < T; i++) {
      const wCurr = i + 1;
      const wPrev = i;
      if (
        !isCutbackWeek(wCurr, T) &&
        !isCutbackWeek(wPrev, T) &&
        v[i] < v[i - 1]
      ) {
        v[i] = v[i - 1];
        changed = true;
      }
    }
    for (let i = 0; i < T - 1; i++) {
      const cap = maxParsedTotalForWowIncrease(v[i]);
      if (v[i + 1] > cap) {
        v[i + 1] = cap;
        changed = true;
      }
    }
    for (let i = 1; i < T; i++) {
      if (isCutbackWeek(i + 1, T) && v[i] >= v[i - 1]) {
        v[i] = Math.max(1, v[i - 1] - 1);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return v.map((x) => roundWhole(x));
}

function buildTenKEasyWeeklyVolumes(trainingWeekCount) {
  const T = Math.max(0, roundWhole(Number(trainingWeekCount) || 0));
  if (T < 1) return [];
  const nonCutWeekNums = [];
  for (let w = 1; w <= T; w++) {
    if (!isCutbackWeek(w, T)) nonCutWeekNums.push(w);
  }
  const B = nonCutWeekNums.length;
  const chain = [];
  for (let k = 0; k < B; k++) {
    if (k === 0) {
      chain.push(TEN_K_EASY_WEEKLY_START_MI);
    } else {
      const prev = chain[k - 1];
      chain.push(
        Math.min(TEN_K_EASY_WEEKLY_PEAK_MI, maxParsedTotalForWowIncrease(prev))
      );
    }
  }
  const v = [];
  let ci = 0;
  for (let w = 1; w <= T; w++) {
    if (isCutbackWeek(w, T)) {
      const prevVol = v[w - 2];
      v.push(
        Math.max(1, Math.min(prevVol - 1, roundWhole(prevVol * 0.82)))
      );
    } else {
      v.push(chain[ci++]);
    }
  }
  if (T >= 2) {
    v[0] = Math.max(
      1,
      Math.min(v[0], Math.min(roundWhole(v[1] * 0.92), v[1] - 1))
    );
  }
  for (let pass = 0; pass < 32; pass++) {
    let changed = false;
    for (let i = 1; i < T; i++) {
      const wCurr = i + 1;
      const wPrev = i;
      if (
        !isCutbackWeek(wCurr, T) &&
        !isCutbackWeek(wPrev, T) &&
        v[i] < v[i - 1]
      ) {
        v[i] = v[i - 1];
        changed = true;
      }
    }
    for (let i = 0; i < T - 1; i++) {
      const cap = maxParsedTotalForWowIncrease(v[i]);
      if (v[i + 1] > cap) {
        v[i + 1] = cap;
        changed = true;
      }
    }
    for (let i = 1; i < T; i++) {
      if (isCutbackWeek(i + 1, T) && v[i] >= v[i - 1]) {
        v[i] = Math.max(1, v[i - 1] - 1);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return v.map((x) => roundWhole(x));
}

/** Vol index (0-based) of the last non-cutback training week strictly before `weekNum` (1-based), or -1. */
function lastNonCutbackVolsIndexBeforeWeek(weekNum, trainingWeekCount) {
  const T = trainingWeekCount;
  for (let j = weekNum - 2; j >= 0; j--) {
    if (!isCutbackWeek(j + 1, T)) return j;
  }
  return -1;
}

/** Non-cutback weeks: cap vol vs last non-cutback week (not the week after a cutback). */
function applyHalfMarathonEasyWowSkipCutback(vols, trainingWeekCount) {
  const T = trainingWeekCount;
  for (let i = 0; i < T; i++) {
    const wn = i + 1;
    if (isCutbackWeek(wn, T)) continue;
    const refIdx = lastNonCutbackVolsIndexBeforeWeek(wn, T);
    if (refIdx < 0) continue;
    const cap = maxParsedTotalForWowIncrease(vols[refIdx]);
    if (vols[i] > cap) vols[i] = cap;
  }
}

/** Same skip-cutback WOW rule as Half Marathon Easy; used by 10 Miler Easy volume chain. */
function applyTenMilerEasyWowSkipCutback(vols, trainingWeekCount) {
  const T = trainingWeekCount;
  for (let i = 0; i < T; i++) {
    const wn = i + 1;
    if (isCutbackWeek(wn, T)) continue;
    const refIdx = lastNonCutbackVolsIndexBeforeWeek(wn, T);
    if (refIdx < 0) continue;
    const cap = maxParsedTotalForWowIncrease(vols[refIdx]);
    if (vols[i] > cap) vols[i] = cap;
  }
}

function buildHalfMarathonEasyWeeklyVolumes(trainingWeekCount) {
  const T = Math.max(0, roundWhole(Number(trainingWeekCount) || 0));
  if (T < 1) return [];
  const nonCutWeekNums = [];
  for (let w = 1; w <= T; w++) {
    if (!isCutbackWeek(w, T)) nonCutWeekNums.push(w);
  }
  const B = nonCutWeekNums.length;
  const chain = [];
  for (let k = 0; k < B; k++) {
    if (k === 0) {
      chain.push(HALF_MARATHON_EASY_WEEKLY_START_MI);
    } else {
      const prev = chain[k - 1];
      chain.push(
        Math.min(
          HALF_MARATHON_EASY_WEEKLY_PEAK_MI,
          maxParsedTotalForWowIncrease(prev)
        )
      );
    }
  }
  const v = [];
  let ci = 0;
  for (let w = 1; w <= T; w++) {
    if (isCutbackWeek(w, T)) {
      const prevVol = v[w - 2];
      v.push(
        Math.max(1, Math.min(prevVol - 1, roundWhole(prevVol * 0.82)))
      );
    } else {
      v.push(chain[ci++]);
    }
  }
  if (T >= 2) {
    v[0] = Math.max(
      1,
      Math.min(v[0], Math.min(roundWhole(v[1] * 0.92), v[1] - 1))
    );
  }
  for (let pass = 0; pass < 40; pass++) {
    let changed = false;
    for (let i = 1; i < T; i++) {
      const wCurr = i + 1;
      const wPrev = i;
      if (
        !isCutbackWeek(wCurr, T) &&
        !isCutbackWeek(wPrev, T) &&
        v[i] < v[i - 1]
      ) {
        v[i] = v[i - 1];
        changed = true;
      }
    }
    const vBefore = v.slice();
    applyHalfMarathonEasyWowSkipCutback(v, T);
    for (let i = 0; i < T; i++) {
      if (v[i] !== vBefore[i]) {
        changed = true;
        break;
      }
    }
    for (let i = 1; i < T; i++) {
      if (isCutbackWeek(i + 1, T) && v[i] >= v[i - 1]) {
        v[i] = Math.max(1, v[i - 1] - 1);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return v.map((x) => roundWhole(x));
}

function buildTenMilerEasyWeeklyVolumes(trainingWeekCount) {
  const T = Math.max(0, roundWhole(Number(trainingWeekCount) || 0));
  if (T < 1) return [];
  const nonCutWeekNums = [];
  for (let w = 1; w <= T; w++) {
    if (!isCutbackWeek(w, T)) nonCutWeekNums.push(w);
  }
  const B = nonCutWeekNums.length;
  const chain = [];
  for (let k = 0; k < B; k++) {
    if (k === 0) {
      chain.push(TEN_MILER_EASY_WEEKLY_START_MI);
    } else {
      const prev = chain[k - 1];
      chain.push(
        Math.min(
          TEN_MILER_EASY_WEEKLY_PEAK_MI,
          maxParsedTotalForWowIncrease(prev)
        )
      );
    }
  }
  const v = [];
  let ci = 0;
  for (let w = 1; w <= T; w++) {
    if (isCutbackWeek(w, T)) {
      const prevVol = v[w - 2];
      v.push(
        Math.max(1, Math.min(prevVol - 1, roundWhole(prevVol * 0.82)))
      );
    } else {
      v.push(chain[ci++]);
    }
  }
  if (T >= 2) {
    v[0] = Math.max(
      1,
      Math.min(v[0], Math.min(roundWhole(v[1] * 0.92), v[1] - 1))
    );
  }
  for (let pass = 0; pass < 40; pass++) {
    let changed = false;
    for (let i = 1; i < T; i++) {
      const wCurr = i + 1;
      const wPrev = i;
      if (
        !isCutbackWeek(wCurr, T) &&
        !isCutbackWeek(wPrev, T) &&
        v[i] < v[i - 1]
      ) {
        v[i] = v[i - 1];
        changed = true;
      }
    }
    const vBefore = v.slice();
    applyTenMilerEasyWowSkipCutback(v, T);
    for (let i = 0; i < T; i++) {
      if (v[i] !== vBefore[i]) {
        changed = true;
        break;
      }
    }
    for (let i = 1; i < T; i++) {
      if (isCutbackWeek(i + 1, T) && v[i] >= v[i - 1]) {
        v[i] = Math.max(1, v[i - 1] - 1);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return v.map((x) => roundWhole(x));
}

/**
 * 5K Easy training week: Mon/Tue/Thu easy {1,1.5,2,2.5}; Wed Walk; Fri Rest; Sat Saturday Run.
 */
function buildFiveKEasyTrainingWeekSchedule(
  weeklyMi,
  weekNum,
  trainingWeekCount,
  unitPreference,
  _onboardingEasyOnly
) {
  const sched = {};
  WEEK_ORDER.forEach((d) => {
    sched[d] = "Rest";
  });
  const wk = Math.max(1, roundWhole(Number(weeklyMi) || 1));
  const walkDay = "Wednesday";
  const easyDays = ["Monday", "Tuesday", "Thursday"];

  const trySolve = () => {
    for (let sat = 2; sat <= 3; sat++) {
      const s = roundWhole(
        clampEasyIntensityRunMilesToRaceDistance(sat, "5K", "Easy")
      );
      const softCap = Math.min(
        2.5,
        fiveKEasyWeekdayCapMi(s),
        maxEasyRunMiGivenLongRun(s, "5K", "Easy")
      );
      const remAfterSat = wk - s;
      if (remAfterSat < 1) continue;

      for (let th = 1; th <= softCap + 1e-9; th += 0.5) {
        const walkMi = Math.round(th * 2) / 2;
        let r2 = remAfterSat - walkMi;
        if (r2 < -1e-9) continue;
        if (r2 < 0) r2 = 0;
        const milesO = distributeFiveKEasyVariedWeekdayMiles(
          r2,
          easyDays,
          softCap,
          0
        );
        if (milesO.length !== easyDays.length) continue;
        const sumO = milesO.reduce((a, b) => a + b, 0);
        if (Math.abs(sumO - r2) > 0.06) continue;
        if (Math.abs(s + walkMi + sumO - wk) > 0.06) continue;
        return {
          satMi: s,
          walkMi,
          milesO: rebalanceFiveKWalkSurplusToEarlierDays(
            easyDays,
            milesO,
            softCap
          ),
        };
      }
    }
    return null;
  };

  let sol = trySolve();
  if (!sol) {
    const s = 2;
    const softCap = 2.5;
    const walkMi = 1;
    const r2 = Math.max(0, wk - s - walkMi);
    let milesO = distributeFiveKEasyVariedWeekdayMiles(
      r2,
      easyDays,
      softCap,
      0
    );
    if (milesO.length !== easyDays.length) {
      milesO = easyDays.map(() => 1);
    }
    sol = {
      satMi: s,
      walkMi,
      milesO: rebalanceFiveKWalkSurplusToEarlierDays(
        easyDays,
        milesO,
        softCap
      ),
    };
  }

  sched.Saturday = formatPrimarySaturdayRunLine(
    "5K",
    sol.satMi,
    unitPreference,
    "Easy"
  );
  easyDays.forEach((day, idx) => {
    const dayMi = sol.milesO[idx] ?? 0;
    sched[day] =
      dayMi <= 0
        ? "Rest"
        : formatFiveKEasyEasyRunLabel(dayMi, unitPreference);
  });
  sched[walkDay] =
    sol.walkMi <= 0
      ? "Rest"
      : `Walk (${formatDist(sol.walkMi, unitPreference)})`;

  return sortScheduleByWeekday(replaceZeroDistanceWorkoutsWithRest(sched));
}

function tenKEasySaturdayMilesFromWeekly(weeklyMi) {
  const wk = roundWhole(Number(weeklyMi) || 1);
  const lo = TEN_K_EASY_WEEKLY_START_MI;
  const hi = TEN_K_EASY_WEEKLY_PEAK_MI;
  const span = hi > lo ? hi - lo : 1;
  const u = Math.max(0, Math.min(1, (wk - lo) / span));
  const raw = 4 + u * (5 - 4);
  return roundWhole(
    clampEasyIntensityRunMilesToRaceDistance(
      Math.min(5, Math.max(4, raw)),
      "10K",
      "Easy"
    )
  );
}

function halfMarathonEasyLongRunMilesFromWeekly(weeklyMi) {
  const wk = roundWhole(Number(weeklyMi) || 1);
  const lo = HALF_MARATHON_EASY_WEEKLY_START_MI;
  const hi = HALF_MARATHON_EASY_WEEKLY_PEAK_MI;
  const span = hi > lo ? hi - lo : 1;
  const u = Math.max(0, Math.min(1, (wk - lo) / span));
  const raw = 5 + u * (11 - 5);
  return roundWhole(
    clampEasyIntensityRunMilesToRaceDistance(
      Math.min(11, Math.max(5, raw)),
      "Half Marathon",
      "Easy"
    )
  );
}

function tenMilerEasyLongRunMilesFromWeekly(weeklyMi) {
  const wk = roundWhole(Number(weeklyMi) || 1);
  const lo = TEN_MILER_EASY_WEEKLY_START_MI;
  const hi = TEN_MILER_EASY_WEEKLY_PEAK_MI;
  const span = hi > lo ? hi - lo : 1;
  const u = Math.max(0, Math.min(1, (wk - lo) / span));
  const raw = 4 + u * (8 - 4);
  return roundWhole(
    clampEasyIntensityRunMilesToRaceDistance(
      Math.min(8, Math.max(4, raw)),
      "10 Miler",
      "Easy"
    )
  );
}

/**
 * 10K Easy: Mon + Thu easy (half steps to cap); Tue Walk; Wed/Fri Rest; Sat 4→5 mi vs weekly volume.
 */
function buildTenKEasyTrainingWeekSchedule(
  weeklyMi,
  weekNum,
  trainingWeekCount,
  unitPreference,
  _onboardingEasyOnly
) {
  const sched = {};
  WEEK_ORDER.forEach((d) => {
    sched[d] = "Rest";
  });
  const walkDay = "Tuesday";
  const easyDays = ["Monday", "Thursday"];
  const easyFloor = easyRunFloorMinMiForDistance("10K");
  const isCutback =
    weekNum != null &&
    trainingWeekCount != null &&
    trainingWeekCount >= 1 &&
    isCutbackWeek(weekNum, trainingWeekCount);
  const isOnboarding = weekNum === 1 || Boolean(_onboardingEasyOnly);
  const minPerDay = isCutback || isOnboarding ? 0 : easyFloor;

  let wk = Math.max(1, roundWhole(Number(weeklyMi) || 1));
  // Floor bump only on build weeks — never inflate cutback or onboarding vols.
  if (!isCutback && !isOnboarding) {
    for (let guard = 0; guard < 32; guard += 1) {
      const s = tenKEasySaturdayMilesFromWeekly(wk);
      const need = bumpWeeklyMiForEasyRunFloors(wk, "10K", {
        easyDayCount: easyDays.length,
        walkMi: 1,
        longRunMi: s,
      });
      if (need <= wk) break;
      wk = need;
    }
  }

  const trySolve = () => {
    const s = tenKEasySaturdayMilesFromWeekly(wk);
    const walkMi = 1;
    const easyCap = maxEasyRunMiGivenLongRun(s, "10K", "Easy");
    const remAfterSat = wk - s;
    if (remAfterSat < 2) return null; // walk 1 + at least 1 mi easy

    const r2 = remAfterSat - walkMi;
    if (r2 < 0) return null;
    const milesO = distributeTenKEasyVariedWeekdayMiles(
      r2,
      easyDays,
      easyCap,
      minPerDay
    );
    if (milesO.length !== easyDays.length) return null;
    const sumO = milesO.reduce((a, b) => a + b, 0);
    if (Math.abs(sumO - r2) > 0.06) return null;
    if (Math.abs(s + walkMi + sumO - wk) > 0.06) return null;
    return { satMi: s, walkMi, milesO };
  };

  let sol = trySolve();
  if (!sol) {
    const s = tenKEasySaturdayMilesFromWeekly(wk);
    const easyCap = maxEasyRunMiGivenLongRun(s, "10K", "Easy");
    const walkMi = 1;
    const r2 = Math.max(0, wk - s - walkMi);
    let milesO = distributeTenKEasyVariedWeekdayMiles(
      r2,
      easyDays,
      easyCap,
      minPerDay
    );
    if (milesO.length !== easyDays.length) {
      milesO = distributeEasyMilesAcrossDays(
        roundWhole(r2),
        easyDays,
        Math.max(1, Math.floor(easyCap)),
        false,
        unitPreference
      );
    }
    sol = { satMi: s, walkMi, milesO };
  }

  sol.walkMi = 1;
  const easyCapFinal = maxEasyRunMiGivenLongRun(sol.satMi, "10K", "Easy");
  if (isCutback) {
    sol.milesO = balanceTenKEasyMonThuCutbackEven(sol.milesO, easyCapFinal);
  } else {
    sol.milesO = balanceTenKEasyMonThuForProgression(
      sol.milesO,
      easyFloor,
      easyCapFinal
    );
  }

  sched.Saturday = formatPrimarySaturdayRunLine(
    "10K",
    sol.satMi,
    unitPreference,
    "Easy"
  );
  easyDays.forEach((day, idx) => {
    const dayMi = sol.milesO[idx] ?? 0;
    sched[day] =
      dayMi <= 0
        ? "Rest"
        : formatFiveKEasyEasyRunLabel(dayMi, unitPreference);
  });
  sched[walkDay] =
    sol.walkMi <= 0
      ? "Rest"
      : `Walk (${formatDist(sol.walkMi, unitPreference)})`;

  return sortScheduleByWeekday(replaceZeroDistanceWorkoutsWithRest(sched));
}

/**
 * Half Marathon Easy: Mon/Wed/Thu easy (≤4 mi); Tue/Fri Rest; Sat Long Run 5→11 mi vs weekly volume.
 */
function buildHalfMarathonEasyTrainingWeekSchedule(
  weeklyMi,
  weekNum,
  trainingWeekCount,
  unitPreference,
  _onboardingEasyOnly
) {
  const sched = {};
  WEEK_ORDER.forEach((d) => {
    sched[d] = "Rest";
  });
  const easyDays = ["Monday", "Wednesday", "Thursday"];
  const easyFloor = easyRunFloorMinMiForDistance("Half Marathon");
  const wk0 = Math.max(1, roundWhole(Number(weeklyMi) || 1));
  const isCutback =
    weekNum != null &&
    trainingWeekCount != null &&
    trainingWeekCount >= 1 &&
    isCutbackWeek(weekNum, trainingWeekCount);
  const isOnboarding = weekNum === 1 || Boolean(_onboardingEasyOnly);
  const minPerDay = isCutback || isOnboarding ? 0 : easyFloor;
  // Pin the long run to the natural weekly-volume curve so a floor bump adds
  // mileage to the EASY days (variation) instead of chasing the long run up and
  // pinning every easy run to the 3 mi floor.
  const lr = halfMarathonEasyLongRunMilesFromWeekly(wk0);
  // Only early build weeks whose natural budget can't fit the floors get bumped
  // (with a buffer); cutback and onboarding keep planned volume.
  const wk =
    isCutback || isOnboarding
      ? wk0
      : bumpWeeklyMiForEasyRunFloors(wk0, "Half Marathon", {
          easyDayCount: easyDays.length,
          longRunMi: lr,
        });

  const trySolve = () => {
    const easyCap = Math.min(
      HALF_MARATHON_EASY_MAX_EASY_RUN_MI,
      maxEasyRunMiGivenLongRun(lr, "Half Marathon", "Easy")
    );
    const rem = wk - lr;
    if (rem < 1) return null;
    const milesO = distributeTenKEasyVariedWeekdayMiles(
      rem,
      easyDays,
      easyCap,
      minPerDay
    );
    if (milesO.length !== easyDays.length) return null;
    const sumO = milesO.reduce((a, b) => a + b, 0);
    if (Math.abs(sumO - rem) > 0.06) return null;
    if (Math.abs(lr + sumO - wk) > 0.06) return null;
    return { lrMi: lr, milesO };
  };

  let sol = trySolve();
  if (!sol) {
    const easyCap = Math.min(
      HALF_MARATHON_EASY_MAX_EASY_RUN_MI,
      maxEasyRunMiGivenLongRun(lr, "Half Marathon", "Easy")
    );
    const rem = Math.max(0, wk - lr);
    let milesO = distributeTenKEasyVariedWeekdayMiles(
      rem,
      easyDays,
      easyCap,
      minPerDay
    );
    if (milesO.length !== easyDays.length) {
      milesO = distributeEasyMilesAcrossDays(
        roundWhole(rem),
        easyDays,
        Math.max(1, Math.floor(easyCap)),
        false,
        unitPreference
      );
    }
    sol = { lrMi: lr, milesO };
  }

  const easyCapFinal = Math.min(
    HALF_MARATHON_EASY_MAX_EASY_RUN_MI,
    maxEasyRunMiGivenLongRun(lr, "Half Marathon", "Easy")
  );
  if (isCutback) {
    sol.milesO = balanceEasyDaysCutbackEven(sol.milesO, easyCapFinal);
  }

  sched.Saturday = formatPrimarySaturdayRunLine(
    "Half Marathon",
    sol.lrMi,
    unitPreference,
    "Easy"
  );
  easyDays.forEach((day, idx) => {
    const dayMi = sol.milesO[idx] ?? 0;
    sched[day] =
      dayMi <= 0
        ? "Rest"
        : formatFiveKEasyEasyRunLabel(dayMi, unitPreference);
  });

  return sortScheduleByWeekday(replaceZeroDistanceWorkoutsWithRest(sched));
}

/**
 * 10 Miler Easy: Mon/Wed/Thu easy (≤4 mi); Tue/Fri Rest; Sat Long Run 4→8 mi vs weekly volume.
 */
function buildTenMilerEasyTrainingWeekSchedule(
  weeklyMi,
  weekNum,
  trainingWeekCount,
  unitPreference,
  _onboardingEasyOnly
) {
  const sched = {};
  WEEK_ORDER.forEach((d) => {
    sched[d] = "Rest";
  });
  const easyDays = ["Monday", "Wednesday", "Thursday"];
  const easyFloor = easyRunFloorMinMiForDistance("10 Miler");
  const wk0 = Math.max(1, roundWhole(Number(weeklyMi) || 1));
  const isCutback =
    weekNum != null &&
    trainingWeekCount != null &&
    trainingWeekCount >= 1 &&
    isCutbackWeek(weekNum, trainingWeekCount);
  const isOnboarding = weekNum === 1 || Boolean(_onboardingEasyOnly);
  const minPerDay = isCutback || isOnboarding ? 0 : easyFloor;
  // Pin the long run to the natural weekly-volume curve so a floor bump adds
  // mileage to the EASY days (variation) instead of chasing the long run up and
  // pinning every easy run to the 3 mi floor.
  const lr = tenMilerEasyLongRunMilesFromWeekly(wk0);
  // Only early build weeks whose natural budget can't fit the floors get bumped
  // (with a buffer); cutback and onboarding keep planned volume.
  const wk =
    isCutback || isOnboarding
      ? wk0
      : bumpWeeklyMiForEasyRunFloors(wk0, "10 Miler", {
          easyDayCount: easyDays.length,
          longRunMi: lr,
        });

  const trySolve = () => {
    const easyCap = Math.min(
      TEN_MILER_EASY_MAX_EASY_RUN_MI,
      maxEasyRunMiGivenLongRun(lr, "10 Miler", "Easy")
    );
    const rem = wk - lr;
    if (rem < 1) return null;
    const milesO = distributeTenKEasyVariedWeekdayMiles(
      rem,
      easyDays,
      easyCap,
      minPerDay
    );
    if (milesO.length !== easyDays.length) return null;
    const sumO = milesO.reduce((a, b) => a + b, 0);
    if (Math.abs(sumO - rem) > 0.06) return null;
    if (Math.abs(lr + sumO - wk) > 0.06) return null;
    return { lrMi: lr, milesO };
  };

  let sol = trySolve();
  if (!sol) {
    const easyCap = Math.min(
      TEN_MILER_EASY_MAX_EASY_RUN_MI,
      maxEasyRunMiGivenLongRun(lr, "10 Miler", "Easy")
    );
    const rem = Math.max(0, wk - lr);
    let milesO = distributeTenKEasyVariedWeekdayMiles(
      rem,
      easyDays,
      easyCap,
      minPerDay
    );
    if (milesO.length !== easyDays.length) {
      milesO = distributeEasyMilesAcrossDays(
        roundWhole(rem),
        easyDays,
        Math.max(1, Math.floor(easyCap)),
        false,
        unitPreference
      );
    }
    sol = { lrMi: lr, milesO };
  }

  const easyCapFinal = Math.min(
    TEN_MILER_EASY_MAX_EASY_RUN_MI,
    maxEasyRunMiGivenLongRun(lr, "10 Miler", "Easy")
  );
  if (isCutback) {
    sol.milesO = balanceEasyDaysCutbackEven(sol.milesO, easyCapFinal);
  }

  sched.Saturday = formatPrimarySaturdayRunLine(
    "10 Miler",
    sol.lrMi,
    unitPreference,
    "Easy"
  );
  easyDays.forEach((day, idx) => {
    const dayMi = sol.milesO[idx] ?? 0;
    sched[day] =
      dayMi <= 0
        ? "Rest"
        : formatFiveKEasyEasyRunLabel(dayMi, unitPreference);
  });

  return sortScheduleByWeekday(replaceZeroDistanceWorkoutsWithRest(sched));
}

/**
 * Planned weekly mi (vols) never step down from one non-cutback week to the next (Marathon).
 */
function enforceNonCutbackAdjacentVolsNonDecreasing(vols, trainingWeekCount) {
  const T = trainingWeekCount;
  if (T < 2) return false;
  let bumped = false;
  for (let idx = 1; idx < T; idx++) {
    const wCurr = idx + 1;
    const wPrev = idx;
    if (isCutbackWeek(wCurr, T) || isCutbackWeek(wPrev, T)) continue;
    const vPrev = roundWhole(vols[idx - 1]);
    if (roundWhole(vols[idx]) < vPrev) {
      vols[idx] = vPrev;
      bumped = true;
    }
  }
  return bumped;
}

/**
 * Minimum vols index when lowering that week for WOW / reconcile. Marathon: non-cutback training
 * weeks stay at or above fixed start volume; peak week also respects minPeakVolFloor.
 */
function marathonLoweringFloorVols(
  normalizedDistance,
  volIdx,
  trainingWeekCount,
  effectiveStart,
  minPeakVolFloor = 1
) {
  const T = trainingWeekCount;
  const peakFloor = roundWhole(Number(minPeakVolFloor) || 1);
  if (normalizedDistance !== "Marathon") {
    return volIdx === T - 1 ? Math.max(1, peakFloor) : 1;
  }
  const esN = Number(effectiveStart);
  const hasStart = Number.isFinite(esN) && esN > 0;
  const es = hasStart ? roundWhole(esN) : 0;
  const weekNum = volIdx + 1;
  let base = 1;
  if (hasStart && !isCutbackWeek(weekNum, T)) {
    base = es;
  }
  if (volIdx === T - 1) {
    return Math.max(base, peakFloor);
  }
  return Math.max(base, 1);
}

/**
 * Weekly miles fed into schedule builder. Cutback weeks cap at ~80% of prior week’s *vols* budget
 * (not prior parsed total — parsed totals shift during rebuilds and caused feedback loops with WOW/cutback passes).
 * LR cap still applies in buildTrainingWeek.
 */
function resolveWeeklyMiForScheduleBuild(
  _plan,
  vols,
  idx,
  trainingWeekCount,
  normalizedDistance = ""
) {
  const weekNum = idx + 1;
  const volRounded = roundWhole(vols[idx]);
  if (!isCutbackWeek(weekNum, trainingWeekCount) || idx < 1) {
    return volRounded;
  }
  const prevVol = roundWhole(vols[idx - 1]);
  const frac =
    _tenKShortNonEasyCutbackFrac != null
      ? _tenKShortNonEasyCutbackFrac
      : CUTBACK_TOTAL_MILEAGE_FRAC;
  let targetTm = Math.max(1, roundWhole(prevVol * frac));
  /**
   * 5K non-Easy: internal `vols` can exceed parsed totals after quality floors; cutback target must also
   * respect prior **displayed** week so ~80%×parsed stays meaningful (not ~80%×inflated vols).
   */
  if (
    normalizeRaceDistance(normalizedDistance) === "5K" &&
    idx >= 1 &&
    _plan &&
    _plan[idx - 1]
  ) {
    const p = _plan[idx - 1].totalMileage;
    const prevParsed =
      typeof p === "number" && Number.isFinite(p) ? roundWhole(p) : null;
    if (prevParsed != null && prevParsed > 0) {
      const targetFromParsed = Math.max(1, roundWhole(prevParsed * frac));
      targetTm = Math.min(targetTm, targetFromParsed);
    }
  }
  return Math.min(volRounded, targetTm);
}

function cutbackPrevLongRunMilesFromPlan(
  plan,
  vols,
  idx,
  trainingWeekCount,
  unitPref,
  longRunPct
) {
  const weekNum = idx + 1;
  if (!isCutbackWeek(weekNum, trainingWeekCount) || idx < 1) return null;
  const volPrev = vols[idx - 1];
  /** Matches displayed LR miles (same as formatDist / Long Run line for that vol). */
  const lrFromVolWhole = roundWhole(volPrev * longRunPct);
  const prevSched = plan[idx - 1]?.schedule;
  let mi = parseLongRunMilesFromAnyDay(prevSched);
  if (mi != null && mi > 0) {
    mi = Math.max(mi, lrFromVolWhole);
  } else {
    mi = lrFromVolWhole;
  }
  return mi > 0 ? mi : null;
}

/** Race-week volume budget (mi): scales with peak but caps lower for short races (Fix 5). */
function computeRaceWeekBudget(peakMiles, distance, intensity = "") {
  const d = normalizeRaceDistance(distance);
  if (d === "5K" && isEasyIntensity(intensity)) {
    const peak = Number(peakMiles);
    const p = Number.isFinite(peak) && peak > 0 ? peak : FIVE_K_EASY_WEEKLY_PEAK_MI;
    return Math.min(4, Math.max(2.8, roundWhole(p * 0.25)));
  }
  if (d === "10K" && isEasyIntensity(intensity)) {
    const peak = Number(peakMiles);
    const p = Number.isFinite(peak) && peak > 0 ? peak : TEN_K_EASY_WEEKLY_PEAK_MI;
    return Math.min(5, Math.max(2.8, roundWhole(p * 0.24)));
  }
  if (d === "Half Marathon" && isEasyIntensity(intensity)) {
    const peak = Number(peakMiles);
    const p =
      Number.isFinite(peak) && peak > 0 ? peak : HALF_MARATHON_EASY_WEEKLY_PEAK_MI;
    return Math.min(6, Math.max(3.5, roundWhole(p * 0.19)));
  }
  if (d === "10 Miler" && isEasyIntensity(intensity)) {
    const peak = Number(peakMiles);
    const p =
      Number.isFinite(peak) && peak > 0 ? peak : TEN_MILER_EASY_WEEKLY_PEAK_MI;
    return Math.min(5, Math.max(3, roundWhole(p * 0.2)));
  }
  const capByDist = {
    "5K": 5,
    "10K": 7,
    "10 Miler": 10,
    "Half Marathon": 14,
    Marathon: 18,
  };
  const peakFracByDist = {
    "5K": 0.05,
    "10K": 0.07,
    "10 Miler": 0.09,
    "Half Marathon": 0.12,
    Marathon: 0.15,
  };
  const cap = capByDist[d] ?? 12;
  const frac = peakFracByDist[d] ?? 0.11;
  return Math.min(cap, peakMiles * frac);
}

/** Whole miles (or whole km in UI) everywhere except race-day line (see raceDayLabel). */
function roundWhole(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return 0;
  return Math.round(x);
}

function maxTempoSessionMiForDistance(distance) {
  return normalizeRaceDistance(distance) === "5K"
    ? MAX_TEMPO_RUN_MI_5K
    : MAX_TEMPO_RUN_MI;
}

/** Tempo session ceiling by distance + intensity (Marathon Moderate capped below Challenging). */
function maxTempoSessionMiForPlan(distance, intensity) {
  const d = normalizeRaceDistance(distance);
  if (d === "5K") return MAX_TEMPO_RUN_MI_5K;
  if (isMarathonModeratePlan(distance, intensity)) {
    return MARATHON_MODERATE_MAX_TEMPO_RUN_MI;
  }
  return MAX_TEMPO_RUN_MI;
}

/** Returns displayed tempo mi (capped by distance, e.g. 5K ≤ 4) and miles to push back into the easy pool. */
function capTempoRunMilesMi(roundedMiles, distanceOpt, intensityOpt) {
  const rW = roundWhole(roundedMiles);
  if (!Number.isFinite(rW) || rW <= 0) {
    return { assigned: 0, returnedToEasy: 0 };
  }
  const maxCap =
    intensityOpt !== undefined && intensityOpt !== null && intensityOpt !== ""
      ? maxTempoSessionMiForPlan(distanceOpt, intensityOpt)
      : distanceOpt !== undefined && distanceOpt !== null
        ? maxTempoSessionMiForDistance(distanceOpt)
        : MAX_TEMPO_RUN_MI;
  const assigned = Math.min(maxCap, rW);
  return { assigned, returnedToEasy: rW - assigned };
}

function maxIntervalSessionMiForDistance(distance) {
  return normalizeRaceDistance(distance) === "5K"
    ? MAX_INTERVAL_RUN_MI_5K
    : MAX_INTERVAL_RUN_MI;
}

/** Interval session ceiling by distance + intensity (Marathon Moderate capped below default 8 mi). */
function maxIntervalSessionMiForPlan(distance, intensity) {
  const d = normalizeRaceDistance(distance);
  if (d === "5K") return MAX_INTERVAL_RUN_MI_5K;
  if (isMarathonModeratePlan(distance, intensity)) {
    return MARATHON_MODERATE_MAX_INTERVAL_RUN_MI;
  }
  return MAX_INTERVAL_RUN_MI;
}

function capIntervalRunMilesMi(roundedMiles, distanceOpt, intensityOpt) {
  const rW = roundWhole(roundedMiles);
  if (!Number.isFinite(rW) || rW <= 0) {
    return { assigned: 0, returnedToEasy: 0 };
  }
  const maxCap =
    intensityOpt !== undefined && intensityOpt !== null && intensityOpt !== ""
      ? maxIntervalSessionMiForPlan(distanceOpt, intensityOpt)
      : distanceOpt !== undefined && distanceOpt !== null
        ? maxIntervalSessionMiForDistance(distanceOpt)
        : MAX_INTERVAL_RUN_MI;
  const assigned = Math.min(maxCap, rW);
  return { assigned, returnedToEasy: rW - assigned };
}

function formatDist(nMiles, unitPreference) {
  const x = Number(nMiles);
  if (!Number.isFinite(x)) {
    return unitPreference === "Kilometers" ? "0 km" : "0 mi";
  }
  if (unitPreference === "Kilometers") {
    const km = x * MI_TO_KM;
    const r2 = Math.round(km * 2);
    if (r2 % 2 === 0) return `${r2 / 2} km`;
    return `${(r2 / 2).toFixed(1)} km`;
  }
  const r2 = Math.round(x * 2);
  if (r2 % 2 === 0) return `${r2 / 2} mi`;
  return `${(r2 / 2).toFixed(1)} mi`;
}

const BEGINNER_EASY_PACE_MIN_PER_MI = 15;

/** ~minutes at 15 min/mi for common 5K Easy distances (matches product copy). */
function beginnerEasyRunEstimatedMinutes(dayMiMiles) {
  const halves = Math.round(Number(dayMiMiles) * 2);
  const table = { 2: 15, 3: 23, 4: 30, 5: 38, 6: 45 };
  if (table[halves] != null) return table[halves];
  return Math.max(
    1,
    Math.round(Number(dayMiMiles) * BEGINNER_EASY_PACE_MIN_PER_MI)
  );
}

/** 5K Easy weekday easy line with beginner time hint (not used for Walk / Saturday / shakeout). */
function formatFiveKEasyEasyRunLabel(dayMiMiles, unitPreference) {
  const distPart = formatDist(dayMiMiles, unitPreference);
  const min = beginnerEasyRunEstimatedMinutes(dayMiMiles);
  return `Easy Run (${distPart} / ~${min} min)`;
}

function isFiveKEasyPlan(distance, intensity) {
  return (
    normalizeRaceDistance(distance) === "5K" && isEasyIntensity(intensity)
  );
}

function isTenKEasyPlan(distance, intensity) {
  return (
    normalizeRaceDistance(distance) === "10K" && isEasyIntensity(intensity)
  );
}

function isTenKModeratePlan(distance, intensity) {
  return (
    normalizeRaceDistance(distance) === "10K" &&
    normalizeIntensityLabel(intensity) === "Moderate"
  );
}

/** Max Tuesday easy session (mi) on 10K Moderate quality/build weeks (redistribute surplus to other easies). */
const TEN_K_MODERATE_MAX_TUESDAY_EASY_MI = 4;

/**
 * If Tuesday easy exceeds the Moderate cap, clamp and move miles onto other easy days (Thu → Wed → Mon → Fri).
 * May place leftover surplus on the first easy day found even above `easySessionHardCap` to preserve week total.
 */
function applyTenKModerateTuesdayEasyCapFromSchedule(
  sched,
  easySessionHardCap,
  unitPreference,
  distance,
  intensity
) {
  if (!isTenKModeratePlan(distance, intensity) || !sched) return;
  const tue = parseEasyRunMilesFromWorkoutLine(sched.Tuesday);
  if (tue == null || tue <= TEN_K_MODERATE_MAX_TUESDAY_EASY_MI) return;
  let surplus = roundWhole(tue - TEN_K_MODERATE_MAX_TUESDAY_EASY_MI);
  const cap = Math.floor(Number(easySessionHardCap));
  const softCap = Number.isFinite(cap) && cap > 0 ? cap : 6;
  sched.Tuesday = `Easy Run (${formatDist(
    TEN_K_MODERATE_MAX_TUESDAY_EASY_MI,
    unitPreference
  )})`;
  const order = ["Thursday", "Wednesday", "Monday", "Friday"];
  for (const day of order) {
    if (surplus <= 0) break;
    const cur = parseEasyRunMilesFromWorkoutLine(sched[day]);
    if (cur == null) continue;
    const c = roundWhole(cur);
    const room = Math.max(0, softCap - c);
    if (room <= 0) continue;
    const add = Math.min(surplus, room);
    sched[day] = `Easy Run (${formatDist(c + add, unitPreference)})`;
    surplus -= add;
  }
  if (surplus > 0) {
    for (const day of order) {
      const cur = parseEasyRunMilesFromWorkoutLine(sched[day]);
      if (cur == null) continue;
      const c = roundWhole(cur);
      sched[day] = `Easy Run (${formatDist(c + surplus, unitPreference)})`;
      break;
    }
  }
}

function isHalfMarathonEasyPlan(distance, intensity) {
  return (
    normalizeRaceDistance(distance) === "Half Marathon" &&
    isEasyIntensity(intensity)
  );
}

function isTenMilerEasyPlan(distance, intensity) {
  return (
    normalizeRaceDistance(distance) === "10 Miler" &&
    isEasyIntensity(intensity)
  );
}

function easyRunLineForTaperOrDefault(
  dayMi,
  unitPreference,
  distance,
  intensity
) {
  if (dayMi <= 0) return "Rest";
  return isFiveKEasyPlan(distance, intensity) ||
    isTenKEasyPlan(distance, intensity) ||
    isHalfMarathonEasyPlan(distance, intensity) ||
    isTenMilerEasyPlan(distance, intensity)
    ? formatFiveKEasyEasyRunLabel(dayMi, unitPreference)
    : `Easy Run (${formatDist(dayMi, unitPreference)})`;
}

/**
 * Long-run miles (internal) from any day’s “Long Run (…)” line.
 * Scans all weekdays so we never miss LR if the schedule layout changes.
 */
function parseLongRunMilesFromAnyDay(schedule) {
  if (!schedule) return null;
  for (const day of WEEK_ORDER) {
    const v = schedule[day];
    if (typeof v !== "string") continue;
    if (!/long run|saturday run/i.test(v)) continue;
    const mm = v.match(/([\d.]+)\s*(mi|mile|miles|km|k)\b/i);
    if (!mm) continue;
    let num = parseFloat(mm[1]);
    const u = mm[2].toLowerCase();
    if (u === "km" || u === "k") {
      num /= MI_TO_KM;
    }
    if (Number.isFinite(num) && num > 0) return num;
  }
  return null;
}

/**
 * Tempo-run miles from schedule (first “Tempo Run (…)” line found).
 */
function parseTempoRunMilesFromAnyDay(schedule) {
  if (!schedule) return null;
  for (const day of WEEK_ORDER) {
    const v = schedule[day];
    if (typeof v !== "string") continue;
    if (!/tempo run/i.test(v)) continue;
    const mm = v.match(/([\d.]+)\s*(mi|mile|miles|km|k)\b/i);
    if (!mm) continue;
    let num = parseFloat(mm[1]);
    const u = mm[2].toLowerCase();
    if (u === "km" || u === "k") {
      num /= MI_TO_KM;
    }
    if (Number.isFinite(num) && num > 0) return num;
  }
  return null;
}

function parseEasyRunMilesFromWorkoutLine(line) {
  if (typeof line !== "string") return null;
  if (!/easy run/i.test(line)) return null;
  const mm = line.match(/([\d.]+)\s*(mi|mile|miles|km|k)\b/i);
  if (!mm) return null;
  let num = parseFloat(mm[1]);
  const u = mm[2].toLowerCase();
  if (u === "km" || u === "k") {
    num /= MI_TO_KM;
  }
  if (Number.isFinite(num) && num > 0) return num;
  return null;
}

/**
 * Short 5K Challenging/Intense (week ≥ 2, build or cutback): raise easy runs still &lt; 2 mi without
 * changing weekly total — first from Saturday LR (&gt;2 mi), then (cutback only) from Tuesday easy or
 * Wednesday tempo so Monday/Friday can reach 2 mi when LR is already at the floor.
 */
function rebalanceShortFiveKCiEasyRunFloorsFromSaturday(
  sched,
  distance,
  intensity,
  trainingWeekCount,
  weekNum,
  unitPreference,
  cutbackEasySessionCapMi = null
) {
  if (
    normalizeRaceDistance(distance) !== "5K" ||
    isEasyIntensity(intensity) ||
    !usesFiveKChallengingOrIntenseQualityTempoFloor(distance, intensity) ||
    trainingWeekCount == null ||
    trainingWeekCount >
      FIVE_K_NON_EASY_ONBOARDING_WOW_PARSED_ONLY_MAX_TRAINING_WEEKS ||
    weekNum == null ||
    roundWhole(weekNum) < 2
  ) {
    return;
  }

  const bumpEasyDay = (dayName, addMi) => {
    const cur = parseEasyRunMilesFromWorkoutLine(sched[dayName]);
    const curW = cur == null ? 0 : roundWhole(cur);
    const lrForCap = parseLongRunMilesFromAnyDay(sched);
    const lrCap = lrForCap == null ? 0 : roundWhole(lrForCap);
    const easyCap = maxEasyRunMiGivenLongRun(
      lrCap,
      distance,
      intensity,
      cutbackEasySessionCapMi
    );
    const bumped = Math.min(
      curW + addMi,
      Math.floor(Number(easyCap) || 2)
    );
    if (bumped <= curW) return false;
    sched[dayName] = `Easy Run (${formatDist(bumped, unitPreference)})`;
    return true;
  };

  for (let guard = 0; guard < 16; guard += 1) {
    const lrRaw = parseLongRunMilesFromAnyDay(sched);
    const lrW = lrRaw == null ? 0 : roundWhole(lrRaw);
    if (lrW <= 2) break;

    const needyDay = QUALITY_EASY_DAY_MI_FLOOR_PRIORITY.find((dayName) => {
      const e = parseEasyRunMilesFromWorkoutLine(sched[dayName]);
      return e != null && roundWhole(e) < 2;
    });
    if (!needyDay) break;

    const newLr = lrW - 1;
    sched.Saturday = formatPrimarySaturdayRunLine(
      distance,
      newLr,
      unitPreference,
      intensity
    );
    if (!bumpEasyDay(needyDay, 1)) break;
  }

  if (cutbackEasySessionCapMi == null) return;

  for (let guard = 0; guard < 24; guard += 1) {
    const monFriNeedy = ["Monday", "Friday"].find((dayName) => {
      const e = parseEasyRunMilesFromWorkoutLine(sched[dayName]);
      return e != null && roundWhole(e) < 2;
    });
    if (!monFriNeedy) break;

    const lrRaw2 = parseLongRunMilesFromAnyDay(sched);
    const lrW2 = lrRaw2 == null ? 0 : roundWhole(lrRaw2);
    if (lrW2 > 2) {
      sched.Saturday = formatPrimarySaturdayRunLine(
        distance,
        lrW2 - 1,
        unitPreference,
        intensity
      );
      if (bumpEasyDay(monFriNeedy, 1)) continue;
      sched.Saturday = formatPrimarySaturdayRunLine(
        distance,
        lrW2,
        unitPreference,
        intensity
      );
    }

    const tueE = parseEasyRunMilesFromWorkoutLine(sched.Tuesday);
    const tueW = tueE == null ? 0 : roundWhole(tueE);
    if (tueW > 2) {
      sched.Tuesday = `Easy Run (${formatDist(tueW - 1, unitPreference)})`;
      if (bumpEasyDay(monFriNeedy, 1)) continue;
      sched.Tuesday = `Easy Run (${formatDist(tueW, unitPreference)})`;
    }

    const wedLine = sched.Wednesday;
    if (typeof wedLine === "string" && /tempo run/i.test(wedLine)) {
      const mm = wedLine.match(/([\d.]+)\s*(mi|mile|miles|km|k)\b/i);
      if (mm) {
        let tw = parseFloat(mm[1]);
        const u = mm[2].toLowerCase();
        if (u === "km" || u === "k") tw /= MI_TO_KM;
        const twR = roundWhole(tw);
        /** 3→2 mi when Sat/Tue cannot fund Mon/Fri ≥2 (cutback totals tight vs table min tempo). */
        if (twR > 2) {
          sched.Wednesday = `Tempo Run (${formatDist(twR - 1, unitPreference)})`;
          if (bumpEasyDay(monFriNeedy, 1)) continue;
          sched.Wednesday = `Tempo Run (${formatDist(twR, unitPreference)})`;
        }
      }
    }
    break;
  }
}

/** Mean miles of “Easy Run (…)” lines (internal mi, matches schedule strings). */
function averageEasyRunMilesFromSchedule(schedule) {
  if (!schedule) return null;
  const vals = [];
  for (const day of WEEK_ORDER) {
    const v = parseEasyRunMilesFromWorkoutLine(schedule[day]);
    if (v != null) vals.push(v);
  }
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/** Per-easy cap for non-Easy cutback weeks from prior week’s average easy run; falls back to distance floor when prior has no easy lines (e.g. Walk-only weekdays). */
function cutbackEasySessionCapFromPriorWeekSchedule(schedule, distance = "") {
  const avg = averageEasyRunMilesFromSchedule(schedule);
  const floorMi =
    normalizeRaceDistance(distance) === "10K" ||
    normalizeRaceDistance(distance) === "5K"
      ? Math.max(CUTBACK_EASY_RUN_MIN_MI, 3)
      : CUTBACK_EASY_RUN_MIN_MI;
  if (avg == null || !Number.isFinite(avg) || avg <= 0) return floorMi;
  return Math.max(
    floorMi,
    roundWhole(avg * CUTBACK_EASY_RUN_FRAC_OF_PRIOR_BUILD_AVG)
  );
}

/**
 * Most recent non-cutback training week strictly before `currentIndex` (0-based): parsed tempo mi, or null.
 */
function priorNonCutbackBuildWeekTempoMiFromPlan(
  plan,
  currentIndex,
  trainingWeekCount
) {
  const T = trainingWeekCount;
  if (!plan || currentIndex < 1 || T < 1) return null;
  for (let j = currentIndex - 1; j >= 0; j--) {
    const wn = j + 1;
    if (isCutbackWeek(wn, T)) continue;
    const mi = parseTempoRunMilesFromAnyDay(plan[j]?.schedule);
    if (mi != null && Number.isFinite(mi) && mi > 0) return mi;
  }
  return null;
}

function cutbackSingleTempoCapMiFromPriorBuild(priorBuildTempoMi) {
  const p = Number(priorBuildTempoMi);
  if (!Number.isFinite(p) || p <= 0) return null;
  const cap = roundWhole(p * CUTBACK_SINGLE_TEMPO_FRAC_OF_PRIOR_BUILD);
  return Math.min(roundWhole(p), Math.max(1, cap));
}

/** Latest long-run mi on a non-cutback week strictly before `currentIndex` (for canonical LR monotonicity). */
function priorNonCutbackLongRunMiFromPlan(plan, currentIndex, trainingWeekCount) {
  const T = trainingWeekCount;
  if (!plan || currentIndex < 1 || T < 1) return null;
  for (let j = currentIndex - 1; j >= 0; j--) {
    const wn = j + 1;
    if (isCutbackWeek(wn, T)) continue;
    const mi = parseLongRunMilesFromAnyDay(plan[j]?.schedule);
    if (mi != null && Number.isFinite(mi) && mi > 0) return mi;
  }
  return null;
}

function capEasyRunSessionMiles(dayMiMiles, maxMi) {
  const cap = Number(maxMi);
  if (!Number.isFinite(cap) || cap <= 0) return 0;
  const n = Number(dayMiMiles);
  if (Number.isNaN(n)) return 0;
  return Math.min(n, cap);
}

/** Max miles for one easy run by training intensity (taper + quality plans). 5K: 3 mi cap at any intensity. */
const MAX_SINGLE_EASY_RUN_MI_BY_INTENSITY = Object.freeze({
  Easy: 6,
  Moderate: 8,
  Challenging: 10,
  Intense: 12,
});

function maxSingleEasyRunMiForEasyPlan(distance, intensity) {
  const inten = normalizeIntensityLabel(intensity);
  const cap =
    MAX_SINGLE_EASY_RUN_MI_BY_INTENSITY[inten] ??
    MAX_SINGLE_EASY_RUN_MI_BY_INTENSITY.Moderate;
  const d = normalizeRaceDistance(distance);
  if (d === "5K") {
    return Math.min(cap, 3);
  }
  if (d === "Marathon" && inten === "Moderate") {
    return Math.min(cap, MARATHON_MODERATE_MAX_EASY_RUN_MI);
  }
  if (isMarathonIntensePlan(distance, intensity)) {
    return Math.min(cap, MARATHON_INTENSE_MAX_EASY_RUN_MI);
  }
  return cap;
}

/** Minimum long run (mi) for Easy-intensity plans so easy caps stay > 0 and LR stays primary. */
const MIN_LONG_RUN_MI_EASY_INTENSITY = 2;

/** Easy plans: weekly volume bumps so each easy day can hold at least this many miles (with LR ramp). */
const MIN_EASY_DAY_MI_EASY_INTENSITY = 2;

/** Per-run easy floor for Easy-intensity plans and week-1 onboarding (easy runs only; not walks). */
function easyRunFloorMinMiForDistance(distance) {
  const d = normalizeRaceDistance(distance);
  if (d === "10K") return 2;
  if (d === "10 Miler" || d === "Half Marathon" || d === "Marathon") return 3;
  return 0;
}

/** Bump weekly budget when LR + walk + easy floors exceed planned volume.
 *  Adds a small buffer so easy runs can vary instead of all pinning to the floor. */
function bumpWeeklyMiForEasyRunFloors(wk, distance, { easyDayCount, walkMi = 0, longRunMi }) {
  const floor = easyRunFloorMinMiForDistance(distance);
  if (floor <= 0 || easyDayCount <= 0) return wk;
  const lr = Number(longRunMi) || 0;
  const minWk = lr + walkMi + easyDayCount * floor;
  if (wk >= minWk) return wk; // already enough budget; distributor varies naturally
  // Genuinely short (early weeks): cover the floors plus headroom for variation.
  const variationBuffer = Math.max(1, Math.ceil(easyDayCount / 2));
  return minWk + variationBuffer;
}

/**
 * Easy plans: each easy run is at least 1 mi (distribute cap) and at most min(table, max(1, LR−1))
 * so the long run stays strictly longer whenever LR ≥ 3.
 * 5K: peak LR can equal the 3 mi easy cap — LR−1 would cap easy at 2 forever (7 mi plateau) and
 * under LR=2 would allow only 1 mi/run (4 mi weeks). Use min(base, L) so volume can progress.
 * @param cutbackEasySessionCapMi — optional; non-Easy plans: min with this whole-mi cap (cutback weeks from prior avg easy × 0.8).
 */
function maxEasyRunMiGivenLongRun(
  lrWhole,
  distance,
  intensity,
  cutbackEasySessionCapMi = null
) {
  let base = maxSingleEasyRunMiForEasyPlan(distance, intensity);
  if (
    !isEasyIntensity(intensity) &&
    cutbackEasySessionCapMi != null &&
    Number.isFinite(cutbackEasySessionCapMi) &&
    cutbackEasySessionCapMi > 0
  ) {
    base = Math.min(base, roundWhole(cutbackEasySessionCapMi));
  }
  if (!isEasyIntensity(intensity)) return base;
  const L = Number(lrWhole);
  if (!Number.isFinite(L) || L <= 0) return Math.min(1, base);
  let perRun;
  if (normalizeRaceDistance(distance) === "5K") {
    perRun = Math.max(1, Math.min(base, L));
  } else {
    const headroom = Math.max(1, L - 1);
    perRun = Math.max(1, Math.min(base, headroom));
  }
  const raceCap = getEasyIntensityRaceDistanceRunCapMi(distance);
  if (raceCap != null) {
    perRun = Math.min(perRun, raceCap);
  }
  return perRun;
}

/** Clamp Easy-plan long run: ≥2 mi when week can fit LR + 1 mi per easy slot; else best effort within weeklyMi. */
function clampEasyIntensityLongRunWhole(lrRounded, weeklyMi, runSlots) {
  const w = roundWhole(weeklyMi);
  let lr = roundWhole(lrRounded);
  if (runSlots <= 0 || w <= 0) return Math.min(Math.max(0, lr), w);
  const maxLrReserve = w > runSlots ? w - runSlots : w;
  if (w >= MIN_LONG_RUN_MI_EASY_INTENSITY + runSlots) {
    lr = Math.max(
      MIN_LONG_RUN_MI_EASY_INTENSITY,
      Math.min(lr, maxLrReserve, w)
    );
  } else {
    lr = Math.min(Math.max(1, lr), w);
  }
  return lr;
}

/**
 * Easy intensity: integer long-run ramp week 1 → week T (starts at floorLr, ends at cap).
 * Uses ceil on cumulative progress so small (cap−2)/(T−1) slopes still advance most weeks.
 */
function easyIntensityRampLongRunMi(weekNum, trainingWeekCount, peakCapMi) {
  const cap = Math.max(2, Math.min(Number(peakCapMi) || 3, 50));
  const floorLr = 2;
  const T = trainingWeekCount;
  const w = Number(weekNum);
  if (T <= 1 || !Number.isFinite(w) || w < 1) return cap;
  if (cap <= floorLr) return cap;
  const gain = cap - floorLr;
  const steps = Math.max(1, T - 1);
  const inc = Math.ceil(((w - 1) * gain) / steps);
  return Math.min(cap, floorLr + inc);
}

/**
 * Marathon training weeks only: long-run targets for a 14-week build (cutbacks embedded).
 * Weeks 1–4: 6,8,10,8 — 5–8: 11,13,15,11 — 9–12: 14,16,18,13 — 13–14: 17,20.
 * For other training lengths, linearly interpolate along this polyline in week-index space.
 */
const MARATHON_CANONICAL_LONG_RUN_MI = Object.freeze([
  6, 8, 10, 8, 11, 13, 15, 11, 14, 16, 18, 13, 17, 20,
]);

/**
 * Half marathon: same cutback rhythm as marathon, scaled so the polyline peaks at 15 mi (Intense).
 * Capped per intensity in build via peakLrCapMi (Easy 11 … Intense 15).
 */
const HALF_MARATHON_CANONICAL_LONG_RUN_MI = Object.freeze([
  5, 6, 8, 6, 8, 10, 11, 8, 11, 12, 14, 10, 13, 15,
]);

/**
 * 10 Miler: same cutback rhythm as marathon; polyline peaks at 12 mi (Intense).
 * Scaled from marathon key × (12/20). Capped per intensity via peakLrCapMi (Easy 8 … Intense 12).
 */
const TEN_MILER_CANONICAL_LONG_RUN_MI = Object.freeze([
  4, 5, 6, 5, 7, 8, 9, 7, 8, 10, 11, 8, 10, 12,
]);

/**
 * 10K: same cutback rhythm as marathon; polyline peaks at 8 mi (Challenging / Intense).
 * Build segments are strictly non-decreasing (no spurious w1→w2 drops from interpolation);
 * dips align with cutback weeks (every 4th week before taper).
 */
const TEN_K_CANONICAL_LONG_RUN_MI = Object.freeze([
  3, 4, 5, 4, 5, 6, 7, 5, 6, 7, 8, 6, 7, 8,
]);

/**
 * 5K: same cutback rhythm as marathon; polyline peaks at 6 mi (Intense).
 * Smoothed vs raw × (6/20) to limit week-to-week LR jumps (e.g. 2→5) that starve quality days.
 */
const FIVE_K_CANONICAL_LONG_RUN_MI = Object.freeze([
  2, 2, 2, 3, 3, 3, 4, 3, 4, 4, 5, 4, 5, 6,
]);

function canonicalScheduledLongRunMi(key, weekNum, trainingWeekCount) {
  const n = key.length;
  const T = Math.max(1, roundWhole(Number(trainingWeekCount) || 1));
  const w = Math.max(1, Math.min(roundWhole(Number(weekNum) || 1), T));
  if (T <= 1) return key[n - 1];
  const u = (w - 1) / (T - 1);
  const x = u * (n - 1);
  const lo = Math.min(Math.floor(x), n - 2);
  const hi = lo + 1;
  const f = x - lo;
  const raw = key[lo] * (1 - f) + key[hi] * f;
  return Math.max(2, roundWhole(raw));
}

function marathonScheduledLongRunMi(weekNum, trainingWeekCount) {
  return canonicalScheduledLongRunMi(
    MARATHON_CANONICAL_LONG_RUN_MI,
    weekNum,
    trainingWeekCount
  );
}

function halfMarathonScheduledLongRunMi(weekNum, trainingWeekCount) {
  return canonicalScheduledLongRunMi(
    HALF_MARATHON_CANONICAL_LONG_RUN_MI,
    weekNum,
    trainingWeekCount
  );
}

function tenMilerScheduledLongRunMi(weekNum, trainingWeekCount) {
  return canonicalScheduledLongRunMi(
    TEN_MILER_CANONICAL_LONG_RUN_MI,
    weekNum,
    trainingWeekCount
  );
}

function tenKScheduledLongRunMi(weekNum, trainingWeekCount) {
  return canonicalScheduledLongRunMi(
    TEN_K_CANONICAL_LONG_RUN_MI,
    weekNum,
    trainingWeekCount
  );
}

function fiveKScheduledLongRunMi(weekNum, trainingWeekCount) {
  return canonicalScheduledLongRunMi(
    FIVE_K_CANONICAL_LONG_RUN_MI,
    weekNum,
    trainingWeekCount
  );
}

const _usesCanonicalLongRunScheduleCache = new Map();
function usesCanonicalLongRunSchedule(distance) {
  const d = normalizeRaceDistance(distance);
  const hit = _usesCanonicalLongRunScheduleCache.get(d);
  if (hit !== undefined) return hit;
  const v =
    d === "Marathon" ||
    d === "Half Marathon" ||
    d === "10 Miler" ||
    d === "10K" ||
    d === "5K";
  _usesCanonicalLongRunScheduleCache.set(d, v);
  return v;
}

function maxLongRunMiAfterPriorCutbackEasy(prevLrMiles, distance = "") {
  const p = roundWhole(prevLrMiles);
  if (!Number.isFinite(p) || p <= 0) return null;
  const bump =
    normalizeRaceDistance(distance) === "Marathon"
      ? MARATHON_EASY_LR_INCREASE_AFTER_PRIOR_CUTBACK
      : MAX_LONG_RUN_INCREASE_AFTER_CUTBACK_EASY;
  return Math.floor(p * (1 + bump) + 1e-9);
}

/**
 * After a balanced split, nudge miles so easy days differ (sum unchanged, each in [minPerDay, cap]).
 * Targets max−min spread ≥ 2 when possible; else improves spread vs all-equal.
 */
function applyEasyDayMileVariation(milesPerDay, hardCap, minPerDay = 2) {
  const a = milesPerDay.map((x) => roundWhole(x));
  const cap = Math.floor(Number(hardCap));
  if (!Number.isFinite(cap) || cap <= 0) return a;
  const posIdx = a.map((v, i) => i).filter((i) => a[i] > 0);
  if (posIdx.length < 2) return a;

  const effMin =
    posIdx.length && Math.min(...posIdx.map((i) => a[i])) >= 2 ? minPerDay : 1;

  const sub = posIdx.map((i) => a[i]);
  const n = sub.length;
  let s = [...sub];

  for (let iter = 0; iter < n * n + 8; iter++) {
    const mx = Math.max(...s);
    const mn = Math.min(...s);
    if (mx - mn >= 2) break;

    let bestTrial = null;
    let bestSpread = mx - mn;
    for (let k = 0; k < n; k++) {
      for (let j = 0; j < n; j++) {
        if (k === j) continue;
        if (s[k] >= cap || s[j] <= effMin) continue;
        const trial = [...s];
        trial[k] += 1;
        trial[j] -= 1;
        if (trial.some((v) => v < effMin || v > cap)) continue;
        const tmx = Math.max(...trial);
        const tmn = Math.min(...trial);
        const sp = tmx - tmn;
        if (sp > bestSpread) {
          bestSpread = sp;
          bestTrial = trial;
        }
      }
    }
    if (!bestTrial) break;
    s = bestTrial;
    if (bestSpread >= 2) break;
  }

  posIdx.forEach((i, j) => {
    a[i] = s[j];
  });
  return a;
}

/**
 * After mile-based variation, nudge easy-day miles so rounded km differ (taper often has 2×6 mi → 2×10 km).
 * Allows up to `cap + kmSlackMi` on a day only when needed to break km ties; sum of miles unchanged.
 */
function refineEasyDaysForKmDisplay(milesArr, hardCap) {
  const cap = Math.floor(Number(hardCap));
  if (!Number.isFinite(cap) || cap <= 0) return milesArr;
  const kmSlackMi = 1;
  const softCap = cap + kmSlackMi;
  const a = milesArr.map((x) => roundWhole(x));
  const posIdx = a.map((v, i) => i).filter((i) => a[i] > 0);
  if (posIdx.length < 2) return a;

  const effMin =
    posIdx.length && Math.min(...posIdx.map((i) => a[i])) >= 2 ? 2 : 1;

  const kmSpread = () => {
    const kms = posIdx.map((i) => roundWhole(a[i] * MI_TO_KM));
    return Math.max(...kms) - Math.min(...kms);
  };

  const kmSpreadOfTrial = (trial) => {
    if (!trial) return -1;
    const tk = posIdx.map((i) => roundWhole(trial[i] * MI_TO_KM));
    return Math.max(...tk) - Math.min(...tk);
  };

  const bestTransferForDelta = (delta) => {
    let bestTrial = null;
    let bestSp = -1;
    for (let ii = 0; ii < posIdx.length; ii += 1) {
      for (let jj = 0; jj < posIdx.length; jj += 1) {
        if (ii === jj) continue;
        const ik = posIdx[ii];
        const ij = posIdx[jj];
        if (a[ik] + delta > softCap || a[ij] - delta < effMin) continue;
        const trial = [...a];
        trial[ik] += delta;
        trial[ij] -= delta;
        if (trial[ik] > softCap || trial[ij] < effMin) continue;
        const sp = kmSpreadOfTrial(trial);
        if (sp > bestSp) {
          bestSp = sp;
          bestTrial = trial;
        }
      }
    }
    return bestTrial;
  };

  for (let guard = 0; guard < 200 && kmSpread() < 3; guard += 1) {
    const cur = kmSpread();
    const t1 = bestTransferForDelta(1);
    const t2 = bestTransferForDelta(2);
    const s1 = kmSpreadOfTrial(t1);
    const s2 = kmSpreadOfTrial(t2);
    const pick =
      s2 > cur && s2 >= s1 ? t2 : s1 > cur ? t1 : null;
    if (!pick) break;
    pick.forEach((v, i) => {
      a[i] = v;
    });
  }
  return a;
}

/**
 * Spread whole miles across days; each day never exceeds hardCap.
 * Never pushes past cap by "balancing" — budget is min(totalEasy, n × hardCap).
 * @param tieRoundRobin — when true, rotate among tied minimum days (more even than always picking the first index).
 * @param unitPreference — when Kilometers, extra nudge so displayed km are not all identical after rounding.
 */
/**
 * Non-Easy quality/taper: Thursday first, then Tuesday, then other listed weekdays — each receives
 * up to 2 mi before the rest of the easy budget is spread (avoids Tue=0 while Thu holds miles).
 */
const QUALITY_EASY_DAY_MI_FLOOR_PRIORITY = [
  "Thursday",
  "Tuesday",
  "Wednesday",
  "Monday",
  "Friday",
];

/**
 * 10 Miler / 10K / 5K Moderate build weeks: floor 2 mi/slot, round-robin extras to `hardCap`, then map longer
 * runs onto earlier weekday-priority days (Thursday-first) so weeks gain 2/3/4 mi texture vs flat 2s.
 */
function distributeEasyMilesTenMilerModerateBuildWeek(
  dayNames,
  totalEasy,
  hardCap,
  weekNum,
  unitPreference = "Miles"
) {
  const n = dayNames?.length ?? 0;
  if (n === 0 || totalEasy <= 0) return Array(Math.max(0, n)).fill(0);
  const cap = Math.floor(Number(hardCap));
  if (!Number.isFinite(cap) || cap < 2) {
    return distributeEasyMilesAcrossDaysRespectingThursdayFloor(
      totalEasy,
      dayNames,
      hardCap,
      true,
      unitPreference,
      true
    );
  }
  const lo = 2;
  const minSum = n * lo;
  const maxSum = n * cap;
  const tgt = Math.min(maxSum, roundWhole(totalEasy));
  if (tgt < minSum) {
    return distributeEasyMilesAcrossDaysRespectingThursdayFloor(
      totalEasy,
      dayNames,
      hardCap,
      true,
      unitPreference,
      true
    );
  }
  const arr = Array(n).fill(lo);
  let left = tgt - minSum;
  const wn = weekNum != null ? roundWhole(weekNum) : 1;
  let step = n > 0 ? Math.max(0, wn - 1) % n : 0;
  let guard = 0;
  while (left > 0 && guard < n * cap * n + 400) {
    guard += 1;
    const i = step % n;
    if (arr[i] < cap) {
      arr[i] += 1;
      left -= 1;
    }
    step += 1;
  }
  const pri = QUALITY_EASY_DAY_MI_FLOOR_PRIORITY;
  const idxByPri = dayNames
    .map((d, i) => ({ d, i }))
    .sort(
      (a, b) =>
        (pri.indexOf(a.d) === -1 ? 99 : pri.indexOf(a.d)) -
        (pri.indexOf(b.d) === -1 ? 99 : pri.indexOf(b.d))
    );
  const sortedVals = [...arr].sort((a, b) => b - a);
  const out = [...arr];
  idxByPri.forEach(({ i }, k) => {
    out[i] = sortedVals[k] ?? lo;
  });
  if (unitPreference === "Kilometers" && n >= 2) {
    return refineEasyDaysForKmDisplay(out, cap);
  }
  return out;
}

function distributeEasyMilesAcrossDaysRespectingThursdayFloor(
  totalEasy,
  dayNames,
  hardCap,
  tieRoundRobin,
  unitPreference,
  enforceThursdayMin2
) {
  if (
    !enforceThursdayMin2 ||
    dayNames == null ||
    dayNames.length === 0 ||
    totalEasy <= 0
  ) {
    return distributeEasyMilesAcrossDays(
      totalEasy,
      dayNames,
      hardCap,
      tieRoundRobin,
      unitPreference
    );
  }
  const cap = Math.floor(Number(hardCap));
  if (!Number.isFinite(cap) || cap <= 0) {
    return distributeEasyMilesAcrossDays(
      totalEasy,
      dayNames,
      hardCap,
      tieRoundRobin,
      unitPreference
    );
  }
  const n = dayNames.length;
  const maxTotal = n * cap;
  let pool = Math.min(roundWhole(totalEasy), maxTotal);
  const arr = Array(n).fill(0);
  for (const d of QUALITY_EASY_DAY_MI_FLOOR_PRIORITY) {
    const idx = dayNames.indexOf(d);
    if (idx < 0) continue;
    const take = Math.min(2, cap, pool);
    arr[idx] += take;
    pool -= take;
  }
  if (pool > 0) {
    let left = pool;
    let guard = 0;
    let rr = 0;
    while (left > 0 && guard < 20000) {
      guard += 1;
      const under = [];
      for (let i = 0; i < n; i += 1) {
        if (arr[i] < cap) under.push(i);
      }
      if (under.length === 0) break;
      let idx;
      if (tieRoundRobin) {
        const minV = Math.min(...under.map((i) => arr[i]));
        const cand = under.filter((i) => arr[i] === minV);
        idx = cand[rr % cand.length];
        rr += 1;
      } else {
        idx = under.reduce((a, b) => (arr[a] <= arr[b] ? a : b));
      }
      arr[idx] += 1;
      left -= 1;
    }
  }

  const pos = arr.filter((x) => x > 0);
  if (pos.length < 2) return arr;
  const varied = applyEasyDayMileVariation(arr, cap, 2);
  return unitPreference === "Kilometers"
    ? refineEasyDaysForKmDisplay(varied, cap)
    : varied;
}

function distributeEasyMilesAcrossDays(
  totalEasy,
  dayNames,
  hardCap,
  tieRoundRobin = false,
  unitPreference = "Miles"
) {
  const n = dayNames.length;
  if (n === 0 || totalEasy <= 0) return Array(Math.max(0, n)).fill(0);
  const rawCap = Math.floor(Number(hardCap));
  if (!Number.isFinite(rawCap) || rawCap <= 0) return Array(n).fill(0);
  const cap = rawCap;
  const maxTotal = n * cap;
  const budget = Math.min(totalEasy, maxTotal);
  const arr = Array(n).fill(0);
  let left = budget;
  let guard = 0;
  let rr = 0;
  while (left > 0 && guard < 20000) {
    guard += 1;
    const under = [];
    for (let i = 0; i < n; i += 1) {
      if (arr[i] < cap) under.push(i);
    }
    if (under.length === 0) break;
    let idx;
    if (tieRoundRobin) {
      const minV = Math.min(...under.map((i) => arr[i]));
      const cand = under.filter((i) => arr[i] === minV);
      idx = cand[rr % cand.length];
      rr += 1;
    } else {
      idx = under.reduce((a, b) => (arr[a] <= arr[b] ? a : b));
    }
    arr[idx] += 1;
    left -= 1;
  }

  const pos = arr.filter((x) => x > 0);
  if (pos.length < 2) return arr;
  const varied = applyEasyDayMileVariation(arr, cap, 2);
  return unitPreference === "Kilometers"
    ? refineEasyDaysForKmDisplay(varied, cap)
    : varied;
}

/**
 * Non-Easy onboarding (week 1): weekday easy miles sum to `totalEasy` with the **first listed day**
 * (typically Monday) carrying extra vs the rest so, together with Saturday LR, the week reads as
 * Sat longest → Mon second → midweek shorter — not flat equal miles per weekday.
 */
function distributeNonEasyOnboardingWeekdayEasyMiles(
  totalEasy,
  dayNames,
  hardCap,
  unitPreference = "Miles",
  minPerDay = 0
) {
  const n = dayNames?.length ?? 0;
  const tgt = roundWhole(Number(totalEasy));
  if (n === 0 || tgt <= 0) return Array(Math.max(0, n)).fill(0);
  const rawCap = Math.floor(Number(hardCap));
  if (!Number.isFinite(rawCap) || rawCap <= 0) {
    return distributeEasyMilesAcrossDays(
      totalEasy,
      dayNames,
      Math.max(1, rawCap || 1),
      true,
      unitPreference
    );
  }
  const cap = Math.max(1, rawCap);

  if (n === 1) {
    return [Math.min(tgt, cap)];
  }
  if (tgt < n) {
    return distributeEasyMilesAcrossDays(
      totalEasy,
      dayNames,
      cap,
      true,
      unitPreference
    );
  }

  const weights = dayNames.map((_, i) => (i === 0 ? 2 : 1));
  const wsum = weights.reduce((a, b) => a + b, 0);
  const scaled = weights.map((w) => (tgt * w) / wsum);
  let floorParts = scaled.map((x) => Math.floor(x));
  const sumFloors = floorParts.reduce((a, b) => a + b, 0);
  let rem = tgt - sumFloors;
  const fracs = scaled.map((x, i) => ({
    i,
    f: x - floorParts[i],
  }));
  fracs.sort((a, b) => b.f - a.f || a.i - b.i);
  for (let k = 0; k < rem; k += 1) {
    floorParts[fracs[k].i] += 1;
  }

  let out = floorParts.map((p) => Math.min(p, cap));
  let s = out.reduce((a, b) => a + b, 0);
  let need = tgt - s;
  /** After capping: add miles Mon first, then Wed→Fri, then Tue last so Tuesday stays shorter vs Monday. */
  const prio = [0];
  for (let i = 2; i < n; i += 1) prio.push(i);
  if (n > 1) prio.push(1);
  for (let guard = 0; guard < 20000 && need > 0; guard += 1) {
    let placed = false;
    for (const i of prio) {
      if (need <= 0) break;
      if (out[i] < cap) {
        out[i] += 1;
        need -= 1;
        placed = true;
      }
    }
    if (!placed) break;
  }

  const floor = Number(minPerDay) || 0;
  if (floor > 0) {
    out = ensureListedEasyDayMinimumsWithRedistribution(out, dayNames, floor);
  }

  if (unitPreference === "Kilometers" && n >= 2) {
    return refineEasyDaysForKmDisplay(out, cap);
  }
  return out;
}

/** 5K Easy weekday cap: keep Sat run longest (up to 3 mi); weekdays use 1–2.5 mi steps. */
function fiveKEasyWeekdayCapMi(lrWhole) {
  const L = roundWhole(lrWhole);
  return Math.min(2.5, Math.max(1, L - 0.5));
}

/** When weekly easy budget needs more than n × 2.5, allow up to per-run easy cap (still ≤ LR). */
function fiveKEasyWeekdayCapForTotal(
  lrWhole,
  totalEasy,
  nDays,
  distance,
  intensity
) {
  const soft = fiveKEasyWeekdayCapMi(lrWhole);
  const hard = maxEasyRunMiGivenLongRun(lrWhole, distance, intensity, null);
  if (nDays <= 0) return soft;
  const needAvg = totalEasy / nDays;
  if (needAvg <= soft + 1e-9) return soft;
  const bumped = Math.ceil(needAvg * 2) / 2;
  return Math.min(hard, Math.max(soft, bumped));
}

function spreadScoreFiveKEasyMiles(arr) {
  const halves = arr.map((x) => Math.round(x * 2));
  const set = new Set(halves);
  let score = set.size * 100;
  const mx = Math.max(...arr);
  const mn = Math.min(...arr);
  score += (mx - mn) * 20;
  return score;
}

function usesFiveKNonEasyFractionalEasyMi(distance, intensity) {
  return (
    normalizeRaceDistance(distance) === "5K" && !isEasyIntensity(intensity)
  );
}

/** 5K M/C/I build weeks: half-mile steps {1.5, 2, 2.5, …} with 1.5 mi floor. */
function distributeFiveKNonEasyVariedEasyMiles(
  totalEasy,
  dayNames,
  weekdayCapMi
) {
  return distributeFiveKEasyVariedWeekdayMiles(
    totalEasy,
    dayNames,
    weekdayCapMi,
    1.5
  );
}

/**
 * Split weekday easy miles across n days using {1, 1.5, 2, 2.5} mi (within cap). Prefers spread.
 * @param minPerDay — optional floor per weekday (e.g. 2 for 5K Easy peak week).
 */
function distributeFiveKEasyVariedWeekdayMiles(
  totalEasy,
  dayNames,
  weekdayCapMi,
  minPerDay = 0
) {
  const n = dayNames.length;
  if (n === 0) return [];
  const tgt = Number(totalEasy);
  if (!Number.isFinite(tgt) || tgt <= 0) return Array(n).fill(0);
  let effMin = Number(minPerDay) || 0;
  if (effMin > 0 && tgt + 1e-9 < n * effMin) {
    effMin = 0;
  }
  const allowed = [1, 1.5, 2, 2.5].filter((v) => v <= weekdayCapMi + 1e-9);
  if (allowed.length === 0) {
    return distributeEasyMilesAcrossDays(
      roundWhole(tgt),
      dayNames,
      Math.max(1, Math.floor(weekdayCapMi)),
      false,
      "Miles"
    );
  }
  for (const tol of [0.01, 0.51, 1.01]) {
    let best = null;
    let bestScore = -Infinity;
    const path = [];
    const dfs = () => {
      if (path.length === n) {
        const sum = path.reduce((a, b) => a + b, 0);
        if (Math.abs(sum - tgt) > tol) return;
        if (effMin > 0 && path.some((v) => v < effMin - 1e-9)) return;
        const sc = spreadScoreFiveKEasyMiles(path);
        if (sc > bestScore) {
          bestScore = sc;
          best = [...path];
        }
        return;
      }
      for (const v of allowed) {
        if (effMin > 0 && v < effMin - 1e-9) continue;
        path.push(v);
        dfs();
        path.pop();
      }
    };
    dfs();
    if (best) return best;
  }
  return distributeEasyMilesAcrossDays(
    roundWhole(tgt),
    dayNames,
    Math.max(1, Math.floor(weekdayCapMi)),
    false,
    "Miles"
  );
}

/**
 * 10K Easy Mon/Thu: half-step mile choices from 1 mi up to cap (within race distance).
 */
function distributeTenKEasyVariedWeekdayMiles(
  totalEasy,
  dayNames,
  weekdayCapMi,
  minPerDay = 0
) {
  const n = dayNames.length;
  if (n === 0) return [];
  const tgt = Number(totalEasy);
  if (!Number.isFinite(tgt) || tgt <= 0) return Array(n).fill(0);
  const effMin = Number(minPerDay) || 0;
  const cap = Math.min(6.2, Number(weekdayCapMi) || 1);
  const allowed = [];
  for (let v = 1; v <= cap + 1e-9; v += 0.5) {
    allowed.push(Math.round(v * 2) / 2);
  }
  if (allowed.length === 0) {
    return distributeEasyMilesAcrossDays(
      roundWhole(tgt),
      dayNames,
      Math.max(1, Math.floor(cap)),
      false,
      "Miles"
    );
  }
  for (const tol of [0.01, 0.51, 1.01]) {
    let best = null;
    let bestScore = -Infinity;
    const path = [];
    const dfs = () => {
      if (path.length === n) {
        const sum = path.reduce((a, b) => a + b, 0);
        if (Math.abs(sum - tgt) > tol) return;
        if (effMin > 0 && path.some((v) => v < effMin - 1e-9)) return;
        const sc = spreadScoreFiveKEasyMiles(path);
        if (sc > bestScore) {
          bestScore = sc;
          best = [...path];
        }
        return;
      }
      for (const v of allowed) {
        if (effMin > 0 && v < effMin - 1e-9) continue;
        path.push(v);
        dfs();
        path.pop();
      }
    };
    dfs();
    if (best) return best;
  }
  return distributeEasyMilesAcrossDays(
    roundWhole(tgt),
    dayNames,
    Math.max(1, Math.floor(cap)),
    false,
    "Miles"
  );
}

/** Easy cutback: split easy-day total evenly on the 0.5 mi grid (no floor). */
function balanceEasyDaysCutbackEven(milesPerDay, capMi) {
  if (!Array.isArray(milesPerDay) || milesPerDay.length === 0) {
    return milesPerDay;
  }
  if (milesPerDay.length === 2) {
    return balanceTenKEasyMonThuCutbackEven(milesPerDay, capMi);
  }
  const n = milesPerDay.length;
  const total = milesPerDay.reduce((a, b) => a + (Number(b) || 0), 0);
  const totalRounded = Math.round(total * 2) / 2;
  if (totalRounded <= 0) return milesPerDay.slice();

  const basePer = Math.floor(totalRounded / n / 0.5) * 0.5;
  const out = Array(n).fill(basePer);
  let halfUnitsLeft = Math.round((totalRounded - basePer * n) / 0.5);
  for (let guard = 0; halfUnitsLeft > 0 && guard < n * 8; guard += 1) {
    const idx = guard % n;
    if (out[idx] + 0.5 <= capMi + 1e-9) {
      out[idx] = Math.round((out[idx] + 0.5) * 2) / 2;
      halfUnitsLeft -= 1;
    }
  }
  const sum = out.reduce((a, b) => a + b, 0);
  if (Math.abs(sum - totalRounded) > 0.06) return milesPerDay.slice();
  if (out.some((v) => v > capMi + 1e-9)) return milesPerDay.slice();
  return out;
}

/** 10K Easy cutback: split Mon/Thu easy total evenly on the 0.5 mi grid (no floor). */
function balanceTenKEasyMonThuCutbackEven(pair, capMi) {
  if (!Array.isArray(pair) || pair.length !== 2) return pair;
  const total = Math.round((Number(pair[0]) + Number(pair[1])) * 2) / 2;
  if (total <= 0) return pair;
  const even = Math.round((total / 2) * 2) / 2;
  let mon = even;
  let thu = Math.round((total - even) * 2) / 2;
  mon = Math.min(mon, capMi);
  thu = Math.min(thu, capMi);
  mon = Math.max(0, Math.round((total - thu) * 2) / 2);
  thu = Math.round((total - mon) * 2) / 2;
  if (mon > capMi + 1e-9 || thu > capMi + 1e-9) return pair;
  return [mon, thu];
}

/** 10K Easy Mon/Thu: recenter the two-day easy total on its mean with a 1 mi spread
 *  (Monday shorter) so both days progress with weekly volume while staying varied — e.g.
 *  total 6 → [2.5, 3.5], total 7 → [3, 4]. Falls back to an equal split when the floor
 *  would otherwise force a day down to it (e.g. total 5 → [2.5, 2.5]). */
function balanceTenKEasyMonThuForProgression(pair, floorMi, capMi) {
  if (!Array.isArray(pair) || pair.length !== 2) return pair;
  const total = Math.round((pair[0] + pair[1]) * 2) / 2;
  if (total <= 0) return pair;
  const mean = total / 2;
  const low = Math.round((mean - 0.5) * 2) / 2;
  const high = Math.round((total - low) * 2) / 2;
  // Prefer a 1 mi spread (Monday shorter) when the shorter day clears the floor
  // and the longer day fits the cap.
  if (low > floorMi + 1e-9 && high <= capMi + 1e-9 && high >= floorMi - 1e-9) {
    return [low, high];
  }
  // Floor forces an even split; keep it on the 0.5 grid and within [floor, cap].
  const even = Math.round(mean * 2) / 2;
  if (even >= floorMi - 1e-9 && even <= capMi + 1e-9) {
    return [even, even];
  }
  return pair;
}

/** After capping walk to ≤ prior easy (legacy Thu walk layout), push surplus into earlier easy days. */
function rebalanceFiveKWalkSurplusToEarlierDays(
  dayList,
  milesPerDay,
  weekdayCapMi
) {
  const wedI = dayList.indexOf("Wednesday");
  const thuI = dayList.indexOf("Thursday");
  if (wedI < 0 || thuI < 0) return milesPerDay.slice();
  const out = milesPerDay.map((x) => Number(x) || 0);
  if (out[thuI] <= out[wedI] + 1e-9) return out;
  let surplus = Math.round((out[thuI] - out[wedI]) * 2) / 2;
  out[thuI] = out[wedI];
  const fillOrder = ["Monday", "Tuesday", "Wednesday", "Friday"];
  for (const day of fillOrder) {
    const i = dayList.indexOf(day);
    if (i < 0 || surplus < 0.25) continue;
    const room = weekdayCapMi - out[i];
    if (room < 0.25) continue;
    const add = Math.min(surplus, Math.floor(room * 2 + 1e-9) / 2);
    if (add < 0.25) continue;
    out[i] = Math.round((out[i] + add) * 2) / 2;
    surplus = Math.round((surplus - add) * 2) / 2;
  }
  return out;
}

function replaceZeroDistanceWorkoutsWithRest(sched) {
  const out = { ...sched };
  const zeroDist =
    /\b0(\.0+)?\s*(mi|mile|miles|km|k)\b/i;
  for (const d of Object.keys(out)) {
    const v = out[d];
    if (typeof v !== "string" || v === "Rest") continue;
    if (zeroDist.test(v) || /\b0(\.0+)?\s*total\b/i.test(v)) {
      out[d] = "Rest";
    }
  }
  return out;
}

function computeWeekMileage(schedule = {}, unitPreference = "Miles") {
  const entries = Object.values(schedule);
  let total = 0;
  for (const workout of entries) {
    if (typeof workout !== "string") continue;
    /** Race-day lines include “3.1 mi … 5K …” — skip so “5K” is not parsed as 5 km (double count). */
    if (/race day/i.test(workout)) continue;
    const matches = workout.match(/([\d.]+)\s*(mi|mile|miles|km|k)\b/gi);
    if (!matches) continue;
    for (const m of matches) {
      const mm = m.match(/([\d.]+)\s*(mi|mile|miles|km|k)\b/i);
      if (!mm) continue;
      let num = parseFloat(mm[1]);
      const u = mm[2].toLowerCase();
      if (u === "km" || u === "k") {
        if (unitPreference === "Miles") num /= MI_TO_KM;
      } else if (unitPreference === "Kilometers") num *= MI_TO_KM;
      total += num;
    }
  }
  return roundWhole(total);
}

/** Canonical race distance in miles (matches raceDayLabel); used for race-week total. */
const CANONICAL_RACE_DISTANCE_MI = {
  "5K": 3.1,
  "10K": 6.2,
  "10 Miler": 10,
  "Half Marathon": 13.1,
  Marathon: 26.2,
};

function canonicalRaceDistanceMilesForTotal(normalizedDistance) {
  const d = normalizeRaceDistance(normalizedDistance);
  const v = CANONICAL_RACE_DISTANCE_MI[d];
  return v != null && Number.isFinite(v) ? v : null;
}

/** Parsed workouts (race line excluded in computeWeekMileage) + canonical race distance once. */
function computeRaceWeekTotalMileage(schedule, unitPreference, normalizedDistance) {
  const fromWorkouts = computeWeekMileage(schedule, unitPreference);
  const raceMi = canonicalRaceDistanceMilesForTotal(normalizedDistance);
  if (raceMi == null) return fromWorkouts;
  const add =
    unitPreference === "Kilometers" ? raceMi * MI_TO_KM : raceMi;
  return roundWhole(fromWorkouts + add);
}

function sortScheduleByWeekday(schedule = {}) {
  const out = {};
  for (const d of WEEK_ORDER) {
    if (Object.prototype.hasOwnProperty.call(schedule, d)) out[d] = schedule[d];
  }
  for (const k of Object.keys(schedule)) {
    if (!Object.prototype.hasOwnProperty.call(out, k)) out[k] = schedule[k];
  }
  return out;
}

/** Easy / onboarding: weekday list for distributing easy mileage only. */
function easyOnlyRunDayList(runSlots) {
  if (runSlots <= 2) return ["Tuesday", "Thursday"];
  if (runSlots === 3) return ["Monday", "Wednesday", "Thursday"];
  if (runSlots === 4) return ["Monday", "Tuesday", "Wednesday", "Friday"];
  return ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
}

function isFiveKIntensePlan(distance, intensity) {
  return (
    normalizeRaceDistance(distance) === "5K" &&
    normalizeIntensityLabel(intensity) === "Intense"
  );
}

/** 5K Moderate (4 days/wk → runSlots 3): Mon/Tue easy, Wed rest, Thu intervals, Fri rest, Sat easy run. */
function isFiveKModerateFourSlotPlan(distance, intensity, runSlots) {
  return (
    normalizeRaceDistance(distance) === "5K" &&
    normalizeIntensityLabel(intensity) === "Moderate" &&
    runSlots === 3
  );
}

function isFiveKChallengingFourSlotPlan(distance, intensity, runSlots) {
  return (
    normalizeRaceDistance(distance) === "5K" &&
    normalizeIntensityLabel(intensity) === "Challenging" &&
    runSlots === 4
  );
}

/** 10K Moderate (4 days/wk → runSlots 3): Mon tempo, Tue easy, Wed rest, Thu intervals, Sat long run. */
function isTenKModerateThreeSlotPlan(distance, intensity, runSlots) {
  return (
    normalizeRaceDistance(distance) === "10K" &&
    normalizeIntensityLabel(intensity) === "Moderate" &&
    runSlots === 3
  );
}

/** Onboarding easy slots for 5K Moderate (3 run slots). */
function easyOnlyRunDayListFiveKModerateThreeSlot() {
  return ["Monday", "Tuesday", "Thursday"];
}

/** Onboarding easy slots for 5K Challenging (4 run slots; Thu & Sun rest). */
function easyOnlyRunDayListFiveKChallengingFourSlot() {
  return ["Monday", "Tuesday", "Wednesday", "Friday"];
}

/** 5K Challenging quality layout: Wed quality (intervals or cutback tempo); Thu/Sun always rest. */
function fiveKChallengingQualityLayout(isCutbackWeek) {
  return {
    tempoDay: isCutbackWeek ? "Wednesday" : null,
    intervalDay: isCutbackWeek ? null : "Wednesday",
    easyDays: ["Monday", "Tuesday", "Friday"],
  };
}

/** Onboarding easy slots for 10K Moderate (3 run slots). */
function easyOnlyRunDayListTenKModerateThreeSlot() {
  return ["Monday", "Tuesday", "Thursday"];
}

/** Onboarding easy slots for 5K Intense (5 days/wk, Thu rest). */
function easyOnlyRunDayListFiveKIntenseFourSlot() {
  return ["Monday", "Tuesday", "Wednesday", "Friday"];
}

function easyOnlyRunDayListForPlan(runSlots, distance, intensity) {
  if (isFiveKIntensePlan(distance, intensity) && runSlots === 4) {
    return easyOnlyRunDayListFiveKIntenseFourSlot();
  }
  if (isFiveKModerateFourSlotPlan(distance, intensity, runSlots)) {
    return easyOnlyRunDayListFiveKModerateThreeSlot();
  }
  if (isFiveKChallengingFourSlotPlan(distance, intensity, runSlots)) {
    return easyOnlyRunDayListFiveKChallengingFourSlot();
  }
  if (isTenKModerateThreeSlotPlan(distance, intensity, runSlots)) {
    return easyOnlyRunDayListTenKModerateThreeSlot();
  }
  return easyOnlyRunDayList(runSlots);
}

/**
 * Quality layout: default `qualityRunWeekLayout` uses Thu easy for runSlots 4; 5K Intense uses Tue/Fri
 * with Thu (and Sun) rest.
 */
function qualityRunWeekBaseLayoutForPlan(runSlots, distance, intensity) {
  if (isFiveKIntensePlan(distance, intensity) && runSlots === 4) {
    return {
      tempoDay: "Monday",
      intervalDay: "Wednesday",
      easyDays: ["Tuesday", "Friday"],
    };
  }
  if (isFiveKModerateFourSlotPlan(distance, intensity, runSlots)) {
    return {
      tempoDay: null,
      intervalDay: "Thursday",
      easyDays: ["Monday", "Tuesday"],
    };
  }
  if (isFiveKChallengingFourSlotPlan(distance, intensity, runSlots)) {
    return fiveKChallengingQualityLayout(false);
  }
  if (isTenKModerateThreeSlotPlan(distance, intensity, runSlots)) {
    return {
      tempoDay: "Monday",
      intervalDay: "Thursday",
      easyDays: ["Tuesday"],
    };
  }
  return qualityRunWeekLayout(runSlots);
}

function taperQualityRunDayLayoutForPlan(runSlots, distance, intensity) {
  if (isFiveKIntensePlan(distance, intensity) && runSlots === 4) {
    return {
      tempoDay: "Monday",
      easyDays: ["Tuesday", "Friday"],
    };
  }
  if (isFiveKChallengingFourSlotPlan(distance, intensity, runSlots)) {
    return {
      tempoDay: "Wednesday",
      easyDays: ["Monday", "Tuesday", "Friday"],
    };
  }
  if (isFiveKModerateFourSlotPlan(distance, intensity, runSlots)) {
    return {
      tempoDay: null,
      easyDays: ["Monday", "Tuesday"],
    };
  }
  if (isTenKModerateThreeSlotPlan(distance, intensity, runSlots)) {
    return {
      tempoDay: "Monday",
      easyDays: ["Tuesday"],
    };
  }
  return taperQualityRunDayLayout(runSlots);
}

/**
 * Quality weeks (tempo + intervals): Mon tempo, Tue easy buffer, Wed intervals, Thu (+ Fri) easy.
 * Avoids consecutive hard days (old Mon tempo / Tue intervals).
 */
function qualityRunWeekLayout(runSlots) {
  if (runSlots <= 2) {
    return {
      tempoDay: "Monday",
      intervalDay: null,
      easyDays: ["Thursday"],
    };
  }
  if (runSlots === 3) {
    return {
      tempoDay: "Monday",
      intervalDay: "Wednesday",
      easyDays: ["Tuesday"],
    };
  }
  if (runSlots === 4) {
    return {
      tempoDay: "Monday",
      intervalDay: "Wednesday",
      easyDays: ["Tuesday", "Friday"],
    };
  }
  return {
    tempoDay: "Monday",
    intervalDay: "Wednesday",
    easyDays: ["Tuesday", "Thursday", "Friday"],
  };
}

/**
 * Cutback quality weeks: Wed-interval plans → Wed tempo + Mon easy;
 * Thu-interval plans (e.g. 10K Moderate) → Thu tempo + Mon/Tue easy, Wed stays rest.
 * 5K Moderate: Thu tempo only, Mon/Tue easy.
 * 5K Challenging: Wed tempo only (was intervals), Mon/Tue/Fri easy, Thu rest — same skeleton as build.
 */
function cutbackSingleTempoQualityLayoutIfNeeded(
  baseLayout,
  isTrainingCutbackWeek,
  distance = "",
  intensity = ""
) {
  if (!isTrainingCutbackWeek) return baseLayout;

  const d = normalizeRaceDistance(distance);
  const inten = normalizeIntensityLabel(intensity);

  if (d === "5K" && inten === "Moderate") {
    return {
      tempoDay: "Thursday",
      intervalDay: null,
      easyDays: ["Monday", "Tuesday"],
    };
  }

  if (d === "5K" && inten === "Challenging") {
    return fiveKChallengingQualityLayout(true);
  }

  const intervalDay = baseLayout.intervalDay;
  if (
    intervalDay == null ||
    baseLayout.tempoDay !== "Monday" ||
    (intervalDay !== "Wednesday" && intervalDay !== "Thursday")
  ) {
    return baseLayout;
  }
  if (intervalDay === "Thursday") {
    return {
      tempoDay: "Thursday",
      intervalDay: null,
      easyDays: ["Monday", ...baseLayout.easyDays],
    };
  }
  return {
    tempoDay: "Wednesday",
    intervalDay: null,
    easyDays: ["Monday", ...baseLayout.easyDays],
  };
}

function assignTempoDayIfPresent(sched, tempoDay, tempoMi, unitPreference) {
  if (tempoDay == null || tempoDay === "") return;
  sched[tempoDay] = `Tempo Run (${formatDist(tempoMi, unitPreference)})`;
}

/**
 * Tempo / Easy / Intervals / Easy weeks only (interval day present): after easy split, ensure
 * Thursday ≥ Tuesday (swap if needed), then shorten Tuesday (−25%, floor) and Thursday (−10%,
 * rounded); dropped miles stay out of the week (no redistribution). Tuesday is floored at 2 mi
 * after reductions so early weeks stay a meaningful easy day.
 */
function applyTuesdayThursdayPreIntervalEasyReductions(qualityLayout, milesArr) {
  if (
    qualityLayout == null ||
    qualityLayout.intervalDay == null ||
    !qualityLayout.easyDays?.length ||
    !milesArr?.length
  ) {
    return milesArr;
  }
  const names = qualityLayout.easyDays;
  const out = milesArr.map((x) => roundWhole(x));
  const iTue = names.indexOf("Tuesday");
  const iThu = names.indexOf("Thursday");
  if (iThu < 0) {
    return ensureListedEasyDayMinimumsWithRedistribution(out, names, 2);
  }
  const sumEasyDays = out.reduce((a, b) => a + b, 0);
  if (
    iTue >= 0 &&
    iThu >= 0 &&
    iTue < out.length &&
    iThu < out.length &&
    sumEasyDays <= 2 * names.length + 1
  ) {
    return ensureListedEasyDayMinimumsWithRedistribution(out, names, 2);
  }
  if (
    iTue >= 0 &&
    iThu >= 0 &&
    iTue < out.length &&
    iThu < out.length &&
    out[iThu] < out[iTue]
  ) {
    const t = out[iTue];
    out[iTue] = out[iThu];
    out[iThu] = t;
  }
  names.forEach((day, idx) => {
    if (idx >= out.length) return;
    const v = Math.max(0, out[idx]);
    if (day === "Tuesday") {
      out[idx] = Math.floor(v * 0.75);
    } else if (day === "Thursday") {
      out[idx] = Math.round(v * 0.9);
    }
  });
  if (iTue >= 0 && iTue < out.length) {
    out[iTue] = Math.max(2, out[iTue]);
  }
  return ensureListedEasyDayMinimumsWithRedistribution(out, names, 2);
}

/**
 * Listed easy days (quality / taper): work each toward `minMi` using peer miles; order follows
 * QUALITY_EASY_DAY_MI_FLOOR_PRIORITY. Tuesday prefers stealing from non-Thursday donors first.
 */
function ensureListedEasyDayMinimumsWithRedistribution(
  milesArr,
  dayNames,
  minMi = 2
) {
  if (
    milesArr == null ||
    !dayNames?.length ||
    milesArr.length !== dayNames.length
  ) {
    return milesArr;
  }
  const out = milesArr.map((x) => roundWhole(x));
  for (let sweep = 0; sweep < 24; sweep += 1) {
    const recipientName = QUALITY_EASY_DAY_MI_FLOOR_PRIORITY.find((d) => {
      const idx = dayNames.indexOf(d);
      return idx >= 0 && out[idx] < minMi;
    });
    if (!recipientName) break;
    const iRec = dayNames.indexOf(recipientName);
    let shortfall = minMi - out[iRec];
    if (shortfall <= 0) continue;

    const takeFromDonors = (minKeepOnDonor) => {
      while (shortfall > 0) {
        const donors = out
          .map((v, idx) => ({ idx, v }))
          .filter((x) => x.idx !== iRec && x.v > minKeepOnDonor);
        donors.sort((a, b) => {
          if (recipientName === "Tuesday") {
            const ta = dayNames[a.idx] === "Thursday" ? 1 : 0;
            const tb = dayNames[b.idx] === "Thursday" ? 1 : 0;
            if (ta !== tb) return ta - tb;
          }
          return b.v - a.v;
        });
        if (donors.length === 0) break;
        const d = donors[0];
        const take = Math.min(shortfall, d.v - minKeepOnDonor);
        if (take <= 0) break;
        out[d.idx] -= take;
        shortfall -= take;
      }
    };

    takeFromDonors(minMi);
    takeFromDonors(1);
    out[iRec] = minMi - shortfall;
  }
  return out;
}

function ensureThursdayEasyMinimumWithRedistribution(
  milesArr,
  dayNames,
  minMi = 2
) {
  return ensureListedEasyDayMinimumsWithRedistribution(
    milesArr,
    dayNames,
    minMi
  );
}

/** Taper with tempo: Mon tempo; remaining run weekdays are easy (no interval day in taper). */
function taperQualityRunDayLayout(runSlots) {
  if (runSlots <= 0) {
    return { tempoDay: "Monday", easyDays: [] };
  }
  if (runSlots <= 2) {
    return { tempoDay: "Monday", easyDays: ["Thursday"] };
  }
  if (runSlots === 3) {
    return { tempoDay: "Monday", easyDays: ["Tuesday", "Wednesday"] };
  }
  if (runSlots === 4) {
    return {
      tempoDay: "Monday",
      easyDays: ["Tuesday", "Wednesday", "Thursday"],
    };
  }
  return {
    tempoDay: "Monday",
    easyDays: ["Tuesday", "Wednesday", "Thursday", "Friday"],
  };
}

/**
 * Taper: long run + one short tempo + easy; `weeklyMi` drives total intent.
 * LR share ~92% of normal long-run fraction so volume stays ~65–70% of peak when weeklyMi is set.
 * @param easyNoTempo — Easy plans: only easy + long run (no tempo).
 * @param distance, intensity — Easy: per-run cap vs LR via maxEasyRunMiGivenLongRun (LR must exceed each easy run).
 */
function buildTaperWeekSchedule(
  weeklyMi,
  daysPerWeek,
  unitPreference,
  longRunPct,
  easyNoTempo = false,
  distance = "",
  intensity = ""
) {
  const sched = {};
  WEEK_ORDER.forEach((d) => {
    sched[d] = "Rest";
  });

  const runSlots = Math.max(0, daysPerWeek - 1);
  const easyOnlyList = easyOnlyRunDayListForPlan(runSlots, distance, intensity);
  const taperLayout = taperQualityRunDayLayoutForPlan(runSlots, distance, intensity);

  const lrShare = longRunPct * 0.92;
  let lrMiles = weeklyMi * lrShare;
  if (easyNoTempo && isEasyIntensity(intensity) && runSlots > 0 && weeklyMi > runSlots) {
    lrMiles = Math.min(lrMiles, weeklyMi - runSlots);
  }
  let lr = roundWhole(lrMiles);
  if (easyNoTempo && isEasyIntensity(intensity)) {
    lr = clampEasyIntensityLongRunWhole(lr, weeklyMi, runSlots);
    lrMiles = lr;
  }
  if (isEasyIntensity(intensity)) {
    lrMiles = clampEasyIntensityRunMilesToRaceDistance(
      lrMiles,
      distance,
      intensity
    );
    lr = roundWhole(lrMiles);
  }
  lr = roundWhole(Number(lrMiles) || 0);
  lrMiles = lr;
  const wkWhole = roundWhole(Number(weeklyMi) || 0);
  if (
    normalizeRaceDistance(distance) === "Half Marathon" &&
    !isEasyIntensity(intensity) &&
    !easyNoTempo
  ) {
    const maxLr = Math.max(
      0,
      wkWhole - HALF_MARATHON_NON_EASY_TAPER_RESERVE_FOR_TEMPO_EASY_MI
    );
    if (maxLr >= HALF_MARATHON_NON_EASY_TAPER_MIN_LONG_RUN_MI) {
      lr = Math.min(
        maxLr,
        Math.max(lr, HALF_MARATHON_NON_EASY_TAPER_MIN_LONG_RUN_MI)
      );
      lrMiles = lr;
    }
  }
  if (
    normalizeRaceDistance(distance) === "10 Miler" &&
    !isEasyIntensity(intensity) &&
    !easyNoTempo
  ) {
    const maxLrTm = Math.max(
      0,
      wkWhole - TEN_MILER_NON_EASY_TAPER_RESERVE_FOR_TEMPO_EASY_MI
    );
    if (maxLrTm >= TEN_MILER_NON_EASY_TAPER_MIN_LONG_RUN_MI) {
      lr = Math.min(
        maxLrTm,
        Math.max(lr, TEN_MILER_NON_EASY_TAPER_MIN_LONG_RUN_MI)
      );
      lrMiles = lr;
    }
  }
  if (
    normalizeRaceDistance(distance) === "10K" &&
    !isEasyIntensity(intensity) &&
    !easyNoTempo
  ) {
    const maxLrTk = Math.max(
      0,
      wkWhole - TEN_K_NON_EASY_TAPER_RESERVE_FOR_TEMPO_EASY_MI
    );
    if (maxLrTk >= TEN_K_NON_EASY_TAPER_MIN_LONG_RUN_MI) {
      lr = Math.min(
        maxLrTk,
        Math.max(lr, TEN_K_NON_EASY_TAPER_MIN_LONG_RUN_MI)
      );
      lrMiles = lr;
    }
  }
  let remaining = Math.max(0, roundWhole(weeklyMi - lr));
  sched.Saturday =
    lr <= 0
      ? "Rest"
      : formatPrimarySaturdayRunLine(distance, lr, unitPreference, intensity);

  const easySessionCap = easyNoTempo
    ? maxEasyRunMiGivenLongRun(lr, distance, intensity, null)
    : maxSingleEasyRunMiForEasyPlan(distance, intensity);

  const n = Math.max(1, easyOnlyList.length);
  let totalEasy = Math.max(0, remaining);
  const tempoUncapped = Math.min(
    Math.max(2, roundWhole(weeklyMi * 0.07)),
    Math.max(0, totalEasy - 1)
  );
  let tempoMi = Math.min(
    maxTempoSessionMiForPlan(distance, intensity),
    tempoUncapped
  );
  if (isEasyIntensity(intensity)) {
    tempoMi = roundWhole(
      clampEasyIntensityRunMilesToRaceDistance(tempoMi, distance, intensity)
    );
  }
  if (
    !easyNoTempo &&
    usesChallengingIntenseNonFiveKQualityTempoFloor(distance, intensity)
  ) {
    const minT = QUALITY_CHALLENGING_INTENSE_MIN_TEMPO_MI;
    const maxTemp = maxTempoSessionMiForPlan(distance, intensity);
    const nEasyTaper = taperLayout.easyDays.length;
    const minEasySum = nEasyTaper * 2;
    const maxTempoAfford = Math.max(0, totalEasy - minEasySum);
    tempoMi = Math.min(
      maxTemp,
      Math.max(tempoMi, minT),
      totalEasy,
      maxTempoAfford
    );
  }
  if (
    !easyNoTempo &&
    n >= 1 &&
    tempoMi > 0 &&
    totalEasy >= tempoMi
  ) {
    totalEasy -= tempoMi;
    assignTempoDayIfPresent(
      sched,
      taperLayout.tempoDay,
      tempoMi,
      unitPreference
    );
    const restDays = taperLayout.easyDays;
    if (restDays.length > 0) {
      let milesArr = distributeEasyMilesAcrossDaysRespectingThursdayFloor(
        totalEasy,
        restDays,
        easySessionCap,
        true,
        unitPreference,
        !easyNoTempo && !isEasyIntensity(intensity)
      );
      milesArr = ensureThursdayEasyMinimumWithRedistribution(
        milesArr,
        restDays,
        2
      );
      restDays.forEach((day, idx) => {
        const dayMi = capEasyRunSessionMiles(
          milesArr[idx] ?? 0,
          easySessionCap
        );
        sched[day] = easyRunLineForTaperOrDefault(
          dayMi,
          unitPreference,
          distance,
          intensity
        );
      });
    }
  } else if (easyNoTempo && isEasyIntensity(intensity)) {
    const milesPerDay = distributeEasyMilesAcrossDays(
      totalEasy,
      easyOnlyList,
      easySessionCap,
      false,
      unitPreference
    );
    easyOnlyList.forEach((day, idx) => {
      const dayMi = milesPerDay[idx] ?? 0;
      if (dayMi <= 0) {
        sched[day] = "Rest";
        return;
      }
      if (isFiveKEasyPlan(distance, intensity) && day === "Wednesday") {
        sched[day] = `Walk (${formatDist(dayMi, unitPreference)})`;
      } else {
        sched[day] = easyRunLineForTaperOrDefault(
          dayMi,
          unitPreference,
          distance,
          intensity
        );
      }
    });
  } else {
    let milesTaper = distributeEasyMilesAcrossDaysRespectingThursdayFloor(
      totalEasy,
      easyOnlyList,
      easySessionCap,
      true,
      unitPreference,
      !easyNoTempo && !isEasyIntensity(intensity)
    );
    milesTaper = ensureThursdayEasyMinimumWithRedistribution(
      milesTaper,
      easyOnlyList,
      2
    );
    easyOnlyList.forEach((day, idx) => {
      const dayMi = capEasyRunSessionMiles(
        milesTaper[idx] ?? 0,
        easySessionCap
      );
      sched[day] = easyRunLineForTaperOrDefault(
        dayMi,
        unitPreference,
        distance,
        intensity
      );
    });
  }
  if (!easyNoTempo && isTenKModeratePlan(distance, intensity)) {
    applyTenKModerateTuesdayEasyCapFromSchedule(
      sched,
      easySessionCap,
      unitPreference,
      distance,
      intensity
    );
  }
  return sortScheduleByWeekday(replaceZeroDistanceWorkoutsWithRest(sched));
}

/**
 * Build Mon–Sun schedule: Rest default, Saturday long run; quality weeks use Mon tempo / Wed intervals / easy buffers (see qualityRunWeekLayout). Cutback weeks: Wed tempo only, Mon easy.
 * @param onboardingEasyOnly — week 1 onboarding: easy + long run only (no tempo/interval).
 * @param cutbackPrevLongRunMi — if set (cutback week), cap long run at CUTBACK_LONG_RUN_FRAC (~67.5%) of prior week’s LR; applies to canonical curves (Marathon/Half/…) and %-based weeks.
 * @param intensity — Easy: all weeks are easy + long run only (no tempo/intervals).
 * @param weekNum, trainingWeekCount — Marathon / Half / 10 Miler / 10K / 5K: interpolated canonical LR curve; Easy (other distances): ramp + peak + weeklyMi (omit for taper).
 * @param prevWeekLongRunMi — parsed prior LR; after a cutback week, caps recovery vs prior (omit week 1).
 * @param weeklyVolumeAdjustOut — if object, may set `.effectiveWeeklyMi` when Easy week is bumped for min easy-day miles.
 * @param priorNonCutbackLongRunMi — canonical plans: last non-cutback week’s LR (recovery / monotonicity).
 * @param priorBuildWeekTempoMi — parsed tempo mi from last non-cutback week before this one; cutback single-tempo weeks cap vs ~75% of this.
 * @param cutbackEasySessionCapMi — non-Easy cutback weeks: max miles per easy run from prior week avg easy × 0.8 (min 2); null otherwise.
 */
function buildTrainingWeek(
  weeklyMi,
  daysPerWeek,
  distance,
  unitPreference,
  longRunPct,
  isTaper,
  onboardingEasyOnly = false,
  cutbackPrevLongRunMi = null,
  intensity = "",
  weekNum = null,
  trainingWeekCount = null,
  prevWeekLongRunMi = null,
  weeklyVolumeAdjustOut = null,
  priorNonCutbackLongRunMi = null,
  priorBuildWeekTempoMi = null,
  cutbackEasySessionCapMi = null
) {
  if (isTaper) {
    return buildTaperWeekSchedule(
      weeklyMi,
      daysPerWeek,
      unitPreference,
      longRunPct,
      isEasyIntensity(intensity),
      distance,
      intensity
    );
  }

  const sched = {};
  WEEK_ORDER.forEach((d) => {
    sched[d] = "Rest";
  });

  const peakLrCapMi = getPeakLongRunTargetMiForIntensity(distance, intensity);
  const usesCanonicalLr =
    usesCanonicalLongRunSchedule(distance) &&
    !isFiveKEasyPlan(distance, intensity) &&
    !isTenKEasyPlan(distance, intensity) &&
    !isHalfMarathonEasyPlan(distance, intensity) &&
    !isTenMilerEasyPlan(distance, intensity) &&
    weekNum != null &&
    trainingWeekCount != null &&
    trainingWeekCount >= 1;

  const useEasyLrRamp =
    !usesCanonicalLr &&
    isEasyIntensity(intensity) &&
    weekNum != null &&
    trainingWeekCount != null &&
    trainingWeekCount >= 1;

  let lrMiles;
  let maxLrAfterPriorCutback = null;

  if (usesCanonicalLr) {
    const dist = normalizeRaceDistance(distance);
    const scheduled =
      dist === "Marathon"
        ? marathonScheduledLongRunMi(weekNum, trainingWeekCount)
        : dist === "Half Marathon"
          ? halfMarathonScheduledLongRunMi(weekNum, trainingWeekCount)
          : dist === "10 Miler"
            ? tenMilerScheduledLongRunMi(weekNum, trainingWeekCount)
            : dist === "10K"
              ? tenKScheduledLongRunMi(weekNum, trainingWeekCount)
              : fiveKScheduledLongRunMi(weekNum, trainingWeekCount);
    const wk = Number(weeklyMi);
    lrMiles = Math.min(
      scheduled,
      peakLrCapMi,
      Number.isFinite(wk) ? wk : 0
    );
  } else {
    lrMiles = Math.min(weeklyMi * longRunPct, peakLrCapMi);
    if (useEasyLrRamp) {
      const ramped = easyIntensityRampLongRunMi(
        weekNum,
        trainingWeekCount,
        peakLrCapMi
      );
      lrMiles = Math.min(ramped, peakLrCapMi, weeklyMi);
      const priorWeekWasCutback =
        weekNum > 1 && isCutbackWeek(weekNum - 1, trainingWeekCount);
      if (
        priorWeekWasCutback &&
        prevWeekLongRunMi != null &&
        Number.isFinite(prevWeekLongRunMi) &&
        roundWhole(prevWeekLongRunMi) > 0
      ) {
        maxLrAfterPriorCutback = maxLongRunMiAfterPriorCutbackEasy(
          prevWeekLongRunMi,
          distance
        );
        if (maxLrAfterPriorCutback != null) {
          lrMiles = Math.min(lrMiles, maxLrAfterPriorCutback);
        }
      }
    }
  }
  if (
    cutbackPrevLongRunMi != null &&
    Number.isFinite(cutbackPrevLongRunMi) &&
    cutbackPrevLongRunMi > 0
  ) {
    const prevLrWhole = roundWhole(cutbackPrevLongRunMi);
    const capMi = Math.max(2, prevLrWhole * CUTBACK_LONG_RUN_FRAC);
    lrMiles = Math.min(lrMiles, capMi);
  }

  const runSlots = Math.max(0, daysPerWeek - 1);
  const easyOnlyDays = easyOnlyRunDayListForPlan(runSlots, distance, intensity);
  const isTrainingCutbackWeek =
    weekNum != null &&
    trainingWeekCount != null &&
    trainingWeekCount >= 1 &&
    isCutbackWeek(weekNum, trainingWeekCount);
  const qualityBaseLayout = qualityRunWeekBaseLayoutForPlan(
    runSlots,
    distance,
    intensity
  );
  const qualityLayout = cutbackSingleTempoQualityLayoutIfNeeded(
    qualityBaseLayout,
    isTrainingCutbackWeek,
    distance,
    intensity
  );
  const cutbackSingleTempoLayout =
    isTrainingCutbackWeek &&
    runSlots >= 3 &&
    !isEasyIntensity(intensity);
  let cutbackTempoCapMi =
    cutbackSingleTempoLayout && priorBuildWeekTempoMi != null
      ? cutbackSingleTempoCapMiFromPriorBuild(priorBuildWeekTempoMi)
      : null;
  if (
    cutbackTempoCapMi != null &&
    normalizeRaceDistance(distance) === "5K" &&
    usesFiveKChallengingOrIntenseQualityTempoFloor(distance, intensity)
  ) {
    cutbackTempoCapMi = Math.max(
      cutbackTempoCapMi,
      FIVE_K_NON_MODERATE_MIN_TEMPO_MI
    );
  }

  let lr = roundWhole(lrMiles);
  if (isEasyIntensity(intensity)) {
    lr = clampEasyIntensityLongRunWhole(lr, weeklyMi, runSlots);
    lrMiles = lr;
    // Ramp + per-easy cap (e.g. 10K Easy: cap ≤ LR−1) can strand miles: weeklyMi is correct but
    // distributeEasyMilesAcrossDays only places min(remaining, runSlots × cap). Raise LR within
    // the same bounds as clamp (peak LR cap, weeklyMi, reserve ≥1 mi per easy slot) until it fits.
    if (runSlots > 0) {
      const wk = roundWhole(weeklyMi);
      const maxLrReserve = wk > runSlots ? wk - runSlots : wk;
      const maxLrSoak =
        wk >= MIN_LONG_RUN_MI_EASY_INTENSITY + runSlots
          ? Math.min(peakLrCapMi, wk, maxLrReserve)
          : Math.min(peakLrCapMi, wk);
      const lrSoakCeil =
        usesCanonicalLr || maxLrAfterPriorCutback == null
          ? maxLrSoak
          : Math.min(maxLrSoak, maxLrAfterPriorCutback);
      let capFill = maxEasyRunMiGivenLongRun(
        lr,
        distance,
        intensity,
        null
      );
      let remProbe = Math.max(0, wk - lr);
      while (remProbe > runSlots * capFill && lr < lrSoakCeil) {
        lr += 1;
        lrMiles = lr;
        remProbe = Math.max(0, wk - lr);
        capFill = maxEasyRunMiGivenLongRun(lr, distance, intensity, null);
      }
    }
    if (!usesCanonicalLr && maxLrAfterPriorCutback != null) {
      lr = Math.min(lr, maxLrAfterPriorCutback);
      lrMiles = lr;
    }
  }

  const isCanonCutbackWeek =
    weekNum != null &&
    trainingWeekCount != null &&
    trainingWeekCount >= 1 &&
    isCutbackWeek(weekNum, trainingWeekCount);

  if (usesCanonicalLr && isCanonCutbackWeek && prevWeekLongRunMi != null) {
    const prevW = roundWhole(prevWeekLongRunMi);
    if (prevW > 0 && lr >= prevW) {
      lr = Math.max(2, prevW - 1);
      lrMiles = lr;
    }
  }

  if (
    usesCanonicalLr &&
    !isCanonCutbackWeek &&
    priorNonCutbackLongRunMi != null
  ) {
    const prevNc = roundWhole(priorNonCutbackLongRunMi);
    if (prevNc > 0 && lr < prevNc) {
      const wkR = roundWhole(weeklyMi);
      if (isEasyIntensity(intensity)) {
        if (normalizeRaceDistance(distance) !== "5K") {
          const minNeed = prevNc + runSlots * MIN_EASY_DAY_MI_EASY_INTENSITY;
          if (weeklyVolumeAdjustOut && typeof weeklyVolumeAdjustOut === "object") {
            if (minNeed > wkR) {
              weeklyVolumeAdjustOut.effectiveWeeklyMi = Math.max(
                roundWhole(weeklyVolumeAdjustOut.effectiveWeeklyMi || wkR),
                minNeed
              );
            }
          }
          const wkEff = roundWhole(
            (weeklyVolumeAdjustOut &&
              weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
              wkR
          );
          const maxLrFit = Math.min(
            peakLrCapMi,
            wkEff >= runSlots + MIN_LONG_RUN_MI_EASY_INTENSITY
              ? wkEff - runSlots * MIN_EASY_DAY_MI_EASY_INTENSITY
              : wkEff
          );
          lr = Math.max(lr, Math.min(prevNc, maxLrFit));
          lrMiles = lr;
        }
      } else {
        const minRest = Math.max(6, runSlots * 2);
        const lrMaxFit = Math.min(
          peakLrCapMi,
          Math.max(0, wkR - minRest)
        );
        lr = Math.max(lr, Math.min(prevNc, lrMaxFit));
        lrMiles = lr;
        if (
          lr < prevNc &&
          weeklyVolumeAdjustOut &&
          typeof weeklyVolumeAdjustOut === "object"
        ) {
          const needWk = prevNc + minRest;
          weeklyVolumeAdjustOut.effectiveWeeklyMi = Math.max(
            roundWhole(weeklyVolumeAdjustOut.effectiveWeeklyMi || wkR),
            needWk
          );
          const wk2 = roundWhole(weeklyVolumeAdjustOut.effectiveWeeklyMi);
          lr = Math.min(prevNc, peakLrCapMi, Math.max(0, wk2 - minRest));
          lrMiles = lr;
        }
      }
    }
  }

  if (
    normalizeRaceDistance(distance) === "Half Marathon" &&
    isEasyIntensity(intensity) &&
    weekNum != null &&
    trainingWeekCount != null &&
    weekNum === trainingWeekCount &&
    !isCanonCutbackWeek
  ) {
    const wkBase = roundWhole(
      (weeklyVolumeAdjustOut &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
        weeklyMi
    );
    const minEasy = runSlots * MIN_EASY_DAY_MI_EASY_INTENSITY;
    const needWkForPeak = peakLrCapMi + minEasy;
    if (
      weeklyVolumeAdjustOut &&
      typeof weeklyVolumeAdjustOut === "object" &&
      needWkForPeak > wkBase
    ) {
      weeklyVolumeAdjustOut.effectiveWeeklyMi = Math.max(
        wkBase,
        needWkForPeak
      );
    }
    const wkPeak = roundWhole(
      (weeklyVolumeAdjustOut &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
        weeklyMi
    );
    const lrCeil = Math.min(
      peakLrCapMi,
      wkPeak > minEasy ? wkPeak - minEasy : wkPeak
    );
    if (lr < peakLrCapMi && lrCeil >= peakLrCapMi) {
      lr = peakLrCapMi;
      lrMiles = lr;
    } else if (lr < peakLrCapMi && lrCeil > lr) {
      lr = lrCeil;
      lrMiles = lr;
    }
  }

  if (
    normalizeRaceDistance(distance) === "10K" &&
    !isEasyIntensity(intensity) &&
    weekNum != null &&
    trainingWeekCount != null &&
    weekNum === trainingWeekCount &&
    !isCanonCutbackWeek
  ) {
    const wkBaseTk = roundWhole(
      (weeklyVolumeAdjustOut &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
        weeklyMi
    );
    const minRestTk = Math.max(6, runSlots * 2);
    const needWkPeakTk = peakLrCapMi + minRestTk;
    if (
      weeklyVolumeAdjustOut &&
      typeof weeklyVolumeAdjustOut === "object" &&
      needWkPeakTk > wkBaseTk
    ) {
      weeklyVolumeAdjustOut.effectiveWeeklyMi = Math.max(
        wkBaseTk,
        needWkPeakTk
      );
    }
    const wkPeakTk = roundWhole(
      (weeklyVolumeAdjustOut &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
        weeklyMi
    );
    const lrCeilTk = Math.min(
      peakLrCapMi,
      wkPeakTk > minRestTk ? wkPeakTk - minRestTk : wkPeakTk
    );
    if (lr < peakLrCapMi && lrCeilTk >= peakLrCapMi) {
      lr = peakLrCapMi;
      lrMiles = lr;
    } else if (lr < peakLrCapMi && lrCeilTk > lr) {
      lr = lrCeilTk;
      lrMiles = lr;
    }
  }

  if (
    normalizeRaceDistance(distance) === "5K" &&
    !isEasyIntensity(intensity) &&
    weekNum != null &&
    trainingWeekCount != null &&
    weekNum === trainingWeekCount &&
    !isCanonCutbackWeek
  ) {
    const wkBase5 = roundWhole(
      (weeklyVolumeAdjustOut &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
        weeklyMi
    );
    const minRest5 = Math.max(5, runSlots * 2);
    const needWkPeak5 = peakLrCapMi + minRest5;
    if (
      weeklyVolumeAdjustOut &&
      typeof weeklyVolumeAdjustOut === "object" &&
      needWkPeak5 > wkBase5
    ) {
      weeklyVolumeAdjustOut.effectiveWeeklyMi = Math.max(
        wkBase5,
        needWkPeak5
      );
    }
    const wkPeak5 = roundWhole(
      (weeklyVolumeAdjustOut &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
        weeklyMi
    );
    const lrCeil5 = Math.min(
      peakLrCapMi,
      wkPeak5 > minRest5 ? wkPeak5 - minRest5 : wkPeak5
    );
    if (lr < peakLrCapMi && lrCeil5 >= peakLrCapMi) {
      lr = peakLrCapMi;
      lrMiles = lr;
    } else if (lr < peakLrCapMi && lrCeil5 > lr) {
      lr = lrCeil5;
      lrMiles = lr;
    }
  }

  /** 5K non-Easy: primary Saturday line never below 2 mi (cutback vol squeeze could leave Math.min(lr, cap) at 1). */
  if (
    normalizeRaceDistance(distance) === "5K" &&
    !isEasyIntensity(intensity) &&
    lr > 0 &&
    lr < 2
  ) {
    lr = 2;
    lrMiles = 2;
    if (weeklyVolumeAdjustOut && typeof weeklyVolumeAdjustOut === "object") {
      const baseWk5kMinSat = roundWhole(
        (weeklyVolumeAdjustOut.effectiveWeeklyMi != null &&
          weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
          weeklyMi
      );
      if (baseWk5kMinSat < lr) {
        weeklyVolumeAdjustOut.effectiveWeeklyMi = lr;
      }
    }
  }

  if (
    weeklyVolumeAdjustOut &&
    typeof weeklyVolumeAdjustOut === "object" &&
    usesSharedModerateQualityFloorPlan(distance, intensity) &&
    !isTaper &&
    !onboardingEasyOnly &&
    runSlots >= 3
  ) {
    const nEasyMq = qualityLayout.easyDays.length;
    const marathonIntenseBump = isMarathonIntensePlan(distance, intensity);
    const qualityFracBump = marathonIntenseBump
      ? MARATHON_INTENSE_PLAN_BIAS.qualityFrac
      : 0.45;
    const maxTempoBump = maxTempoSessionMiForPlan(distance, intensity);
    /** 10 Miler / 10K Moderate build (week ≥ 2): +1 easy mi so Tue/Thu stay shorter vs Saturday LR. */
    const textureExtraEasyMi =
      (normalizeRaceDistance(distance) === "10 Miler" ||
        normalizeRaceDistance(distance) === "10K") &&
      !isCanonCutbackWeek &&
      !onboardingEasyOnly &&
      weekNum != null &&
      roundWhole(weekNum) >= 2 &&
      nEasyMq >= 2
        ? 1
        : 0;
    /** 10K Moderate cutback: extra easy pool so single-tempo cutbacks keep ≥2 mi/run (not 1 mi fillers). */
    const tenKCutbackEasyPad =
      normalizeRaceDistance(distance) === "10K" &&
      isCanonCutbackWeek &&
      nEasyMq >= 2
        ? 2
        : 0;
    /** 5K Moderate: texture + cutback pad (mirror 10K; shorter race, same easy floors). */
    const fiveKModTexture =
      normalizeRaceDistance(distance) === "5K" &&
      !isCanonCutbackWeek &&
      !onboardingEasyOnly &&
      weekNum != null &&
      roundWhole(weekNum) >= 2 &&
      nEasyMq >= 2
        ? 1
        : 0;
    const fiveKModCutbackEasyPad =
      normalizeRaceDistance(distance) === "5K" &&
      isCanonCutbackWeek &&
      nEasyMq >= 2
        ? 2
        : 0;
    const moderateEasyPad =
      textureExtraEasyMi +
      tenKCutbackEasyPad +
      fiveKModTexture +
      fiveKModCutbackEasyPad;
    const needRem =
      qualityLayout.intervalDay == null
        ? minRemainingMiForQualitySplitSingleTempoFloor(
            nEasyMq,
            MARATHON_MODERATE_MIN_TEMPO_MI,
            qualityFracBump,
            maxTempoBump,
            moderateEasyPad
          )
        : MARATHON_MODERATE_MIN_TEMPO_MI +
          MARATHON_MODERATE_MIN_INTERVAL_MI +
          nEasyMq * MODERATE_QUALITY_MIN_EASY_MI_PER_SLOT +
          moderateEasyPad;
    const needWk = roundWhole(lr) + needRem;
    const baseWk = roundWhole(
      (weeklyVolumeAdjustOut.effectiveWeeklyMi != null &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
        weeklyMi
    );
    if (baseWk < needWk) {
      weeklyVolumeAdjustOut.effectiveWeeklyMi = needWk;
    }
  }

  if (
    weeklyVolumeAdjustOut &&
    typeof weeklyVolumeAdjustOut === "object" &&
    (normalizeRaceDistance(distance) === "10 Miler" ||
      normalizeRaceDistance(distance) === "10K") &&
    !isEasyIntensity(intensity) &&
    usesChallengingIntenseNonFiveKQualityTempoFloor(distance, intensity) &&
    !usesSharedModerateQualityFloorPlan(distance, intensity) &&
    !isTaper &&
    !onboardingEasyOnly &&
    runSlots >= 3
  ) {
    const nEasy10 = qualityLayout.easyDays.length;
    const marathonIntenseBump10 = isMarathonIntensePlan(distance, intensity);
    const qualityFracBump10 = marathonIntenseBump10
      ? MARATHON_INTENSE_PLAN_BIAS.qualityFrac
      : 0.45;
    const maxTempoBump10 = maxTempoSessionMiForPlan(distance, intensity);
    const needRem10 =
      qualityLayout.intervalDay == null
        ? minRemainingMiForQualitySplitSingleTempoFloor(
            nEasy10,
            QUALITY_CHALLENGING_INTENSE_MIN_TEMPO_MI,
            qualityFracBump10,
            maxTempoBump10
          )
        : QUALITY_CHALLENGING_INTENSE_MIN_TEMPO_MI +
          MIN_INTERVAL_SESSION_MI +
          nEasy10 * MODERATE_QUALITY_MIN_EASY_MI_PER_SLOT;
    const needWk10 = roundWhole(lr) + needRem10;
    const baseWk10 = roundWhole(
      (weeklyVolumeAdjustOut.effectiveWeeklyMi != null &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
        weeklyMi
    );
    if (baseWk10 < needWk10) {
      weeklyVolumeAdjustOut.effectiveWeeklyMi = needWk10;
    }
  }

  if (
    weeklyVolumeAdjustOut &&
    typeof weeklyVolumeAdjustOut === "object" &&
    usesFiveKChallengingOrIntenseQualityTempoFloor(distance, intensity) &&
    !usesSharedModerateQualityFloorPlan(distance, intensity) &&
    !isTaper &&
    !onboardingEasyOnly &&
    runSlots >= 3
  ) {
    const nEasy5k = qualityLayout.easyDays.length;
    const fiveKCiCutbackEasyPad =
      isCanonCutbackWeek && nEasy5k >= 2
        ? normalizeIntensityLabel(intensity) === "Challenging"
          ? 0
          : 2
        : 0;
    const fiveKCiBuildTexture =
      !isCanonCutbackWeek &&
      nEasy5k >= 2 &&
      weekNum != null &&
      roundWhole(weekNum) >= 2
        ? 1
        : 0;
    const fiveKCiEasyPad = fiveKCiCutbackEasyPad + fiveKCiBuildTexture;
    const needRem5k =
      qualityLayout.intervalDay == null
        ? FIVE_K_NON_MODERATE_MIN_TEMPO_MI +
          nEasy5k * MODERATE_QUALITY_MIN_EASY_MI_PER_SLOT +
          fiveKCiEasyPad
        : FIVE_K_NON_MODERATE_MIN_TEMPO_MI +
          MIN_INTERVAL_SESSION_MI +
          nEasy5k * MODERATE_QUALITY_MIN_EASY_MI_PER_SLOT +
          fiveKCiEasyPad;
    const needWk5k = roundWhole(lr) + needRem5k;
    const baseWk5k = roundWhole(
      (weeklyVolumeAdjustOut.effectiveWeeklyMi != null &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
        weeklyMi
    );
    if (baseWk5k < needWk5k) {
      weeklyVolumeAdjustOut.effectiveWeeklyMi = needWk5k;
    }
  }

  if (isEasyIntensity(intensity)) {
    lrMiles = clampEasyIntensityRunMilesToRaceDistance(
      lrMiles,
      distance,
      intensity
    );
    lr = roundWhole(lrMiles);
  }
  lr = roundWhole(Number(lrMiles) || 0);
  lrMiles = lr;

  if (
    normalizeRaceDistance(distance) === "5K" &&
    !isTaper &&
    lr > 0 &&
    lr < 2
  ) {
    const delta = 2 - lr;
    lr = 2;
    lrMiles = 2;
    if (
      weeklyVolumeAdjustOut &&
      typeof weeklyVolumeAdjustOut === "object" &&
      (!isCanonCutbackWeek || normalizeRaceDistance(distance) === "5K")
    ) {
      const cur = roundWhole(
        weeklyVolumeAdjustOut.effectiveWeeklyMi != null
          ? weeklyVolumeAdjustOut.effectiveWeeklyMi
          : weeklyMi
      );
      weeklyVolumeAdjustOut.effectiveWeeklyMi = cur + delta;
    }
  }

  /**
   * 5K Moderate (and any non-Easy path without the Challenging/Intense needRem5k guard): cutback
   * single-tempo weeks need enough remaining miles for min tempo + ≥2 mi on each easy slot; otherwise
   * Thu-first distribution leaves Monday at Rest and Tuesday at 1 mi.
   */
  if (
    weeklyVolumeAdjustOut &&
    typeof weeklyVolumeAdjustOut === "object" &&
    normalizeRaceDistance(distance) === "5K" &&
    !isEasyIntensity(intensity) &&
    !isTaper &&
    !onboardingEasyOnly &&
    isTrainingCutbackWeek &&
    runSlots >= 3 &&
    qualityLayout.intervalDay == null
  ) {
    const nEz = qualityLayout.easyDays.length;
    const minTempo5kCb = usesSharedModerateQualityFloorPlan(distance, intensity)
      ? MARATHON_MODERATE_MIN_TEMPO_MI
      : FIVE_K_NON_MODERATE_MIN_TEMPO_MI;
    const pad5kCb =
      usesFiveKChallengingOrIntenseQualityTempoFloor(distance, intensity) &&
      nEz >= 2
        ? normalizeIntensityLabel(intensity) === "Challenging"
          ? 0
          : 2
        : 0;
    const needRem5kCb =
      minTempo5kCb + nEz * CUTBACK_EASY_RUN_MIN_MI + pad5kCb;
    const needWk5kCb = roundWhole(lr) + needRem5kCb;
    const baseWk5kCb = roundWhole(
      (weeklyVolumeAdjustOut.effectiveWeeklyMi != null &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
        weeklyMi
    );
    if (baseWk5kCb < needWk5kCb) {
      weeklyVolumeAdjustOut.effectiveWeeklyMi = needWk5kCb;
    }
  }

  let wkForEasy = roundWhole(
    (weeklyVolumeAdjustOut &&
      weeklyVolumeAdjustOut.effectiveWeeklyMi) ||
      weeklyMi
  );
  /**
   * Cutback vols are capped in resolveWeeklyMiForScheduleBuild (~80%×prior). Floor bumps must not raise
   * displayed weekly totals above that budget (otherwise cutback looks like ~5% vs prior parsed).
   */
  if (
    normalizeRaceDistance(distance) === "5K" &&
    !isEasyIntensity(intensity) &&
    isTrainingCutbackWeek
  ) {
    const cutbackWeeklyCeil = roundWhole(weeklyMi);
    if (wkForEasy > cutbackWeeklyCeil) {
      wkForEasy = cutbackWeeklyCeil;
      if (
        weeklyVolumeAdjustOut &&
        typeof weeklyVolumeAdjustOut === "object" &&
        weeklyVolumeAdjustOut.effectiveWeeklyMi != null
      ) {
        weeklyVolumeAdjustOut.effectiveWeeklyMi = Math.min(
          roundWhole(weeklyVolumeAdjustOut.effectiveWeeklyMi),
          cutbackWeeklyCeil
        );
      }
    }
    if (
      weeklyVolumeAdjustOut &&
      typeof weeklyVolumeAdjustOut === "object" &&
      isFiveKModerateFourSlotPlan(distance, intensity, runSlots) &&
      qualityLayout.intervalDay == null
    ) {
      const pad5kCb = 2;
      const needWk5kModerateCb =
        roundWhole(lr) +
        MARATHON_MODERATE_MIN_TEMPO_MI +
        qualityLayout.easyDays.length * CUTBACK_EASY_RUN_MIN_MI +
        pad5kCb;
      if (wkForEasy < needWk5kModerateCb) {
        wkForEasy = needWk5kModerateCb;
        weeklyVolumeAdjustOut.effectiveWeeklyMi = needWk5kModerateCb;
      }
    }
  }
  if (isEasyIntensity(intensity)) {
    const minEasySlotMi =
      normalizeRaceDistance(distance) === "5K"
        ? 1
        : easyRunFloorMinMiForDistance(distance) ||
          MIN_EASY_DAY_MI_EASY_INTENSITY;
    const minWkForEasyDays = lr + runSlots * minEasySlotMi;
    if (
      !isTrainingCutbackWeek &&
      minWkForEasyDays > wkForEasy &&
      weeklyVolumeAdjustOut &&
      typeof weeklyVolumeAdjustOut === "object"
    ) {
      // Headroom so easy runs can vary rather than all pinning to the floor
      // (5K keeps its 1 mi slot behavior — no buffer).
      const variationBuffer =
        easyRunFloorMinMiForDistance(distance) > 0
          ? Math.max(1, Math.ceil(runSlots / 2))
          : 0;
      wkForEasy = minWkForEasyDays + variationBuffer;
      weeklyVolumeAdjustOut.effectiveWeeklyMi = wkForEasy;
    }
  }

  let remaining = Math.max(0, wkForEasy - lr);
  sched.Saturday =
    lr <= 0
      ? "Rest"
      : formatPrimarySaturdayRunLine(distance, lr, unitPreference, intensity);

  // Onboarding or Easy intensity: only easy runs + long run (no tempo/intervals)
  if ((onboardingEasyOnly || isEasyIntensity(intensity)) && !isTaper) {
    const totalEasy = Math.max(0, remaining);
    if (isEasyIntensity(intensity)) {
      const cap = maxEasyRunMiGivenLongRun(lr, distance, intensity, null);
      const dayList = easyOnlyDays;
      const isFiveKEasyPlanFlag = isFiveKEasyPlan(distance, intensity);
      const isFiveKEasyVariedWeekdays =
        isFiveKEasyPlanFlag && !onboardingEasyOnly;
      const weekdayCap = isFiveKEasyVariedWeekdays
        ? fiveKEasyWeekdayCapForTotal(
            lr,
            totalEasy,
            dayList.length,
            distance,
            intensity
          )
        : cap;
      const easyFloor = easyRunFloorMinMiForDistance(distance);
      const useVariedEasy =
        easyFloor > 0 && !isFiveKEasyVariedWeekdays;
      let milesPerDay = isFiveKEasyVariedWeekdays
        ? distributeFiveKEasyVariedWeekdayMiles(
            totalEasy,
            dayList,
            weekdayCap,
            0
          )
        : useVariedEasy
          ? distributeTenKEasyVariedWeekdayMiles(
              totalEasy,
              dayList,
              cap,
              easyFloor
            )
          : distributeEasyMilesAcrossDays(
              totalEasy,
              dayList,
              cap,
              false,
              unitPreference
            );
      if (isFiveKEasyVariedWeekdays) {
        milesPerDay = rebalanceFiveKWalkSurplusToEarlierDays(
          dayList,
          milesPerDay,
          weekdayCap
        );
      }
      dayList.forEach((day, idx) => {
        const raw = milesPerDay[idx] ?? 0;
        const dayMi = isFiveKEasyVariedWeekdays
          ? Math.min(raw, weekdayCap)
          : Math.min(raw, cap);
        if (dayMi <= 0) {
          sched[day] = "Rest";
          return;
        }
        const distStr = formatDist(dayMi, unitPreference);
        if (isFiveKEasyPlanFlag && day === "Wednesday") {
          sched[day] = `Walk (${distStr})`;
        } else if (isFiveKEasyPlanFlag) {
          sched[day] = formatFiveKEasyEasyRunLabel(dayMi, unitPreference);
        } else {
          sched[day] = `Easy Run (${distStr})`;
        }
      });
    } else {
      const fiveKFractionalOnb = usesFiveKNonEasyFractionalEasyMi(
        distance,
        intensity
      );
      let capOnb = Math.max(totalEasy, easyOnlyDays.length * 2);
      const dOnb = normalizeRaceDistance(distance);
      if (
        !isEasyIntensity(intensity) &&
        (dOnb === "10 Miler" ||
          dOnb === "10K" ||
          dOnb === "5K" ||
          dOnb === "Half Marathon" ||
          dOnb === "Marathon")
      ) {
        const lrCapOnb = Math.max(2, roundWhole(lr));
        capOnb = Math.min(capOnb, lrCapOnb);
      }
      const floorOnb = fiveKFractionalOnb
        ? 1.5
        : easyRunFloorMinMiForDistance(distance);
      let totalEasyOb = totalEasy;
      if (floorOnb > 0 && onboardingEasyOnly && !isTaper) {
        const nEasyOb = easyOnlyDays.length;
        const minEasyTotal = nEasyOb * floorOnb;
        if (
          minEasyTotal > totalEasyOb + 1e-9 &&
          weeklyVolumeAdjustOut &&
          typeof weeklyVolumeAdjustOut === "object"
        ) {
          const needWk = roundWhole(lr) + minEasyTotal;
          wkForEasy = needWk;
          weeklyVolumeAdjustOut.effectiveWeeklyMi = needWk;
          remaining = needWk - lr;
          totalEasyOb = remaining;
        }
      }
      const milesOb = fiveKFractionalOnb
        ? distributeFiveKNonEasyVariedEasyMiles(
            totalEasyOb,
            easyOnlyDays,
            capOnb
          )
        : distributeNonEasyOnboardingWeekdayEasyMiles(
            totalEasyOb,
            easyOnlyDays,
            capOnb,
            unitPreference,
            floorOnb
          );
      const isObFiveKEasy = isFiveKEasyPlan(distance, intensity);
      easyOnlyDays.forEach((day, idx) => {
        const dayMi = milesOb[idx] ?? 0;
        if (dayMi <= 0) {
          sched[day] = "Rest";
          return;
        }
        const distStr = formatDist(dayMi, unitPreference);
        if (isObFiveKEasy && day === "Wednesday") {
          sched[day] = `Walk (${distStr})`;
        } else if (isObFiveKEasy) {
          sched[day] = formatFiveKEasyEasyRunLabel(dayMi, unitPreference);
        } else {
          sched[day] = `Easy Run (${distStr})`;
        }
      });
    }
    return sortScheduleByWeekday(
      replaceZeroDistanceWorkoutsWithRest(sched)
    );
  }

  const marathonIntense = isMarathonIntensePlan(distance, intensity);
  const b = MARATHON_INTENSE_PLAN_BIAS;
  const maxTempoCap = maxTempoSessionMiForPlan(distance, intensity);
  const qualityFrac = marathonIntense ? b.qualityFrac : 0.45;
  const tempoShareOfQ = marathonIntense ? b.tempoShareOfQuality : 0.55;
  const intervalShareOfQ = marathonIntense ? b.intervalShareOfQuality : 0.45;
  const qualityBudget = roundWhole(remaining * qualityFrac);
  let easyBudget = Math.max(0, remaining - qualityBudget);

  let tempoMiForSched = 0;
  let intervalMiForSched = 0;

  if (runSlots <= 2) {
    const tempo2 = marathonIntense ? b.qualityTempoShare2Slot : 0.6;
    const uncappedTempo = roundWhole(qualityBudget * tempo2);
    const { assigned, returnedToEasy } = capTempoRunMilesMi(
      uncappedTempo,
      distance,
      intensity
    );
    tempoMiForSched = assigned;
    easyBudget += returnedToEasy;
    const bumpTwo = bumpTempoWithEasyBudgetToChallengingIntenseFloor(
      distance,
      intensity,
      runSlots,
      tempoMiForSched,
      easyBudget
    );
    tempoMiForSched = bumpTwo.tempoMiForSched;
    easyBudget = bumpTwo.easyBudget;
    const easyCapTwoSlot = maxEasyRunMiGivenLongRun(
      lr,
      distance,
      intensity,
      cutbackEasySessionCapMi
    );
    const milesTwoSlot = ensureThursdayEasyMinimumWithRedistribution(
      distributeEasyMilesAcrossDaysRespectingThursdayFloor(
        easyBudget,
        qualityLayout.easyDays,
        easyCapTwoSlot,
        true,
        unitPreference,
        !isEasyIntensity(intensity)
      ),
      qualityLayout.easyDays,
      2
    );
    assignTempoDayIfPresent(
      sched,
      qualityLayout.tempoDay,
      tempoMiForSched,
      unitPreference
    );
    qualityLayout.easyDays.forEach((day, idx) => {
      const raw = milesTwoSlot[idx] ?? 0;
      const dayMi = capEasyRunSessionMiles(raw, easyCapTwoSlot);
      sched[day] =
        dayMi <= 0
          ? "Rest"
          : `Easy Run (${formatDist(dayMi, unitPreference)})`;
    });
  } else if (usesSharedModerateQualityFloorPlan(distance, intensity)) {
    const q = qualityBudget;
    const minT = MARATHON_MODERATE_MIN_TEMPO_MI;
    const minI = MARATHON_MODERATE_MIN_INTERVAL_MI;
    const shareSum = tempoShareOfQ + intervalShareOfQ;
    let tempoReturnedFromCap = 0;

    if (qualityLayout.intervalDay == null) {
      let tempoTarget = Math.min(maxTempoCap, Math.max(minT, q));
      const cCut = capTempoRunMilesMi(tempoTarget, distance, intensity);
      tempoMiForSched = cCut.assigned;
      let easyBudgetMq = Math.max(
        0,
        remaining - tempoMiForSched + cCut.returnedToEasy
      );
      if (tempoMiForSched < minT) {
        const need = minT - tempoMiForSched;
        const take = Math.min(need, easyBudgetMq);
        tempoMiForSched += take;
        easyBudgetMq -= take;
        const ct = capTempoRunMilesMi(tempoMiForSched, distance, intensity);
        easyBudgetMq += ct.returnedToEasy;
        tempoMiForSched = ct.assigned;
      }
      intervalMiForSched = 0;
      if (
        cutbackTempoCapMi != null &&
        tempoMiForSched > cutbackTempoCapMi
      ) {
        const freedMq = tempoMiForSched - cutbackTempoCapMi;
        tempoMiForSched = cutbackTempoCapMi;
        easyBudgetMq += freedMq;
      }
      const easyCapMq = maxEasyRunMiGivenLongRun(
        lr,
        distance,
        intensity,
        cutbackEasySessionCapMi
      );
      const shortRaceModerateBuildEasies =
        (normalizeRaceDistance(distance) === "10 Miler" ||
          normalizeRaceDistance(distance) === "10K" ||
          normalizeRaceDistance(distance) === "5K") &&
        (!isTrainingCutbackWeek ||
          isFiveKModerateFourSlotPlan(distance, intensity, runSlots));
      const fiveKFractional = usesFiveKNonEasyFractionalEasyMi(
        distance,
        intensity
      );
      const baseEasyDistMq = shortRaceModerateBuildEasies
        ? fiveKFractional
          ? distributeFiveKNonEasyVariedEasyMiles(
              easyBudgetMq,
              qualityLayout.easyDays,
              easyCapMq
            )
          : distributeEasyMilesTenMilerModerateBuildWeek(
              qualityLayout.easyDays,
              easyBudgetMq,
              easyCapMq,
              weekNum,
              unitPreference
            )
        : distributeEasyMilesAcrossDaysRespectingThursdayFloor(
            easyBudgetMq,
            qualityLayout.easyDays,
            easyCapMq,
            true,
            unitPreference,
            !isEasyIntensity(intensity)
          );
      const milesPerEasyMq = fiveKFractional
        ? baseEasyDistMq
        : ensureListedEasyDayMinimumsWithRedistribution(
            baseEasyDistMq,
            qualityLayout.easyDays,
            2
          );
      assignTempoDayIfPresent(
        sched,
        qualityLayout.tempoDay,
        tempoMiForSched,
        unitPreference
      );
      qualityLayout.easyDays.forEach((day, idx) => {
        const raw = milesPerEasyMq[idx] ?? 0;
        const dayMi = Math.min(raw, easyCapMq);
        sched[day] =
          dayMi <= 0
            ? "Rest"
            : `Easy Run (${formatDist(dayMi, unitPreference)})`;
      });
    } else if (qualityLayout.tempoDay == null) {
      intervalMiForSched = Math.max(minI, roundWhole(q));
      const intCapOnly = capIntervalRunMilesMi(
        intervalMiForSched,
        distance,
        intensity
      );
      intervalMiForSched = intCapOnly.assigned;
      tempoMiForSched = 0;
      let easyBudgetMq = Math.max(
        0,
        remaining - intervalMiForSched + intCapOnly.returnedToEasy
      );
      if (intervalMiForSched < minI) {
        const need = minI - intervalMiForSched;
        const take = Math.min(need, easyBudgetMq);
        intervalMiForSched += take;
        easyBudgetMq -= take;
      }
      const intCapFinalOnly = capIntervalRunMilesMi(
        intervalMiForSched,
        distance,
        intensity
      );
      intervalMiForSched = intCapFinalOnly.assigned;
      easyBudgetMq += intCapFinalOnly.returnedToEasy;

      const easyCapMq = maxEasyRunMiGivenLongRun(
        lr,
        distance,
        intensity,
        cutbackEasySessionCapMi
      );
      const easyDayListMq = qualityLayout.easyDays;
      const shortRaceModerateBuildEasiesInt =
        (normalizeRaceDistance(distance) === "10 Miler" ||
          normalizeRaceDistance(distance) === "10K" ||
          normalizeRaceDistance(distance) === "5K") &&
        !isTrainingCutbackWeek;
      const fiveKFractionalInt = usesFiveKNonEasyFractionalEasyMi(
        distance,
        intensity
      );
      const baseEasyDistMq = shortRaceModerateBuildEasiesInt
        ? fiveKFractionalInt
          ? distributeFiveKNonEasyVariedEasyMiles(
              easyBudgetMq,
              easyDayListMq,
              easyCapMq
            )
          : distributeEasyMilesTenMilerModerateBuildWeek(
              easyDayListMq,
              easyBudgetMq,
              easyCapMq,
              weekNum,
              unitPreference
            )
        : distributeEasyMilesAcrossDaysRespectingThursdayFloor(
            easyBudgetMq,
            easyDayListMq,
            easyCapMq,
            true,
            unitPreference,
            !isEasyIntensity(intensity)
          );
      const milesPerEasyMq = applyTuesdayThursdayPreIntervalEasyReductions(
        qualityLayout,
        baseEasyDistMq
      );
      assignTempoDayIfPresent(
        sched,
        qualityLayout.tempoDay,
        tempoMiForSched,
        unitPreference
      );
      sched[qualityLayout.intervalDay] = `Intervals (${formatDist(
        intervalMiForSched,
        unitPreference
      )} total)`;
      qualityLayout.easyDays.forEach((day, idx) => {
        const raw = milesPerEasyMq[idx] ?? 0;
        const dayMi = Math.min(raw, easyCapMq);
        sched[day] =
          dayMi <= 0
            ? "Rest"
            : `Easy Run (${formatDist(dayMi, unitPreference)})`;
      });
    } else {
    if (q >= minT + minI) {
      const spare = q - minT - minI;
      let tExtra =
        shareSum > 0
          ? roundWhole((spare * tempoShareOfQ) / shareSum)
          : roundWhole(spare * 0.55);
      let iExtra = spare - tExtra;
      tempoMiForSched = Math.min(maxTempoCap, minT + tExtra);
      intervalMiForSched = minI + iExtra;
      if (tempoMiForSched + intervalMiForSched > q) {
        let over = tempoMiForSched + intervalMiForSched - q;
        const itrim = Math.min(over, Math.max(0, intervalMiForSched - minI));
        intervalMiForSched -= itrim;
        over -= itrim;
        tempoMiForSched = Math.max(minT, tempoMiForSched - over);
      }
      const sumQ = tempoMiForSched + intervalMiForSched;
      if (sumQ < q) {
        const short = q - sumQ;
        const addT = Math.min(short, maxTempoCap - tempoMiForSched);
        tempoMiForSched += addT;
        intervalMiForSched += short - addT;
      }
    } else {
      intervalMiForSched = Math.min(minI, q);
      tempoMiForSched = Math.max(0, q - intervalMiForSched);
      const c = capTempoRunMilesMi(tempoMiForSched, distance, intensity);
      tempoMiForSched = c.assigned;
      tempoReturnedFromCap = c.returnedToEasy;
    }

    let easyBudgetMq = Math.max(
      0,
      remaining - tempoMiForSched - intervalMiForSched + tempoReturnedFromCap
    );

    if (intervalMiForSched < minI) {
      const need = minI - intervalMiForSched;
      const take = Math.min(need, easyBudgetMq);
      intervalMiForSched += take;
      easyBudgetMq -= take;
    }
    if (tempoMiForSched < minT) {
      const need = minT - tempoMiForSched;
      const take = Math.min(need, easyBudgetMq);
      tempoMiForSched += take;
      easyBudgetMq -= take;
      const ct = capTempoRunMilesMi(tempoMiForSched, distance, intensity);
      easyBudgetMq += ct.returnedToEasy;
      tempoMiForSched = ct.assigned;
    }

    const intCap = capIntervalRunMilesMi(intervalMiForSched, distance, intensity);
    intervalMiForSched = intCap.assigned;
    easyBudgetMq += intCap.returnedToEasy;

    if (intervalMiForSched < minI) {
      const need = minI - intervalMiForSched;
      const take = Math.min(need, easyBudgetMq);
      intervalMiForSched += take;
      easyBudgetMq -= take;
    }

    const intCapFinal = capIntervalRunMilesMi(intervalMiForSched, distance, intensity);
    intervalMiForSched = intCapFinal.assigned;
    easyBudgetMq += intCapFinal.returnedToEasy;

    const easyCapMq = maxEasyRunMiGivenLongRun(
      lr,
      distance,
      intensity,
      cutbackEasySessionCapMi
    );
    const easyDayListMq = qualityLayout.easyDays;
    const shortRaceModerateBuildEasiesInt =
      (normalizeRaceDistance(distance) === "10 Miler" ||
        normalizeRaceDistance(distance) === "10K" ||
        normalizeRaceDistance(distance) === "5K") &&
      !isTrainingCutbackWeek;
    const fiveKFractionalDualMq = usesFiveKNonEasyFractionalEasyMi(
      distance,
      intensity
    );
    const baseEasyDistMq = shortRaceModerateBuildEasiesInt
      ? fiveKFractionalDualMq
        ? distributeFiveKNonEasyVariedEasyMiles(
            easyBudgetMq,
            easyDayListMq,
            easyCapMq
          )
        : distributeEasyMilesTenMilerModerateBuildWeek(
            easyDayListMq,
            easyBudgetMq,
            easyCapMq,
            weekNum,
            unitPreference
          )
      : distributeEasyMilesAcrossDaysRespectingThursdayFloor(
          easyBudgetMq,
          easyDayListMq,
          easyCapMq,
          true,
          unitPreference,
          !isEasyIntensity(intensity)
        );
    const milesPerEasyMq = applyTuesdayThursdayPreIntervalEasyReductions(
      qualityLayout,
      baseEasyDistMq
    );
    assignTempoDayIfPresent(
      sched,
      qualityLayout.tempoDay,
      tempoMiForSched,
      unitPreference
    );
    sched[qualityLayout.intervalDay] = `Intervals (${formatDist(intervalMiForSched, unitPreference)} total)`;
    qualityLayout.easyDays.forEach((day, idx) => {
      const raw = milesPerEasyMq[idx] ?? 0;
      const dayMi = Math.min(raw, easyCapMq);
      sched[day] =
        dayMi <= 0
          ? "Rest"
          : `Easy Run (${formatDist(dayMi, unitPreference)})`;
    });
    }
  } else if (qualityLayout.intervalDay == null) {
    const uncappedTempo = roundWhole(qualityBudget);
    const { assigned, returnedToEasy } = capTempoRunMilesMi(
      uncappedTempo,
      distance,
      intensity
    );
    tempoMiForSched = assigned;
    let easyBudgetSingle = Math.max(
      0,
      remaining - tempoMiForSched + returnedToEasy
    );
    if (
      usesFiveKChallengingOrIntenseQualityTempoFloor(distance, intensity) &&
      runSlots >= 3 &&
      tempoMiForSched > 0 &&
      tempoMiForSched < FIVE_K_NON_MODERATE_MIN_TEMPO_MI
    ) {
      const needT = FIVE_K_NON_MODERATE_MIN_TEMPO_MI - tempoMiForSched;
      const takeT = Math.min(needT, easyBudgetSingle);
      tempoMiForSched += takeT;
      easyBudgetSingle -= takeT;
      const ct5 = capTempoRunMilesMi(tempoMiForSched, distance, intensity);
      easyBudgetSingle += ct5.returnedToEasy;
      tempoMiForSched = ct5.assigned;
    }
    const bumpSingle = bumpTempoWithEasyBudgetToChallengingIntenseFloor(
      distance,
      intensity,
      runSlots,
      tempoMiForSched,
      easyBudgetSingle
    );
    tempoMiForSched = bumpSingle.tempoMiForSched;
    easyBudgetSingle = bumpSingle.easyBudget;
    if (
      cutbackTempoCapMi != null &&
      tempoMiForSched > cutbackTempoCapMi
    ) {
      const freedSingle = tempoMiForSched - cutbackTempoCapMi;
      tempoMiForSched = cutbackTempoCapMi;
      easyBudgetSingle += freedSingle;
    }
    const easyCapGeneric = maxEasyRunMiGivenLongRun(
      lr,
      distance,
      intensity,
      cutbackEasySessionCapMi
    );
    const shortFiveKNonEasyCiBuildEasies =
      normalizeRaceDistance(distance) === "5K" &&
      !isTrainingCutbackWeek &&
      usesFiveKChallengingOrIntenseQualityTempoFloor(distance, intensity) &&
      !usesSharedModerateQualityFloorPlan(distance, intensity) &&
      trainingWeekCount != null &&
      trainingWeekCount <=
        FIVE_K_NON_EASY_ONBOARDING_WOW_PARSED_ONLY_MAX_TRAINING_WEEKS;
    const fiveKFractionalCi = usesFiveKNonEasyFractionalEasyMi(
      distance,
      intensity
    );
    const baseEasyDistSingle = shortFiveKNonEasyCiBuildEasies
      ? fiveKFractionalCi
        ? distributeFiveKNonEasyVariedEasyMiles(
            easyBudgetSingle,
            qualityLayout.easyDays,
            easyCapGeneric
          )
        : distributeEasyMilesTenMilerModerateBuildWeek(
            qualityLayout.easyDays,
            easyBudgetSingle,
            easyCapGeneric,
            weekNum,
            unitPreference
          )
      : distributeEasyMilesAcrossDaysRespectingThursdayFloor(
          easyBudgetSingle,
          qualityLayout.easyDays,
          easyCapGeneric,
          true,
          unitPreference,
          !isEasyIntensity(intensity)
        );
    const milesGeneric = fiveKFractionalCi
      ? baseEasyDistSingle
      : ensureListedEasyDayMinimumsWithRedistribution(
          baseEasyDistSingle,
          qualityLayout.easyDays,
          2
        );
    assignTempoDayIfPresent(
      sched,
      qualityLayout.tempoDay,
      tempoMiForSched,
      unitPreference
    );
    qualityLayout.easyDays.forEach((day, idx) => {
      const raw = milesGeneric[idx] ?? 0;
      const dayMi = capEasyRunSessionMiles(raw, easyCapGeneric);
      sched[day] =
        dayMi <= 0
          ? "Rest"
          : `Easy Run (${formatDist(dayMi, unitPreference)})`;
    });
  } else if (qualityLayout.tempoDay == null && qualityLayout.intervalDay != null) {
    intervalMiForSched = roundWhole(qualityBudget);
    const intervalCappedOnly = capIntervalRunMilesMi(
      intervalMiForSched,
      distance,
      intensity
    );
    intervalMiForSched = intervalCappedOnly.assigned;
    tempoMiForSched = 0;
    easyBudget = Math.max(
      0,
      remaining - intervalMiForSched + intervalCappedOnly.returnedToEasy
    );
    if (intervalMiForSched > 0 && intervalMiForSched < MIN_INTERVAL_SESSION_MI) {
      const add = MIN_INTERVAL_SESSION_MI - intervalMiForSched;
      intervalMiForSched = MIN_INTERVAL_SESSION_MI;
      easyBudget = Math.max(0, easyBudget - add);
    }
    const intCapFinalOnly = capIntervalRunMilesMi(
      intervalMiForSched,
      distance,
      intensity
    );
    intervalMiForSched = intCapFinalOnly.assigned;
    easyBudget += intCapFinalOnly.returnedToEasy;

    const easyCapGeneric = maxEasyRunMiGivenLongRun(
      lr,
      distance,
      intensity,
      cutbackEasySessionCapMi
    );
    const shortFiveKNonEasyCiBuildEasies =
      normalizeRaceDistance(distance) === "5K" &&
      !isTrainingCutbackWeek &&
      usesFiveKChallengingOrIntenseQualityTempoFloor(distance, intensity) &&
      !usesSharedModerateQualityFloorPlan(distance, intensity) &&
      trainingWeekCount != null &&
      trainingWeekCount <=
        FIVE_K_NON_EASY_ONBOARDING_WOW_PARSED_ONLY_MAX_TRAINING_WEEKS;
    const fiveKFractionalCiOnly = usesFiveKNonEasyFractionalEasyMi(
      distance,
      intensity
    );
    const baseEasyDistOnly = shortFiveKNonEasyCiBuildEasies
      ? fiveKFractionalCiOnly
        ? distributeFiveKNonEasyVariedEasyMiles(
            easyBudget,
            qualityLayout.easyDays,
            easyCapGeneric
          )
        : distributeEasyMilesTenMilerModerateBuildWeek(
            qualityLayout.easyDays,
            easyBudget,
            easyCapGeneric,
            weekNum,
            unitPreference
          )
      : distributeEasyMilesAcrossDaysRespectingThursdayFloor(
          easyBudget,
          qualityLayout.easyDays,
          easyCapGeneric,
          true,
          unitPreference,
          !isEasyIntensity(intensity)
        );
    const milesIntervalOnly = applyTuesdayThursdayPreIntervalEasyReductions(
      qualityLayout,
      baseEasyDistOnly
    );
    assignTempoDayIfPresent(
      sched,
      qualityLayout.tempoDay,
      tempoMiForSched,
      unitPreference
    );
    sched[qualityLayout.intervalDay] = `Intervals (${formatDist(
      intervalMiForSched,
      unitPreference
    )} total)`;
    qualityLayout.easyDays.forEach((day, idx) => {
      const raw = milesIntervalOnly[idx] ?? 0;
      const dayMi = capEasyRunSessionMiles(raw, easyCapGeneric);
      sched[day] =
        dayMi <= 0
          ? "Rest"
          : `Easy Run (${formatDist(dayMi, unitPreference)})`;
    });
  } else {
    const uncappedTempo = roundWhole(qualityBudget * tempoShareOfQ);
    const { assigned, returnedToEasy } = capTempoRunMilesMi(
      uncappedTempo,
      distance,
      intensity
    );
    tempoMiForSched = assigned;
    easyBudget += returnedToEasy;
    const uncappedInterval = roundWhole(qualityBudget * intervalShareOfQ);
    const intervalCapped = capIntervalRunMilesMi(
      uncappedInterval,
      distance,
      intensity
    );
    intervalMiForSched = intervalCapped.assigned;
    easyBudget += intervalCapped.returnedToEasy;
    if (intervalMiForSched > 0 && intervalMiForSched < MIN_INTERVAL_SESSION_MI) {
      const add = MIN_INTERVAL_SESSION_MI - intervalMiForSched;
      intervalMiForSched = MIN_INTERVAL_SESSION_MI;
      easyBudget = Math.max(0, easyBudget - add);
      if (easyBudget < 0) {
        const take = Math.min(
          Math.max(0, tempoMiForSched - 1),
          -easyBudget
        );
        tempoMiForSched -= take;
        easyBudget += take;
      }
    }
    if (
      usesFiveKChallengingOrIntenseQualityTempoFloor(distance, intensity) &&
      runSlots >= 3 &&
      tempoMiForSched > 0 &&
      tempoMiForSched < FIVE_K_NON_MODERATE_MIN_TEMPO_MI
    ) {
      const needT = FIVE_K_NON_MODERATE_MIN_TEMPO_MI - tempoMiForSched;
      const takeT = Math.min(needT, easyBudget);
      tempoMiForSched += takeT;
      easyBudget -= takeT;
      const ct5 = capTempoRunMilesMi(tempoMiForSched, distance, intensity);
      easyBudget += ct5.returnedToEasy;
      tempoMiForSched = ct5.assigned;
    }
    const bumpDual = bumpTempoWithEasyBudgetToChallengingIntenseFloor(
      distance,
      intensity,
      runSlots,
      tempoMiForSched,
      easyBudget
    );
    tempoMiForSched = bumpDual.tempoMiForSched;
    easyBudget = bumpDual.easyBudget;
    const easyCapGeneric = maxEasyRunMiGivenLongRun(
      lr,
      distance,
      intensity,
      cutbackEasySessionCapMi
    );
    const fiveKFractionalDual = usesFiveKNonEasyFractionalEasyMi(
      distance,
      intensity
    );
    const easyDistDual = fiveKFractionalDual
      ? distributeFiveKNonEasyVariedEasyMiles(
          easyBudget,
          qualityLayout.easyDays,
          easyCapGeneric
        )
      : distributeEasyMilesAcrossDaysRespectingThursdayFloor(
          easyBudget,
          qualityLayout.easyDays,
          easyCapGeneric,
          true,
          unitPreference,
          !isEasyIntensity(intensity)
        );
    const milesGeneric = applyTuesdayThursdayPreIntervalEasyReductions(
      qualityLayout,
      easyDistDual
    );
    assignTempoDayIfPresent(
      sched,
      qualityLayout.tempoDay,
      tempoMiForSched,
      unitPreference
    );
    sched[qualityLayout.intervalDay] = `Intervals (${formatDist(intervalMiForSched, unitPreference)} total)`;
    qualityLayout.easyDays.forEach((day, idx) => {
      const raw = milesGeneric[idx] ?? 0;
      const dayMi = capEasyRunSessionMiles(raw, easyCapGeneric);
      sched[day] =
        dayMi <= 0
          ? "Rest"
          : `Easy Run (${formatDist(dayMi, unitPreference)})`;
    });
  }

  if (
    !isTaper &&
    !onboardingEasyOnly &&
    isTenKModeratePlan(distance, intensity)
  ) {
    applyTenKModerateTuesdayEasyCapFromSchedule(
      sched,
      maxEasyRunMiGivenLongRun(
        lr,
        distance,
        intensity,
        cutbackEasySessionCapMi
      ),
      unitPreference,
      distance,
      intensity
    );
  }

  rebalanceShortFiveKCiEasyRunFloorsFromSaturday(
    sched,
    distance,
    intensity,
    trainingWeekCount,
    weekNum,
    unitPreference,
    cutbackEasySessionCapMi
  );

  return sortScheduleByWeekday(replaceZeroDistanceWorkoutsWithRest(sched));
}

/** Early-week easy in race week (mi); shakeout stays 2 days before. */
function raceWeekEarlyEasyMilesMi(distance) {
  const d = normalizeRaceDistance(distance);
  const byDist = {
    "5K": 2,
    "10K": 2,
    "10 Miler": 3,
    "Half Marathon": 3,
    Marathon: 4,
  };
  return byDist[d] ?? 2;
}

/** Shakeout length (mi): scales lightly with race-week budget but capped by distance (marathon → ~2 mi ≈ 3 km). */
function raceWeekShakeoutMilesMi(distance, weeklyBudgetMi) {
  const d = normalizeRaceDistance(distance);
  const b = Number(weeklyBudgetMi);
  const budget = Number.isFinite(b) && b > 0 ? b : 2;
  const scaled = budget / 2.5;
  const maxMiByDist = {
    Marathon: 2,
    "Half Marathon": 3,
    "10 Miler": 3,
    "10K": 2,
    "5K": 2,
  };
  const maxMi = maxMiByDist[d] ?? 3;
  return roundWhole(Math.max(1, Math.min(maxMi, scaled)));
}

/**
 * Race week: easy run 4 days before race, shakeout 2 days before, rest day before, race placeholder (raceDay).
 * All other days Rest; days after race stay Rest (see raceDay clearAfter). Shakeout scales with `weeklyBudgetMi`.
 */
function buildRaceWeekSchedule(
  weeklyBudgetMi,
  raceDateStr,
  distance,
  unitPreference,
  intensity = ""
) {
  const raceIdx = localWeekdayIndexFromString(raceDateStr);
  const sched = {};
  WEEK_ORDER.forEach((d) => {
    sched[d] = "Rest";
  });

  if (raceIdx == null) {
    sched.Sunday = "RACE DAY — set a valid race date";
    return sortScheduleByWeekday(sched);
  }

  const dayBeforeRaceIdx = (raceIdx + 6) % 7;
  const shakeoutIdx = (raceIdx + 5) % 7;
  /** Four calendar days before race (more separation from shakeout). */
  const earlyEasyIdx = (raceIdx + 3) % 7;

  const b = Number(weeklyBudgetMi);
  const budget = Number.isFinite(b) && b > 0 ? b : 2;
  const shakeMi = raceWeekShakeoutMilesMi(distance, budget);
  let earlyEasyMi = raceWeekEarlyEasyMilesMi(distance);
  if (
    isHalfMarathonEasyPlan(distance, intensity) ||
    isTenMilerEasyPlan(distance, intensity)
  ) {
    earlyEasyMi = 2;
  }

  for (let i = 0; i < 7; i++) {
    const dayName = JS_DAYS[i];
    if (i === raceIdx) {
      sched[dayName] = `${distance} — RACE DAY! (placeholder)`;
      continue;
    }
    if (i === dayBeforeRaceIdx) {
      sched[dayName] = "Rest";
      continue;
    }
    if (i === shakeoutIdx) {
      sched[dayName] = `Shake-out Run (${formatDist(shakeMi, unitPreference)})`;
      continue;
    }
    if (i === earlyEasyIdx) {
      sched[dayName] =
        isFiveKEasyPlan(distance, intensity) ||
        isTenKEasyPlan(distance, intensity) ||
        isHalfMarathonEasyPlan(distance, intensity) ||
        isTenMilerEasyPlan(distance, intensity)
          ? formatFiveKEasyEasyRunLabel(earlyEasyMi, unitPreference)
          : `Easy Run (${formatDist(earlyEasyMi, unitPreference)})`;
      continue;
    }
    sched[dayName] = "Rest";
  }

  return sortScheduleByWeekday(sched);
}

function raceDayLabel(distance, unitPreference) {
  const dist = String(distance || "").trim();
  const isKm = unitPreference === "Kilometers";
  const miles = CANONICAL_RACE_DISTANCE_MI[dist];
  if (miles == null) return `${dist} — RACE DAY! Run strong!`;
  // Keep canonical race distances (may include one decimal); do not whole-round here.
  const shown = isKm ? Number((miles * MI_TO_KM).toFixed(1)) : miles;
  const unitWord = isKm ? "km" : "miles";
  return `${shown} ${unitWord} – ${dist} – RACE DAY! Run strong!`;
}

/** Cross-training day by distance + intensity (only listed combos). */
const X_TRAIN_DAY_BY_DISTANCE_INTENSITY = {
  "5K|Easy": "Wednesday",
  "5K|Moderate": "Wednesday",
  "5K|Challenging": "Thursday",
  "5K|Intense": "Thursday",
  "10K|Easy": "Wednesday",
  "10K|Moderate": "Wednesday",
  "10K|Challenging": "Thursday",
  "10K|Intense": "Thursday",
  "10 Miler|Easy": "Wednesday",
  "10 Miler|Moderate": "Friday",
  "10 Miler|Challenging": "Thursday",
  "10 Miler|Intense": "Thursday",
  "Half Marathon|Easy": "Wednesday",
  "Half Marathon|Moderate": "Friday",
  "Half Marathon|Challenging": "Thursday",
  "Half Marathon|Intense": "Thursday",
  "Marathon|Easy": "Wednesday",
  "Marathon|Moderate": "Friday",
  "Marathon|Challenging": "Thursday",
  "Marathon|Intense": "Thursday",
};

function xTrainDayForPlan(distance, intensity) {
  const d = normalizeRaceDistance(distance);
  const inten = normalizeIntensityLabel(intensity);
  return X_TRAIN_DAY_BY_DISTANCE_INTENSITY[`${d}|${inten}`] ?? null;
}

function xTrainDurationForWeek(weekNum) {
  const w = Number(weekNum) || 0;
  if (w <= 4) return 20;
  if (w <= 8) return 30;
  return 40;
}

/**
 * Post-process generated weeks: optional cross-training on a fixed weekday.
 * Marathon Intense replaces Thursday regardless; other combos replace Rest or allowed easy/walk slots.
 */
function applyXTrainingToWeeks(weeks, distance, intensity, includeCrossTraining) {
  if (!includeCrossTraining || !Array.isArray(weeks) || weeks.length === 0) {
    return weeks;
  }

  const xTrainDay = xTrainDayForPlan(distance, intensity);
  if (!xTrainDay) return weeks;

  const d = normalizeRaceDistance(distance);
  const inten = normalizeIntensityLabel(intensity);
  const marathonIntenseReplaceRegardless =
    d === "Marathon" && inten === "Intense" && xTrainDay === "Thursday";

  return weeks.map((week) => {
    const focus = String(week?.focus ?? "").trim();
    if (focus.startsWith("Taper") || focus === "Race Week") {
      return week;
    }

    const sched = week?.schedule;
    if (!sched || typeof sched !== "object") return week;

    const current = sched[xTrainDay];
    const currentTrimmed = String(current ?? "").trim();

    const fridayEasyReplace =
      xTrainDay === "Friday" &&
      ((d === "Marathon" && inten === "Moderate") ||
        (d === "Half Marathon" && inten === "Moderate") ||
        (d === "10 Miler" && inten === "Moderate"));

    // Plans where X-Train replaces an easy run or walk on the target day
    const easyOrWalkReplace =
      currentTrimmed.startsWith("Easy Run") ||
      currentTrimmed.startsWith("Walk");

    // Only allow easy/walk replacement for specific plan combos
    const allowEasyReplace =
      // Friday Moderate plans (already handled)
      fridayEasyReplace ||
      // Easy intensity plans - Wednesday
      (xTrainDay === "Wednesday" && inten === "Easy") ||
      // Intense/Challenging plans - Thursday easy buffer
      (xTrainDay === "Thursday" &&
        (inten === "Intense" || inten === "Challenging")) ||
      // Marathon Intense Thursday (already handled)
      marathonIntenseReplaceRegardless;

    const shouldApply =
      (allowEasyReplace && easyOrWalkReplace) ||
      marathonIntenseReplaceRegardless ||
      currentTrimmed === "Rest";

    if (!shouldApply) return week;

    const duration = xTrainDurationForWeek(week?.week);
    const label = `Cross Training (${duration} min)`;

    return {
      ...week,
      schedule: {
        ...sched,
        [xTrainDay]: label,
      },
    };
  });
}

export function getPlan(formData, callback) {
  const distance = formData.distance ?? formData.raceDistance;
  const normalizedDistance = normalizeRaceDistance(
    distance?.replace(/\u00A0/g, " ")?.trim()
  );
  const rawIntensity = formData.intensity ?? formData.trainingIntensity;
  const intensity =
    rawIntensity == null || String(rawIntensity).trim() === ""
      ? "Moderate"
      : String(rawIntensity).trim();
  const raceDate = formData.raceDate;
  const unitPref = formData.unitPreference ?? formData.units ?? "Miles";

  const requiredWeeks =
    Number(formData?.planWeeks) ||
    Number(formData?.totalWeeks) ||
    computeWeeks(formData.startDate, formData.raceDate);

  if (!normalizedDistance || !requiredWeeks || requiredWeeks < 1) {
    callback?.([]);
    return [];
  }

  _marathonCalendarWeeksForFixedStart =
    normalizedDistance === "Marathon" ? requiredWeeks : null;
  try {
  const effectiveStart = getFixedStartWeeklyMiles(
    normalizedDistance,
    intensity
  );

  const daysPerWeek = getDaysPerWeek(intensity, normalizedDistance);
  const longRunPct = getLongRunPct(normalizedDistance);
  const marathonPeakLrMi =
    normalizedDistance === "Marathon"
      ? (() => {
          const tableLr = getMarathonPeakLongRunTargetMi(intensity);
          if (!isEasyIntensity(intensity)) return tableLr;
          const lrFrom40MpW = roundWhole(40 * longRunPct);
          return Math.min(tableLr, lrFrom40MpW);
        })()
      : null;

  const halfMarathonPeakLrMi =
    normalizedDistance === "Half Marathon"
      ? getPeakLongRunTargetMiForIntensity("Half Marathon", intensity)
      : null;

  const tenMilerPeakLrMi =
    normalizedDistance === "10 Miler"
      ? getPeakLongRunTargetMiForIntensity("10 Miler", intensity)
      : null;

  const tenMilerMinPeakWeeklyVol =
    normalizedDistance === "10 Miler" && !isEasyIntensity(intensity)
      ? roundWhole(
          weeklyMilesForLongRunTarget(
            Math.max(2, roundWhole(Number(tenMilerPeakLrMi) || 8)),
            longRunPct
          )
        )
      : null;

  const tenKPeakLrMi =
    normalizedDistance === "10K"
      ? getPeakLongRunTargetMiForIntensity("10K", intensity)
      : null;

  const tenKMinPeakWeeklyVol =
    normalizedDistance === "10K" && !isEasyIntensity(intensity)
      ? roundWhole(
          weeklyMilesForLongRunTarget(
            Math.max(2, roundWhole(Number(tenKPeakLrMi) || 6)),
            longRunPct
          )
        )
      : null;

  const fiveKPeakLrMi =
    normalizedDistance === "5K"
      ? getPeakLongRunTargetMiForIntensity("5K", intensity)
      : null;

  const fiveKMinPeakWeeklyVol =
    normalizedDistance === "5K" && !isEasyIntensity(intensity)
      ? roundWhole(
          weeklyMilesForLongRunTarget(
            Math.max(2, roundWhole(Number(fiveKPeakLrMi) || 4)),
            longRunPct
          )
        )
      : null;

  const peakLrMiForCanonicalPlan =
    marathonPeakLrMi ??
    halfMarathonPeakLrMi ??
    tenMilerPeakLrMi ??
    tenKPeakLrMi ??
    fiveKPeakLrMi ??
    null;

  const minMarathonPeakTrainingVol =
    normalizedDistance === "Marathon"
      ? marathonMinPeakTrainingWeekVolMi(
          Math.max(4, roundWhole(Number(marathonPeakLrMi) || 20)),
          longRunPct,
          normalizedDistance,
          intensity
        )
      : 1;

  // Non-Marathon: always 1 taper + race week. Marathon: 2 taper weeks when plan > 4 weeks, else 1.
  const isMarathonPlan = normalizedDistance === "Marathon";
  const useSingleTaper = !isMarathonPlan || requiredWeeks <= 4;
  const trainingWeekCount = useSingleTaper
    ? Math.max(0, requiredWeeks - 2)
    : Math.max(1, requiredWeeks - 3);

  _tenKShortNonEasyCutbackFrac =
    normalizedDistance === "10K" &&
    !isEasyIntensity(intensity) &&
    trainingWeekCount >= 1 &&
    trainingWeekCount <= 6
      ? 0.92
      : null;

  const isFiveKEasyUnified =
    normalizedDistance === "5K" && isEasyIntensity(intensity);
  const isTenKEasyUnified =
    normalizedDistance === "10K" && isEasyIntensity(intensity);
  const isHalfMarathonEasyUnified =
    normalizedDistance === "Half Marathon" && isEasyIntensity(intensity);
  const isTenMilerEasyUnified =
    normalizedDistance === "10 Miler" && isEasyIntensity(intensity);

  const peakWeeklyMiles = computePeakWeeklyMiles(
    effectiveStart,
    normalizedDistance,
    longRunPct,
    intensity
  );
  let vols;
  if (isFiveKEasyUnified) {
    vols = buildFiveKEasyWeeklyVolumes(trainingWeekCount);
  } else if (isTenKEasyUnified) {
    vols = buildTenKEasyWeeklyVolumes(trainingWeekCount);
  } else if (isHalfMarathonEasyUnified) {
    vols = buildHalfMarathonEasyWeeklyVolumes(trainingWeekCount);
  } else if (isTenMilerEasyUnified) {
    vols = buildTenMilerEasyWeeklyVolumes(trainingWeekCount);
  } else {
    vols = buildGoalDrivenVolumes(
      trainingWeekCount,
      effectiveStart,
      peakWeeklyMiles,
      _tenKShortNonEasyCutbackFrac ?? CUTBACK_TOTAL_MILEAGE_FRAC
    );
    if (normalizedDistance === "Marathon") {
      vols = applyMarathonPreTaperLongRunTargets(
        vols,
        longRunPct,
        marathonPeakLrMi,
        normalizedDistance,
        intensity
      );
    } else if (
      normalizedDistance === "Half Marathon" &&
      !isHalfMarathonEasyUnified
    ) {
      vols = applyHalfMarathonPreTaperLongRunTargets(
        vols,
        longRunPct,
        halfMarathonPeakLrMi
      );
    } else if (
      normalizedDistance === "10 Miler" &&
      !isTenMilerEasyUnified
    ) {
      vols = applyTenMilerPreTaperLongRunTargets(
        vols,
        longRunPct,
        tenMilerPeakLrMi
      );
    } else if (normalizedDistance === "10K") {
      vols = applyTenKPreTaperLongRunTargets(
        vols,
        longRunPct,
        tenKPeakLrMi
      );
    } else if (normalizedDistance === "5K") {
      vols = applyFiveKPreTaperLongRunTargets(
        vols,
        longRunPct,
        fiveKPeakLrMi
      );
    }
  }
  if (
    !isFiveKEasyUnified &&
    !isTenKEasyUnified &&
    !isHalfMarathonEasyUnified &&
    !isTenMilerEasyUnified
  ) {
    vols = enforceOnboardingLowerThanWeek2(
      vols,
      normalizedDistance,
      longRunPct,
      peakLrMiForCanonicalPlan,
      effectiveStart
    );
  }

  if (
    _tenKShortNonEasyCutbackFrac != null &&
    !isFiveKEasyUnified &&
    !isTenKEasyUnified
  ) {
    const Tv = vols.length;
    const f = Number(_tenKShortNonEasyCutbackFrac);
    const frac =
      Number.isFinite(f) && f > 0 && f < 1 ? f : CUTBACK_TOTAL_MILEAGE_FRAC;
    for (let i = 1; i < Tv; i++) {
      if (!isCutbackWeek(i + 1, Tv)) continue;
      const prevV = roundWhole(vols[i - 1]);
      const minCut = Math.max(1, roundWhole(prevV * frac));
      if (roundWhole(vols[i]) < minCut) {
        vols[i] = minCut;
      }
    }
  }

  if (
    !isFiveKEasyUnified &&
    !isTenKEasyUnified &&
    !isHalfMarathonEasyUnified &&
    !isTenMilerEasyUnified &&
    (normalizedDistance === "Marathon" ||
      normalizedDistance === "Half Marathon" ||
      normalizedDistance === "10 Miler" ||
      normalizedDistance === "10K" ||
      normalizedDistance === "5K")
  ) {
    const es = roundWhole(effectiveStart);
    const Tv = vols.length;
    for (let i = 0; i < Tv; i++) {
      if (!isCutbackWeek(i + 1, Tv)) {
        vols[i] = Math.max(roundWhole(vols[i]), es);
      }
    }
  }

  const plan = [];

  for (let i = 0; i < trainingWeekCount; i++) {
    const weekNum = i + 1;
    const wmi = resolveWeeklyMiForScheduleBuild(
      plan,
      vols,
      i,
      trainingWeekCount,
      normalizedDistance
    );
    vols[i] = wmi;
    let focus = "Build";
    let label = `Build ${weekNum}`;
    if (trainingWeekCount > 1 && weekNum === 1) {
      focus = "Onboarding";
      label = "Onboarding";
    } else if (isCutbackWeek(weekNum, trainingWeekCount)) {
      focus = "Cutback";
      label = "Cutback";
    } else if (weekNum === trainingWeekCount) {
      focus = "Peak";
      label = "Peak";
    }

    const cutbackPrevLr = cutbackPrevLongRunMilesFromPlan(
      plan,
      vols,
      i,
      trainingWeekCount,
      unitPref,
      longRunPct
    );
    const prevWeekLr =
      i > 0
        ? parseLongRunMilesFromAnyDay(plan[i - 1]?.schedule)
        : null;
    const priorNcLr =
      i > 0
        ? priorNonCutbackLongRunMiFromPlan(plan, i, trainingWeekCount)
        : null;
    const priorBuildTempoMi =
      i > 0
        ? priorNonCutbackBuildWeekTempoMiFromPlan(plan, i, trainingWeekCount)
        : null;
    const cutbackEasySessionCapMi =
      i > 0 &&
      isCutbackWeek(weekNum, trainingWeekCount) &&
      !isEasyIntensity(intensity)
        ? cutbackEasySessionCapFromPriorWeekSchedule(
            plan[i - 1]?.schedule,
            normalizedDistance
          )
        : null;
    const volAdjust =
      isFiveKEasyUnified ||
      isTenKEasyUnified ||
      isHalfMarathonEasyUnified ||
      isTenMilerEasyUnified
        ? null
        : isEasyIntensity(intensity) ||
            usesCanonicalLongRunSchedule(normalizedDistance)
          ? {}
          : null;
    const sched = isFiveKEasyUnified
      ? buildFiveKEasyTrainingWeekSchedule(
          wmi,
          weekNum,
          trainingWeekCount,
          unitPref,
          weekNum === 1
        )
      : isTenKEasyUnified
        ? buildTenKEasyTrainingWeekSchedule(
            wmi,
            weekNum,
            trainingWeekCount,
            unitPref,
            weekNum === 1
          )
        : isHalfMarathonEasyUnified
          ? buildHalfMarathonEasyTrainingWeekSchedule(
              wmi,
              weekNum,
              trainingWeekCount,
              unitPref,
              weekNum === 1
            )
          : isTenMilerEasyUnified
            ? buildTenMilerEasyTrainingWeekSchedule(
                wmi,
                weekNum,
                trainingWeekCount,
                unitPref,
                weekNum === 1
              )
        : buildTrainingWeek(
            wmi,
            daysPerWeek,
            normalizedDistance,
            unitPref,
            longRunPct,
            false,
            weekNum === 1,
            cutbackPrevLr,
            intensity,
            weekNum,
            trainingWeekCount,
            prevWeekLr,
            volAdjust,
            priorNcLr,
            priorBuildTempoMi,
            cutbackEasySessionCapMi
          );
    const tm = computeWeekMileage(sched, unitPref);
    if (
      !isFiveKEasyUnified &&
      !isTenKEasyUnified &&
      !isHalfMarathonEasyUnified &&
      !isTenMilerEasyUnified &&
      volAdjust &&
      volAdjust.effectiveWeeklyMi != null &&
      volAdjust.effectiveWeeklyMi > roundWhole(wmi) &&
      (!isCutbackWeek(weekNum, trainingWeekCount) ||
        (normalizedDistance === "5K" && !isEasyIntensity(intensity)))
    ) {
      let vUp = roundWhole(volAdjust.effectiveWeeklyMi);
      if (
        normalizedDistance === "5K" &&
        !isEasyIntensity(intensity) &&
        !isCutbackWeek(weekNum, trainingWeekCount)
      ) {
        vUp = Math.min(vUp, Math.max(roundWhole(wmi), tm));
      }
      vols[i] = vUp;
    }
    if (
      !isFiveKEasyUnified &&
      !isTenKEasyUnified &&
      !isHalfMarathonEasyUnified &&
      !isTenMilerEasyUnified &&
      isCutbackWeek(weekNum, trainingWeekCount) &&
      !isEasyIntensity(intensity) &&
      daysPerWeek >= 4
    ) {
      vols[i] = Math.min(roundWhole(vols[i]), Math.max(1, tm));
    }
    plan.push({
      week: weekNum,
      focus,
      label,
      schedule: sched,
      totalMileage: tm,
      totalMileageLabel: `${tm} ${unitPref === "Kilometers" ? "km" : "miles"}`,
    });

    // totalMileage is parsed from workout strings, not raw vols[i] — onboarding used to
    // inflate easy miles (Math.max(2, rawPer) per day). Ensure week 1 display < week 2.
    if (
      !isFiveKEasyUnified &&
      !isTenKEasyUnified &&
      !isHalfMarathonEasyUnified &&
      !isTenMilerEasyUnified &&
      trainingWeekCount > 1 &&
      weekNum === 2 &&
      vols[0] <= vols[1]
    ) {
      let tm0 = plan[0].totalMileage;
      let tm1 = plan[1].totalMileage;
      let minVols0 = 1;
      if (normalizedDistance === "Marathon" && trainingWeekCount === 2) {
        minVols0 = weeklyMilesForLongRunTarget(
          Math.max(4, marathonPeakLrMi - 1),
          longRunPct
        );
      } else if (
        normalizedDistance === "Half Marathon" &&
        trainingWeekCount === 2 &&
        halfMarathonPeakLrMi != null
      ) {
        minVols0 = weeklyMilesForLongRunTarget(
          Math.max(3, halfMarathonPeakLrMi - 1),
          longRunPct
        );
      } else if (
        normalizedDistance === "10 Miler" &&
        trainingWeekCount === 2 &&
        tenMilerPeakLrMi != null
      ) {
        minVols0 = weeklyMilesForLongRunTarget(
          Math.max(2, tenMilerPeakLrMi - 1),
          longRunPct
        );
      } else if (
        normalizedDistance === "10K" &&
        trainingWeekCount === 2 &&
        tenKPeakLrMi != null
      ) {
        minVols0 = weeklyMilesForLongRunTarget(
          Math.max(2, tenKPeakLrMi - 1),
          longRunPct
        );
      } else if (
        normalizedDistance === "5K" &&
        trainingWeekCount === 2 &&
        fiveKPeakLrMi != null
      ) {
        minVols0 = weeklyMilesForLongRunTarget(
          Math.max(2, fiveKPeakLrMi - 1),
          longRunPct
        );
      }
      if (
        normalizedDistance === "Marathon" ||
        normalizedDistance === "Half Marathon" ||
        normalizedDistance === "10 Miler" ||
        normalizedDistance === "10K" ||
        normalizedDistance === "5K"
      ) {
        minVols0 = Math.max(minVols0, roundWhole(effectiveStart));
      }
      let guardRaise = 0;
      while (tm0 >= tm1 && vols[1] < 450 && guardRaise < 60) {
        vols[1] += 1;
        rebuildTrainingWeekPlanEntry(
          plan,
          vols,
          1,
          trainingWeekCount,
          daysPerWeek,
          normalizedDistance,
          unitPref,
          longRunPct,
          intensity
        );
        tm1 = plan[1].totalMileage;
        guardRaise += 1;
      }
      let guard = 0;
      while (tm0 >= tm1 && vols[0] > minVols0 && guard < 60) {
        vols[0] -= 1;
        const w0 = roundWhole(vols[0]);
        const sched0 = buildTrainingWeek(
          w0,
          daysPerWeek,
          normalizedDistance,
          unitPref,
          longRunPct,
          false,
          true,
          null,
          intensity,
          1,
          trainingWeekCount,
          null,
          null,
          null,
          null,
          null
        );
        tm0 = computeWeekMileage(sched0, unitPref);
        plan[0] = {
          ...plan[0],
          schedule: sched0,
          totalMileage: tm0,
          totalMileageLabel: `${tm0} ${unitPref === "Kilometers" ? "km" : "miles"}`,
        };
        guard += 1;
      }
    }
  }

  if (
    trainingWeekCount >= 1 &&
    !isFiveKEasyUnified &&
    !isTenKEasyUnified &&
    !isHalfMarathonEasyUnified &&
    !isTenMilerEasyUnified
  ) {
    const parsedWowCapOpts =
      normalizedDistance === "Marathon"
        ? {
            marathonPeakLrMi,
            intensity,
            marathonNonCutbackFloorMi: effectiveStart,
            lastWeekFloor: minMarathonPeakTrainingVol,
          }
        : tenMilerMinPeakWeeklyVol != null
          ? { intensity, lastWeekFloor: tenMilerMinPeakWeeklyVol }
          : tenKMinPeakWeeklyVol != null
            ? { intensity, lastWeekFloor: tenKMinPeakWeeklyVol }
            : fiveKMinPeakWeeklyVol != null
              ? { intensity, lastWeekFloor: fiveKMinPeakWeeklyVol }
              : { intensity };
    enforceParsedWeekOverWeekCap(
      plan,
      vols,
      trainingWeekCount,
      normalizedDistance,
      daysPerWeek,
      unitPref,
      longRunPct,
      parsedWowCapOpts
    );
    if (normalizedDistance === "Marathon" && trainingWeekCount >= 2) {
      reconcileMarathonWowCutbackPeak(
        plan,
        vols,
        trainingWeekCount,
        normalizedDistance,
        daysPerWeek,
        unitPref,
        longRunPct,
        marathonPeakLrMi,
        intensity,
        effectiveStart
      );
      enforceCutbackStrictlyBelowPriorWeek(
        plan,
        vols,
        trainingWeekCount,
        normalizedDistance,
        daysPerWeek,
        unitPref,
        longRunPct,
        intensity
      );
    } else {
      enforceCutbackStrictlyBelowPriorWeek(
        plan,
        vols,
        trainingWeekCount,
        normalizedDistance,
        daysPerWeek,
        unitPref,
        longRunPct,
        intensity
      );
      enforceParsedWeekOverWeekCap(
        plan,
        vols,
        trainingWeekCount,
        normalizedDistance,
        daysPerWeek,
        unitPref,
        longRunPct,
        parsedWowCapOpts
      );
      enforceCutbackStrictlyBelowPriorWeek(
        plan,
        vols,
        trainingWeekCount,
        normalizedDistance,
        daysPerWeek,
        unitPref,
        longRunPct,
        intensity
      );
    }
  }

  if (
    trainingWeekCount > 1 &&
    vols[0] <= vols[1] &&
    !isFiveKEasyUnified &&
    !isTenKEasyUnified &&
    !isHalfMarathonEasyUnified &&
    !isTenMilerEasyUnified
  ) {
    let tm0 = plan[0].totalMileage;
    let tm1 = plan[1].totalMileage;
    let minVols0 = 1;
    if (normalizedDistance === "Marathon" && trainingWeekCount === 2) {
      minVols0 = weeklyMilesForLongRunTarget(
        Math.max(4, marathonPeakLrMi - 1),
        longRunPct
      );
    } else if (
      normalizedDistance === "Half Marathon" &&
      trainingWeekCount === 2 &&
      halfMarathonPeakLrMi != null
    ) {
      minVols0 = weeklyMilesForLongRunTarget(
        Math.max(3, halfMarathonPeakLrMi - 1),
        longRunPct
      );
    } else if (
      normalizedDistance === "10 Miler" &&
      trainingWeekCount === 2 &&
      tenMilerPeakLrMi != null
    ) {
      minVols0 = weeklyMilesForLongRunTarget(
        Math.max(2, tenMilerPeakLrMi - 1),
        longRunPct
      );
    } else if (
      normalizedDistance === "10K" &&
      trainingWeekCount === 2 &&
      tenKPeakLrMi != null
    ) {
      minVols0 = weeklyMilesForLongRunTarget(
        Math.max(2, tenKPeakLrMi - 1),
        longRunPct
      );
    } else if (
      normalizedDistance === "5K" &&
      trainingWeekCount === 2 &&
      fiveKPeakLrMi != null
    ) {
      minVols0 = weeklyMilesForLongRunTarget(
        Math.max(2, fiveKPeakLrMi - 1),
        longRunPct
      );
    }
    if (
      normalizedDistance === "Marathon" ||
      normalizedDistance === "Half Marathon" ||
      normalizedDistance === "10 Miler" ||
      normalizedDistance === "10K" ||
      normalizedDistance === "5K"
    ) {
      minVols0 = Math.max(minVols0, roundWhole(effectiveStart));
    }
    let guardRaise = 0;
    while (tm0 >= tm1 && vols[1] < 450 && guardRaise < 60) {
      vols[1] += 1;
      rebuildTrainingWeekPlanEntry(
        plan,
        vols,
        1,
        trainingWeekCount,
        daysPerWeek,
        normalizedDistance,
        unitPref,
        longRunPct,
        intensity
      );
      tm1 = plan[1].totalMileage;
      guardRaise += 1;
    }
    let guard = 0;
    while (tm0 >= tm1 && vols[0] > minVols0 && guard < 60) {
      vols[0] -= 1;
      const w0 = roundWhole(vols[0]);
      const sched0 = buildTrainingWeek(
        w0,
        daysPerWeek,
        normalizedDistance,
        unitPref,
        longRunPct,
        false,
        true,
        null,
        intensity,
        1,
        trainingWeekCount,
        null,
        null,
        null,
        null,
        null
      );
      tm0 = computeWeekMileage(sched0, unitPref);
      plan[0] = {
        ...plan[0],
        schedule: sched0,
        totalMileage: tm0,
        totalMileageLabel: `${tm0} ${unitPref === "Kilometers" ? "km" : "miles"}`,
      };
      guard += 1;
    }
  }

  if (
    trainingWeekCount > 1 &&
    normalizedDistance === "5K" &&
    !isEasyIntensity(intensity) &&
    !isFiveKEasyUnified
  ) {
    const parsedWowCapOpts5kRe =
      fiveKMinPeakWeeklyVol != null
        ? { intensity, lastWeekFloor: fiveKMinPeakWeeklyVol }
        : { intensity };
    const runFiveKWowCutbackPair = () => {
      enforceParsedWeekOverWeekCap(
        plan,
        vols,
        trainingWeekCount,
        normalizedDistance,
        daysPerWeek,
        unitPref,
        longRunPct,
        parsedWowCapOpts5kRe
      );
      enforceCutbackStrictlyBelowPriorWeek(
        plan,
        vols,
        trainingWeekCount,
        normalizedDistance,
        daysPerWeek,
        unitPref,
        longRunPct,
        intensity
      );
      enforceParsedWeekOverWeekCap(
        plan,
        vols,
        trainingWeekCount,
        normalizedDistance,
        daysPerWeek,
        unitPref,
        longRunPct,
        parsedWowCapOpts5kRe
      );
      enforceCutbackStrictlyBelowPriorWeek(
        plan,
        vols,
        trainingWeekCount,
        normalizedDistance,
        daysPerWeek,
        unitPref,
        longRunPct,
        intensity
      );
    };
    runFiveKWowCutbackPair();
    const fiveKWowSlackMi = 1;
    let bumpWeekOneGuard = 0;
    while (bumpWeekOneGuard < 120) {
      bumpWeekOneGuard += 1;
      const capW2 = marathonParsedWowCapForWeekAfterEdge(
        plan,
        1,
        trainingWeekCount,
        vols,
        normalizedDistance,
        intensity
      );
      if (
        plan[1].totalMileage <= capW2 + fiveKWowSlackMi ||
        !Number.isFinite(capW2)
      ) {
        break;
      }
      const v0 = roundWhole(vols[0]);
      if (v0 >= 450) break;
      if (plan[0].totalMileage >= plan[1].totalMileage) break;
      vols[0] = v0 + 1;
      rebuildTrainingWeekPlanEntry(
        plan,
        vols,
        0,
        trainingWeekCount,
        daysPerWeek,
        normalizedDistance,
        unitPref,
        longRunPct,
        intensity
      );
      rebuildTrainingWeekPlanEntry(
        plan,
        vols,
        1,
        trainingWeekCount,
        daysPerWeek,
        normalizedDistance,
        unitPref,
        longRunPct,
        intensity
      );
      runFiveKWowCutbackPair();
    }
  }

  const enforceMarathonMidBuildVolMonotone =
    trainingWeekCount >= 2 && normalizedDistance === "Marathon";
  const enforceTenMilerMidBuildVolMonotone =
    trainingWeekCount >= 2 &&
    normalizedDistance === "10 Miler" &&
    tenMilerMinPeakWeeklyVol != null;
  const enforceTenKMidBuildVolMonotone =
    trainingWeekCount >= 2 &&
    normalizedDistance === "10K" &&
    tenKMinPeakWeeklyVol != null;
  const enforceFiveKMidBuildVolMonotone =
    trainingWeekCount >= 2 &&
    normalizedDistance === "5K" &&
    fiveKMinPeakWeeklyVol != null;
  if (enforceMarathonMidBuildVolMonotone) {
    const volMonotoneBumped = enforceNonCutbackAdjacentVolsNonDecreasing(
      vols,
      trainingWeekCount
    );
    if (volMonotoneBumped) {
      for (let k = 0; k < trainingWeekCount; k++) {
        rebuildTrainingWeekPlanEntry(
          plan,
          vols,
          k,
          trainingWeekCount,
          daysPerWeek,
          normalizedDistance,
          unitPref,
          longRunPct,
          intensity
        );
      }
      if (trainingWeekCount >= 1) {
        enforceParsedWeekOverWeekCap(
          plan,
          vols,
          trainingWeekCount,
          normalizedDistance,
          daysPerWeek,
          unitPref,
          longRunPct,
          {
            marathonPeakLrMi,
            intensity,
            marathonNonCutbackFloorMi: effectiveStart,
            lastWeekFloor: minMarathonPeakTrainingVol,
          }
        );
        if (trainingWeekCount >= 2) {
          reconcileMarathonWowCutbackPeak(
            plan,
            vols,
            trainingWeekCount,
            normalizedDistance,
            daysPerWeek,
            unitPref,
            longRunPct,
            marathonPeakLrMi,
            intensity,
            effectiveStart
          );
          enforceCutbackStrictlyBelowPriorWeek(
            plan,
            vols,
            trainingWeekCount,
            normalizedDistance,
            daysPerWeek,
            unitPref,
            longRunPct,
            intensity
          );
        }
      }
    }
  } else if (
    enforceTenMilerMidBuildVolMonotone &&
    !isFiveKEasyUnified &&
    !isTenKEasyUnified &&
    !isHalfMarathonEasyUnified &&
    !isTenMilerEasyUnified
  ) {
    const volMonotoneBumpedTm = enforceNonCutbackAdjacentVolsNonDecreasing(
      vols,
      trainingWeekCount
    );
    if (volMonotoneBumpedTm) {
      for (let k = 0; k < trainingWeekCount; k++) {
        rebuildTrainingWeekPlanEntry(
          plan,
          vols,
          k,
          trainingWeekCount,
          daysPerWeek,
          normalizedDistance,
          unitPref,
          longRunPct,
          intensity
        );
      }
      if (trainingWeekCount >= 1) {
        enforceParsedWeekOverWeekCap(
          plan,
          vols,
          trainingWeekCount,
          normalizedDistance,
          daysPerWeek,
          unitPref,
          longRunPct,
          {
            intensity,
            lastWeekFloor: tenMilerMinPeakWeeklyVol,
          }
        );
        enforceCutbackStrictlyBelowPriorWeek(
          plan,
          vols,
          trainingWeekCount,
          normalizedDistance,
          daysPerWeek,
          unitPref,
          longRunPct,
          intensity
        );
      }
    }
  } else if (
    enforceTenKMidBuildVolMonotone &&
    !isFiveKEasyUnified &&
    !isTenKEasyUnified &&
    !isHalfMarathonEasyUnified &&
    !isTenMilerEasyUnified
  ) {
    const volMonotoneBumpedTk = enforceNonCutbackAdjacentVolsNonDecreasing(
      vols,
      trainingWeekCount
    );
    if (volMonotoneBumpedTk) {
      for (let k = 0; k < trainingWeekCount; k++) {
        rebuildTrainingWeekPlanEntry(
          plan,
          vols,
          k,
          trainingWeekCount,
          daysPerWeek,
          normalizedDistance,
          unitPref,
          longRunPct,
          intensity
        );
      }
      if (trainingWeekCount >= 1) {
        enforceParsedWeekOverWeekCap(
          plan,
          vols,
          trainingWeekCount,
          normalizedDistance,
          daysPerWeek,
          unitPref,
          longRunPct,
          {
            intensity,
            lastWeekFloor: tenKMinPeakWeeklyVol,
          }
        );
        enforceCutbackStrictlyBelowPriorWeek(
          plan,
          vols,
          trainingWeekCount,
          normalizedDistance,
          daysPerWeek,
          unitPref,
          longRunPct,
          intensity
        );
      }
    }
  } else if (
    enforceFiveKMidBuildVolMonotone &&
    !isFiveKEasyUnified &&
    !isTenKEasyUnified &&
    !isHalfMarathonEasyUnified &&
    !isTenMilerEasyUnified
  ) {
    const volMonotoneBumped5k = enforceNonCutbackAdjacentVolsNonDecreasing(
      vols,
      trainingWeekCount
    );
    if (volMonotoneBumped5k) {
      for (let k = 0; k < trainingWeekCount; k++) {
        rebuildTrainingWeekPlanEntry(
          plan,
          vols,
          k,
          trainingWeekCount,
          daysPerWeek,
          normalizedDistance,
          unitPref,
          longRunPct,
          intensity
        );
      }
      if (trainingWeekCount >= 1) {
        enforceParsedWeekOverWeekCap(
          plan,
          vols,
          trainingWeekCount,
          normalizedDistance,
          daysPerWeek,
          unitPref,
          longRunPct,
          {
            intensity,
            lastWeekFloor: fiveKMinPeakWeeklyVol,
          }
        );
        enforceCutbackStrictlyBelowPriorWeek(
          plan,
          vols,
          trainingWeekCount,
          normalizedDistance,
          daysPerWeek,
          unitPref,
          longRunPct,
          intensity
        );
      }
    }
  }

  const peakVolForRace = vols.length ? vols[vols.length - 1] : peakWeeklyMiles;
  /** Always use internal weekly miles for taper — never plan[].totalMileage (km/mi display sum). */
  const peakMiForTaper = Math.max(
    1,
    roundWhole(
      trainingWeekCount >= 1 && vols[trainingWeekCount - 1] != null
        ? vols[trainingWeekCount - 1]
        : peakVolForRace
    )
  );
  let taper2Vol = Math.max(1, roundWhole(peakMiForTaper * TAPER_WEEK_RETAIN));
  let taper1Vol = Math.max(1, roundWhole(taper2Vol * TAPER_WEEK_RETAIN));
  let singleTaperVol = Math.max(
    1,
    roundWhole(peakMiForTaper * TAPER_WEEK_RETAIN)
  );
  if (isTenKEasyUnified) {
    const tenKF = Math.max(
      1,
      roundWhole(peakMiForTaper * TEN_K_EASY_TAPER_MIN_FRAC_OF_PEAK)
    );
    taper2Vol = Math.max(tenKF, taper2Vol);
    taper1Vol = Math.max(tenKF, taper1Vol);
    singleTaperVol = Math.max(tenKF, singleTaperVol);
  }
  if (isHalfMarathonEasyUnified) {
    const halfF = Math.max(
      1,
      roundWhole(peakMiForTaper * HALF_MARATHON_EASY_TAPER_MIN_FRAC_OF_PEAK)
    );
    taper2Vol = Math.max(halfF, taper2Vol);
    taper1Vol = Math.max(halfF, taper1Vol);
    singleTaperVol = Math.max(halfF, singleTaperVol);
  }
  if (
    useSingleTaper &&
    normalizedDistance === "Half Marathon" &&
    !isHalfMarathonEasyUnified
  ) {
    singleTaperVol = Math.max(
      singleTaperVol,
      HALF_MARATHON_NON_EASY_TAPER_MIN_WEEKLY_MI
    );
  }
  if (
    useSingleTaper &&
    normalizedDistance === "10 Miler" &&
    !isTenMilerEasyUnified
  ) {
    singleTaperVol = Math.max(
      singleTaperVol,
      TEN_MILER_NON_EASY_TAPER_MIN_WEEKLY_MI
    );
  }
  if (
    useSingleTaper &&
    normalizedDistance === "10K" &&
    !isTenKEasyUnified
  ) {
    singleTaperVol = Math.max(
      singleTaperVol,
      TEN_K_NON_EASY_TAPER_MIN_WEEKLY_MI
    );
  }
  if (isTenMilerEasyUnified) {
    const tmF = Math.max(
      1,
      roundWhole(peakMiForTaper * TEN_MILER_EASY_TAPER_MIN_FRAC_OF_PEAK)
    );
    taper2Vol = Math.max(tmF, taper2Vol);
    taper1Vol = Math.max(tmF, taper1Vol);
    singleTaperVol = Math.max(tmF, singleTaperVol);
  }
  const raceWeekBudget = computeRaceWeekBudget(
    peakVolForRace,
    normalizedDistance,
    intensity
  );

  if (useSingleTaper) {
    const schedT = isTenKEasyUnified
      ? buildTenKEasyTrainingWeekSchedule(
          roundWhole(singleTaperVol),
          plan.length + 1,
          Math.max(1, trainingWeekCount),
          unitPref,
          false
        )
      : isHalfMarathonEasyUnified
        ? buildHalfMarathonEasyTrainingWeekSchedule(
            roundWhole(singleTaperVol),
            plan.length + 1,
            Math.max(1, trainingWeekCount),
            unitPref,
            false
          )
        : isTenMilerEasyUnified
          ? buildTenMilerEasyTrainingWeekSchedule(
              roundWhole(singleTaperVol),
              plan.length + 1,
              Math.max(1, trainingWeekCount),
              unitPref,
              false
            )
          : buildTrainingWeek(
              roundWhole(singleTaperVol),
              daysPerWeek,
              normalizedDistance,
              unitPref,
              longRunPct,
              true,
              false,
              null,
              intensity,
              null,
              null,
              null,
              null,
              null,
              null,
              null
            );
    const tmT = computeWeekMileage(schedT, unitPref);
    plan.push({
      week: plan.length + 1,
      focus: "Taper",
      label: "Taper",
      schedule: schedT,
      totalMileage: tmT,
      totalMileageLabel: `${tmT} ${unitPref === "Kilometers" ? "km" : "miles"}`,
    });
  } else {
    const schedT2 = buildTrainingWeek(
      roundWhole(taper2Vol),
      daysPerWeek,
      normalizedDistance,
      unitPref,
      longRunPct,
      true,
      false,
      null,
      intensity,
      null,
      null,
      null,
      null,
      null,
      null,
      null
    );
    const tm2 = computeWeekMileage(schedT2, unitPref);
    plan.push({
      week: plan.length + 1,
      focus: "Taper 1",
      label: "Taper 1",
      schedule: schedT2,
      totalMileage: tm2,
      totalMileageLabel: `${tm2} ${unitPref === "Kilometers" ? "km" : "miles"}`,
    });

    const schedT1 = buildTrainingWeek(
      roundWhole(taper1Vol),
      daysPerWeek,
      normalizedDistance,
      unitPref,
      longRunPct,
      true,
      false,
      null,
      intensity,
      null,
      null,
      null,
      null,
      null,
      null,
      null
    );
    const tm1 = computeWeekMileage(schedT1, unitPref);
    plan.push({
      week: plan.length + 1,
      focus: "Taper 2",
      label: "Taper 2",
      schedule: schedT1,
      totalMileage: tm1,
      totalMileageLabel: `${tm1} ${unitPref === "Kilometers" ? "km" : "miles"}`,
    });
  }

  const schedRace = buildRaceWeekSchedule(
    raceWeekBudget,
    raceDate,
    normalizedDistance,
    unitPref,
    intensity
  );
  const tmR = computeRaceWeekTotalMileage(
    schedRace,
    unitPref,
    normalizedDistance
  );
  plan.push({
    week: plan.length + 1,
    focus: "Race Week",
    label: "Race Week",
    schedule: schedRace,
    totalMileage: tmR,
    totalMileageLabel: `${tmR} ${unitPref === "Kilometers" ? "km" : "miles"}`,
  });

  plan.forEach((w, idx) => {
    w.week = idx + 1;
  });

  /** For tests / diagnostics: internal weekly mi budget vs parsed schedule totals (WOW ref uses max of both). */
  for (let bi = 0; bi < trainingWeekCount; bi++) {
    if (plan[bi]) {
      plan[bi]._budgetWeeklyMi = roundWhole(vols[bi]);
    }
  }

  const label = raceDayLabel(normalizedDistance, unitPref);
  const easyTwoLine =
    isFiveKEasyPlan(normalizedDistance, intensity) ||
    isTenKEasyPlan(normalizedDistance, intensity) ||
    isHalfMarathonEasyPlan(normalizedDistance, intensity) ||
    isTenMilerEasyPlan(normalizedDistance, intensity)
      ? formatFiveKEasyEasyRunLabel(2, unitPref)
      : `Easy Run (${formatDist(2, unitPref)})`;
  placeRaceDayOnCorrectWeekday(plan, raceDate, label, {
    clearAfter: true,
    replaceHardWith: easyTwoLine,
    replaceLongRunWith: easyTwoLine,
  });

  const raceWeekEntry = plan[plan.length - 1];
  if (raceWeekEntry?.schedule) {
    const tmRace = computeRaceWeekTotalMileage(
      raceWeekEntry.schedule,
      unitPref,
      normalizedDistance
    );
    raceWeekEntry.totalMileage = tmRace;
    raceWeekEntry.totalMileageLabel = `${tmRace} ${
      unitPref === "Kilometers" ? "km" : "miles"
    }`;
  }

  const planWithXTrain = applyXTrainingToWeeks(
    plan,
    normalizedDistance,
    intensity,
    !!formData.includeCrossTraining
  );

  callback?.(planWithXTrain);
  return planWithXTrain;
  } finally {
    _marathonCalendarWeeksForFixedStart = null;
    _tenKShortNonEasyCutbackFrac = null;
  }
}

/**
 * Weekly total in the user’s unit, derived from schedule text (correct for km/mi and after race-week cleanup).
 */
export function weekDisplayTotals(
  week,
  unitPreference,
  raceDistanceRaw,
  isRaceWeek
) {
  const normalizedDistance = normalizeRaceDistance(
    String(raceDistanceRaw ?? "")
      .replace(/\u00A0/g, " ")
      .trim()
  );
  const sched = week?.schedule;
  if (!sched || typeof sched !== "object") {
    const t = Number(week?.totalMileage);
    const safe = Number.isFinite(t) ? t : 0;
    return {
      totalMileage: safe,
      totalMileageLabel:
        week?.totalMileageLabel ??
        `${safe} ${unitPreference === "Kilometers" ? "km" : "miles"}`,
    };
  }
  const t = isRaceWeek
    ? computeRaceWeekTotalMileage(sched, unitPreference, normalizedDistance)
    : computeWeekMileage(sched, unitPreference);
  return {
    totalMileage: t,
    totalMileageLabel: `${t} ${
      unitPreference === "Kilometers" ? "km" : "miles"
    }`,
  };
}
